// ARXMENTIS Market Forge — Phase 1 Lean MVP
// Chart cockpit aligned with the existing Income Engine.
// Reuses /api/forge/bars + /api/forge/wes which call the same wes_engine.py used by the Wheel ITM scanner.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { createChart, ColorType, CrosshairMode, LineStyle, CandlestickSeries, LineSeries } from "lightweight-charts";
import { CandlestickChart, RefreshCw, Search, Activity, AlertTriangle, Loader2, ZoomIn, ZoomOut, Maximize2, Minimize2, Pin, RotateCcw, Layers, Settings as SettingsIcon, Camera, BookOpen, X, Trash2, Zap, Trophy, History, Eye, EyeOff, ChevronLeft, ChevronRight, ChevronDown, BarChart3, CheckCircle2, ShieldAlert, Copy, Check, MousePointer2, Minus, TrendingUp, GitMerge, Eraser, Magnet, Palette, Save, Sparkles } from "lucide-react";

// Extracted MarketForge sub-components (refactor — keeps this file under control).
import { GEXPanel, GEX_CLASS_COLORS } from "./components/MarketForge/GEXPanel";
import { BatchBacktestModal } from "./components/MarketForge/BatchBacktestModal";
import { WesGexPanel } from "./components/MarketForge/WesGexPanel";
import { StrategyVerdictsSection } from "./components/MarketForge/StrategyVerdictsSection";
import { TradeVerdictCard } from "./components/MarketForge/TradeVerdictCard";
import { StrikeDecisionCard } from "./components/MarketForge/StrikeDecisionCard";
import FreshnessIndicator from "@/components/FreshnessIndicator";
import InlineError from "@/components/InlineError";
import { useI18n, LanguageToggle } from "@/i18n";
import { useAuth } from "@/App";
import { WatchlistPicker, ManageWatchlistsModal, useActiveWatchlist } from "@/components/WatchlistPicker";
import { ShadowBanner } from "@/components/gex/ReasonCodeRenderer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TF_OPTIONS = [
  { id: "15m",   label: "15M" },
  { id: "1h",    label: "1H"  },
  { id: "4h",    label: "4H"  },
  { id: "daily", label: "1D"  },
];

// Iter121.1 — Bar timer. Per-timeframe bar length in seconds drives
// the countdown to "current bar closes" displayed inside the chart
// header. Pure derivation from the latest bar's open + timeframe;
// no extra backend call, no extra state on the parent component.
const TF_SECONDS = {
  "15m":   15 * 60,
  "1h":    60 * 60,
  "4h":    4 * 60 * 60,
  "daily": 24 * 60 * 60,
};

// Iter121.9 — The forge/bars backend stamps every bar with its ET
// wall-clock time treated AS-IF UTC (so a 15m bar that opens at 09:30
// America/New_York is stored as UNIX seconds of "09:30Z"). Charts
// render correctly because `bar.time` and the axis both live in the
// same ET-as-UTC convention. The bar-timer chip must therefore
// compare bar times against a "now" built in the SAME convention —
// NOT against real UTC (`Date.now()/1000`). Otherwise every bar looks
// 4-5h stale and the chip flips to CLOSED even during live RTH.
//
// Returns the current wall-clock time in America/New_York expressed
// as UNIX seconds AS-IF UTC. Uses `Intl.DateTimeFormat` to dodge any
// dependency on the operator's local timezone.
function nowAsEtSeconds() {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(new Date()).map((p) => [p.type, p.value]),
  );
  const H = Number(parts.hour) % 24;
  return Math.floor(
    Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      H,
      Number(parts.minute),
      Number(parts.second),
    ) / 1000,
  );
}

// Iter121.1 — Returns the UNIX-seconds timestamp at which the current
// (latest) bar will close. `lastBarTime` is the bar's OPEN time in
// UNIX seconds (or null when bars are unavailable). Adds the timeframe
// step to land on the close boundary.
function nextBarCloseUnix(lastBarTime, tf) {
  if (lastBarTime == null) return null;
  const step = TF_SECONDS[tf];
  if (!step) return null;
  return Number(lastBarTime) + step;
}

// Iter121.8 — Roll-forward variant of `nextBarCloseUnix`. When the
// last bar's close is already in the past (market outside RTH, no
// new bars flowing), returns the NEXT expected bar-close boundary
// so the timer keeps ticking instead of freezing on "OFFLINE".
// Returns { boundary, rolled } where `rolled` is true iff we skipped
// past one or more expected boundaries because the market is closed.
function nextExpectedBarBoundary(lastBarTime, tf, nowUnix) {
  if (lastBarTime == null) return null;
  const step = TF_SECONDS[tf];
  if (!step) return null;
  const first = Number(lastBarTime) + step;
  if (first > nowUnix) return { boundary: first, rolled: false };
  // Roll forward N whole steps so `boundary > now`.
  const missed = Math.ceil((nowUnix - first + 1) / step);
  return { boundary: first + missed * step, rolled: true };
}

// Iter121.1 — Format a non-negative second count as `MM:SS` or `HH:MM:SS`.
// Used by the bar timer chip; bounded at zero so flicker around the
// boundary never shows negative values.
function formatBarCountdown(secs) {
  const s = Math.max(0, Math.floor(secs));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (hh > 0) return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
  return `${pad(mm)}:${pad(ss)}`;
}


// Iter119.9 — WES analysis is locked to 1H/Daily on the backend.
// Map any intraday chart TF (15m/1h/4h) → "1h" for the WES + candidates
// calls; daily → "daily". Keeps the WES levels meaningful regardless
// of the chart's display granularity.
const wesTfFor = (chartTf) => (chartTf === "daily" ? "daily" : "1h");

// Iter119.9 — Per-symbol VAL / POC / VAH styling. Each symbol carries
// its own visibility / color / lineStyle / lineWidth so operators can
// tune SPY differently from QQQ without bleeding settings across.
// Iter119.10 — Three-tier fallback chain:
//   1. `forge.lines.{SYMBOL}` — operator's per-symbol override
//   2. `forge.lines.__default__` — operator's saved global default
//      (set via "Save as default" button in the Levels popover)
//   3. `DEFAULT_LEVEL_STYLES` — hardcoded factory defaults
const DEFAULT_LEVEL_STYLES = {
  val: { visible: true, color: "#00FF88", style: "solid",  width: 1 },
  poc: { visible: true, color: "#FFB800", style: "dashed", width: 1 },
  vah: { visible: true, color: "#FF3366", style: "solid",  width: 1 },
};
const GLOBAL_DEFAULT_KEY = "forge.lines.__default__";

function _parseStoredStyles(raw) {
  try {
    const parsed = JSON.parse(raw);
    return {
      val: { ...DEFAULT_LEVEL_STYLES.val, ...(parsed.val || {}) },
      poc: { ...DEFAULT_LEVEL_STYLES.poc, ...(parsed.poc || {}) },
      vah: { ...DEFAULT_LEVEL_STYLES.vah, ...(parsed.vah || {}) },
    };
  } catch { return null; }
}
function readLevelStyles(symbol) {
  try {
    const raw = localStorage.getItem(`forge.lines.${symbol}`);
    if (raw) {
      const parsed = _parseStoredStyles(raw);
      if (parsed) return parsed;
    }
    const rawDefault = localStorage.getItem(GLOBAL_DEFAULT_KEY);
    if (rawDefault) {
      const parsed = _parseStoredStyles(rawDefault);
      if (parsed) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_LEVEL_STYLES;
}
function writeLevelStyles(symbol, styles) {
  try { localStorage.setItem(`forge.lines.${symbol}`, JSON.stringify(styles)); } catch { /* ignore */ }
}
function writeGlobalDefaultLevelStyles(styles) {
  try { localStorage.setItem(GLOBAL_DEFAULT_KEY, JSON.stringify(styles)); } catch { /* ignore */ }
}
function clearSymbolLevelStyles(symbol) {
  try { localStorage.removeItem(`forge.lines.${symbol}`); } catch { /* ignore */ }
}

// ── Iter119.11 — Chart drawing tools (P3a) ─────────────────────────
// Trendlines, horizontal lines, and Fibonacci retracements as an
// SVG overlay above the chart canvas. Each drawing is anchored in
// (time, price) so it survives pan/zoom/timeframe switches.
//
// Storage model (per-symbol):
//   forge.draw.{SYMBOL} = [
//     { id, type, tf, points: [{time, price}, ...] }, ...
//   ]
// • `time` is ALWAYS unix-seconds (UTCTimestamp) so cross-TF rendering
//   works without format gymnastics.
// • `tf` is the TF the drawing was made on. Horizontal lines IGNORE tf
//   (a key support level is meaningful on every chart). Trendlines
//   and fibs render only when chartTf matches `tf`.
const DRAW_TOOLS = ["none", "hline", "trendline", "fib", "eraser"];

function readDrawings(symbol) {
  try {
    const raw = localStorage.getItem(`forge.draw.${symbol}`);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}
function writeDrawings(symbol, drawings) {
  try {
    localStorage.setItem(`forge.draw.${symbol}`, JSON.stringify(drawings || []));
  } catch { /* ignore */ }
}

// ── Iter119.14 — Snap-to-OHLC ────────────────────────────────────
// Global on/off flag for snap-to-OHLC drawing mode. When ON, the
// drawing layer snaps the clicked price to the nearest O/H/L/C of
// the nearest bar — so trendlines and hlines anchor exactly on the
// candle wicks/bodies the operator was aiming at. Saved per-user
// in localStorage so it persists across sessions.
function readSnapMode() {
  try { return localStorage.getItem("forge.draw.snap") === "1"; }
  catch { return false; }
}
function writeSnapMode(on) {
  try { localStorage.setItem("forge.draw.snap", on ? "1" : "0"); }
  catch { /* ignore */ }
}

// Given a clicked (time, price) and the chart's bar set, find the
// bar with the nearest `time` and snap `price` to whichever of its
// OHLC values is closest. Returns the snapped { time, price, anchor }
// where `anchor` is one of "open"|"high"|"low"|"close".
function snapToOHLC(time, price, bars) {
  if (!Array.isArray(bars) || bars.length === 0) return { time, price, anchor: null };
  // Locate nearest-bar by time (linear scan — bar count is bounded).
  let nearest = bars[0];
  let bestDt = Math.abs(Number(nearest.time) - time);
  for (let i = 1; i < bars.length; i++) {
    const dt = Math.abs(Number(bars[i].time) - time);
    if (dt < bestDt) { bestDt = dt; nearest = bars[i]; }
  }
  const cands = [
    { k: "open",  v: Number(nearest.open)  },
    { k: "high",  v: Number(nearest.high)  },
    { k: "low",   v: Number(nearest.low)   },
    { k: "close", v: Number(nearest.close) },
  ].filter(c => Number.isFinite(c.v));
  if (cands.length === 0) return { time: Number(nearest.time), price, anchor: null };
  let best = cands[0];
  let bestDp = Math.abs(best.v - price);
  for (let i = 1; i < cands.length; i++) {
    const dp = Math.abs(cands[i].v - price);
    if (dp < bestDp) { bestDp = dp; best = cands[i]; }
  }
  return { time: Number(nearest.time), price: best.v, anchor: best.k };
}

// ── Iter119.15 — Chart Layout Presets (per-symbol) ───────────────
// A "preset" captures the chart's current visual layout so the
// operator can save (e.g.) "SPY · Intraday Scalping" and one-click
// restore it later. We persist:
//   • chartPrefs (entire prefs object — vp bins, va opacity, theme,
//     grid, ranges, right offset, vwap, vwap bands, visibility flags)
//   • levelStyles for VAL/POC/VAH (per-line color/style/width)
//   • timeframe + strikeBand
// We intentionally DO NOT include drawings (data, not layout) or
// snap mode (a global per-operator preference). Storage key is
// per-symbol so SPY presets stay separate from QQQ presets.
function readChartPresets(symbol) {
  try {
    const raw = localStorage.getItem(`forge.preset.${symbol}`);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : {};
  } catch { return {}; }
}
function writeChartPresets(symbol, presets) {
  try { localStorage.setItem(`forge.preset.${symbol}`, JSON.stringify(presets || {})); }
  catch { /* ignore */ }
}

// Lightweight-charts hands `time` back from coordinateToTime() in 1 of 3
// shapes (UTCTimestamp number, "YYYY-MM-DD" string, or BusinessDay object).
// We normalise to unix-seconds for storage.
function normalizeChartTime(t) {
  if (t == null) return null;
  if (typeof t === "number" && Number.isFinite(t)) return Math.floor(t);
  if (typeof t === "string") {
    // "YYYY-MM-DD"
    const d = new Date(t + "T00:00:00Z");
    const u = Math.floor(d.getTime() / 1000);
    return Number.isFinite(u) ? u : null;
  }
  if (typeof t === "object" && t.year != null && t.month != null && t.day != null) {
    const d = new Date(Date.UTC(t.year, t.month - 1, t.day));
    return Math.floor(d.getTime() / 1000);
  }
  return null;
}

// Inverse: convert stored unix-seconds back to the shape `timeToCoordinate`
// expects on this chart. Daily charts use string format; intraday use number.
function unixToChartTime(unixSec, isDailyChart) {
  if (unixSec == null) return null;
  if (isDailyChart) {
    return new Date(unixSec * 1000).toISOString().slice(0, 10);  // YYYY-MM-DD
  }
  return Number(unixSec);
}

const FIB_LEVELS = [
  { v: 0,     label: "0%"     },
  { v: 0.236, label: "23.6%"  },
  { v: 0.382, label: "38.2%"  },
  { v: 0.5,   label: "50%"    },
  { v: 0.618, label: "61.8%"  },
  { v: 0.786, label: "78.6%"  },
  { v: 1,     label: "100%"   },
];

const DRAW_DEFAULT_COLOR = {
  hline:     "#22D3FF",
  trendline: "#FFB800",
  fib:       "#A78BFA",
};

const COLORS = {
  bg: "#08080A",
  panel: "#121216",
  border: "#26262B",
  borderSoft: "#1A1A20",
  text: "#E4E4E7",
  textDim: "#8A8A93",
  green: "#00FF88",
  red: "#FF3366",
  amber: "#FFB800",
};

function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

// Iter119.12 — Compact volume formatter (1.2K / 3.4M / 1.2B). Powers the
// new day-metrics strip's V chip in the chart header.
function fmtVol(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n.toFixed(0)}`;
}

/**
 * AggressionChip — surface the WES "Bull/Bear @ VAL/VAH · Turning Point/Continuation"
 * signal (already computed by wes_engine.detect_aggression). Mirrors the chip the
 * Scanner page renders so the same regime cue is visible in the Forge cockpit
 * (chart header + WES Structure card). Returns null when no aggression detected
 * so the layout collapses cleanly.
 *
 * Continuation logic (Pine parity):
 *   Bull @ VAL = continuation (buyers defend support)
 *   Bull @ VAH = turning point (breakout)
 *   Bear @ VAH = continuation (sellers defend resistance)
 *   Bear @ VAL = turning point (breakdown)
 */
function AggressionChip({ agg, t, compact = false }) {
  if (!agg || !agg.type || !agg.edge) return null;
  const isBull = agg.type === "Bull";
  const isTurning = (isBull && agg.edge === "VAH") || (!isBull && agg.edge === "VAL");
  const signalLabel = isTurning ? t("forge.signal_turning_point") : t("forge.signal_continuation");
  const arrow = isBull ? "\u25B2" : "\u25BC";
  const cls = isBull
    ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/15"
    : "border-[#FF3366] text-[#FF3366] bg-[#FF3366]/15";
  const title = `${agg.type} @ ${agg.edge} (${signalLabel}) · ΔVAL ${agg.d_val}% · ΔVAH ${agg.d_vah}%`;
  return (
    <span
      data-testid={`forge-aggression-chip${compact ? "-compact" : ""}`}
      title={title}
      className={`inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-sm font-mono font-bold border ${cls} animate-pulse`}
    >
      {arrow} {agg.type} @ {agg.edge} · {signalLabel}
    </span>
  );
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
}
function fmtNum(v, dp = 1) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(dp);
}

function themeForCandles(name) {
  switch (name) {
    case "blue-orange": return { up: "#4DA3FF", down: "#FF8A2C" };
    case "mono":         return { up: "#E4E4E7", down: "#8A8A93" };
    case "green-red":
    default:             return { up: "#00FF88", down: "#FF3366" };
  }
}

// Session VWAP — typical price (H+L+C)/3 weighted by volume, reset each trading day.
// Accepts bars with `time` as UNIX seconds (1H intraday); each bar must have high/low/close/volume.
// Returns an object with 5 arrays consumable by Lightweight Charts LineSeries.setData():
//   { vwap, upper1, lower1, upper2, lower2 }
// Bands are ±1σ / ±2σ from the cumulative volume-weighted variance of the session.
// Session key uses the bar's UTC date — matches the ET trading day for our RTH-filtered bars.
function computeSessionVwap(bars) {
  const result = { vwap: [], upper1: [], lower1: [], upper2: [], lower2: [] };
  if (!Array.isArray(bars) || bars.length === 0) return result;
  let curKey = null;
  let sumPV = 0;
  let sumV = 0;
  let sumSqDev = 0;
  for (const b of bars) {
    const t = Number(b.time);
    const h = Number(b.high);
    const l = Number(b.low);
    const c = Number(b.close);
    const v = Number(b.volume) || 0;
    if (!Number.isFinite(t) || !Number.isFinite(h) || !Number.isFinite(l) || !Number.isFinite(c)) continue;
    if (h <= 0 || l <= 0 || c <= 0) continue;
    // Key by UTC day (good enough for our ET-hour-stamped bars).
    const d = new Date(t * 1000);
    const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
    if (key !== curKey) { curKey = key; sumPV = 0; sumV = 0; sumSqDev = 0; }
    const tp = (h + l + c) / 3;
    sumPV += tp * v;
    sumV += v;
    const vwap = sumV > 0 ? sumPV / sumV : tp;
    // Cumulative volume-weighted variance for the bands
    sumSqDev += v * (tp - vwap) * (tp - vwap);
    const variance = sumV > 0 ? sumSqDev / sumV : 0;
    const sigma = variance > 0 ? Math.sqrt(variance) : 0;
    const round4 = (x) => Number(x.toFixed(4));
    result.vwap.push({ time: b.time, value: round4(vwap) });
    result.upper1.push({ time: b.time, value: round4(vwap + sigma) });
    result.lower1.push({ time: b.time, value: round4(vwap - sigma) });
    result.upper2.push({ time: b.time, value: round4(vwap + 2 * sigma) });
    result.lower2.push({ time: b.time, value: round4(vwap - 2 * sigma) });
  }
  return result;
}

function MarketForge() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter121.20 — Honour `?symbol=XXX` in the URL so operators can open
  // Market Forge in a new tab (from a Dashboard ticker card, an
  // external link, etc.) pre-focused on a specific symbol. Falls back
  // to SPY when no query param is present. Reads once on mount — the
  // symbol input remains the source of truth thereafter.
  const _initialSymbol = React.useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("symbol") || "";
      const s = q.toUpperCase().trim();
      // Sanity-check: alphanumeric + optional dot/slash for tickers
      if (s && /^[A-Z0-9./^-]{1,10}$/.test(s)) return s;
    } catch { /* SSR / no window */ }
    return "SPY";
  }, []);
  const [symbol, setSymbol] = useState(_initialSymbol);
  const [draftSymbol, setDraftSymbol] = useState(_initialSymbol);
  const [timeframe, setTimeframe] = useState("1h");
  const [bars, setBars] = useState([]);
  // Keep a ref that mirrors the latest bars so the 5s tick callback (captured once
  // per symbol/timeframe change via useEffect) always reads the freshest candle
  // instead of a stale closure from when the interval was set up. Without this,
  // a symbol switch (SPY 710 → CCL 25) or a bars refresh could leave the tick
  // handler painting spot into the wrong bar → corrupted OHLC → massive wick.
  const barsRef = useRef([]);
  useEffect(() => { barsRef.current = bars; }, [bars]);
  const [wes, setWes] = useState(null);
  const [loadingBars, setLoadingBars] = useState(false);
  const [loadingWes, setLoadingWes] = useState(false);
  const [error, setError] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  // Iter121.3 — Multi-watchlist state now sourced from a shared hook so
  // MarketForge, Dashboard, StrategyScannerV2 and MarketScannerBridgeTile
  // all share the same active-list pointer. `watchlist` above still
  // holds the items in the currently-active list (populated by
  // `fetchWatchlistItems` below).
  const {
    watchlists,
    activeId: activeWatchlistId,
    switchActive: switchActiveWatchlistBase,
    refetch: refetchWatchlists,
  } = useActiveWatchlist();
  const [showManageWatchlists, setShowManageWatchlists] = useState(false);
  // Iter121.16 — Broker health strip. Poll `/api/broker/health` every 60s
  // and render two coloured status dots in the header so the operator
  // can tell at a glance whether a red ERROR chip in the rail is a
  // credentials / config issue (broker down) or ambient market-data
  // slowness. Reset on each mount so operators always start with a
  // fresh probe within the first minute.
  const [brokerHealth, setBrokerHealth] = useState({ brokers: [] });
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/broker/health`);
        if (!cancelled) setBrokerHealth(r.data || { brokers: [] });
      } catch { /* silent — dots render as unknown */ }
    };
    poll();
    const id = setInterval(poll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  // Iter32 — Pine-parity is the OFFICIAL operational source. Legacy stays
  // available only as a diagnostic compare. New users land on parity; the
  // localStorage value persists across sessions but seeds to "parity" if no
  // value or the legacy default from prior installs is found stale.
  const [wesEngine, setWesEngine] = useState(() => {
    const v = localStorage.getItem("forge.wes.engine");
    return v === "legacy" || v === "parity" ? v : "parity";
  });
  useEffect(() => { localStorage.setItem("forge.wes.engine", wesEngine); }, [wesEngine]);

  // Phase 2 — Wheel ITM Decision Engine
  const [candidates, setCandidates] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [pinResult, setPinResult] = useState(null);
  const [pinModalOpen, setPinModalOpen] = useState(false);

  // Iter119 — Strike-band trim for the Options Chain panel.
  // Persisted to localStorage so each operator keeps their preferred
  // view. Default "2sigma" trims ~50% of strikes (perf win) without
  // affecting candidate selection (backend uses full ITM band for
  // Pure/Ladder evaluation regardless of this knob).
  const [strikeBand, setStrikeBandState] = useState(() => {
    try {
      const v = localStorage.getItem("forge.strikeBand");
      if (v === "full" || v === "2sigma" || v === "3sigma") return v;
    } catch { /* ignore */ }
    return "2sigma";
  });
  const setStrikeBand = useCallback((v) => {
    setStrikeBandState(v);
    try { localStorage.setItem("forge.strikeBand", v); } catch { /* ignore */ }
  }, []);

  // Phase 3 — Snapshots / Chart preferences
  const [snapshots, setSnapshots] = useState([]);
  const [savingSnap, setSavingSnap] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [chartPrefs, setChartPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem("forge.chartPrefs");
      if (raw) return JSON.parse(raw);
    } catch {}
    return {
      vpBins: 80,             // visual VP bin density (40-160) — does NOT change WES math
      vaOpacity: "default",   // 'subtle' | 'default' | 'bold'
      candleTheme: "green-red", // 'green-red' | 'blue-orange' | 'mono'
      grid: true,
      tfRange1h: "all",       // 'last5d' | 'last10d' | 'all'
      tfRangeDaily: "all",    // 'last30d' | 'last60d' | 'last90d' | 'all'
      rightOffset: 30,        // empty bars to the right of the last candle (TradingView-style breathing room)
      showVwap: true,         // session VWAP overlay on 1H chart (resets each trading day)
      showVwapBands: false,   // VWAP ±1σ/±2σ bands — OFF by default (strategy is volume-profile-first)
      // Iter119.13 — Chart-visibility quick toggles. Master switches that
      // dim entire overlay layers in one click. The popover (toggled via
      // the Eye button next to Settings) wraps these into a single panel.
      showVp:        true,    // SVG volume-profile overlay
      showLevels:    true,    // VAL/POC/VAH HTML overlay (overrides per-line visibility)
      showDrawings:  true,    // user drawings (hlines/trendlines/fibs)
      showWatermark: true,    // background symbol watermark
    };
  });
  // Back-compat: migrate older persisted prefs that pre-date rightOffset
  useEffect(() => {
    if (chartPrefs.rightOffset === undefined) setChartPrefs(p => ({ ...p, rightOffset: 30 }));
    if (chartPrefs.showVwap === undefined) setChartPrefs(p => ({ ...p, showVwap: true }));
    if (chartPrefs.showVwapBands === undefined) setChartPrefs(p => ({ ...p, showVwapBands: false }));
    // Iter119.13 — default the new master toggles to ON for back-compat.
    if (chartPrefs.showVp        === undefined) setChartPrefs(p => ({ ...p, showVp: true }));
    if (chartPrefs.showLevels    === undefined) setChartPrefs(p => ({ ...p, showLevels: true }));
    if (chartPrefs.showDrawings  === undefined) setChartPrefs(p => ({ ...p, showDrawings: true }));
    if (chartPrefs.showWatermark === undefined) setChartPrefs(p => ({ ...p, showWatermark: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try { localStorage.setItem("forge.chartPrefs", JSON.stringify(chartPrefs)); } catch {}
  }, [chartPrefs]);
  const [showChartMenu, setShowChartMenu] = useState(false);
  // Iter119.13 — Chart-visibility quick-toggle popover. Lives next to
  // Settings; one-click "Hide all overlays" to declutter, one-click
  // "Show all" to restore.
  const [showVisToggle, setShowVisToggle] = useState(false);

  // Phase 5 — Replay Mode (time-travel the cockpit)
  const [replayOn, setReplayOn] = useState(false);
  const [replayAsOf, setReplayAsOf] = useState(() => {
    // Default to 60 days ago (daily replay) — formatted as YYYY-MM-DD for the date input
    const d = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [replayDte, setReplayDte] = useState(7);
  const [replayData, setReplayData] = useState(null);
  const [replayLoading, setReplayLoading] = useState(false);
  const [replayError, setReplayError] = useState(null);
  const [replayRevealed, setReplayRevealed] = useState(false);

  // Phase 6 — Replay Batch Backtest
  const [showBatchModal, setShowBatchModal] = useState(false);

  // GEX classification gets lifted from <GEXPanel /> via the onUpdate callback so
  // the Decision panel can render a tiny GEX chip alongside the strategy label.
  // GEX never overrides the trade decision — this is purely informational.
  const [gexReport, setGexReport] = useState(null);
  // Iter37 — User-selected strike (from auto-suggest "Evaluate" click).
  // Lives at the cockpit level so WesGexPanel + StrikeDecisionCard both
  // re-fetch when the user picks one. Resets on symbol change.
  const [evaluatedStrike, setEvaluatedStrike] = useState(null);
  // Iter116 — Consolidated right-rail. By default the cockpit shows only
  // WES Structure + Trade Verdict (the unified pre-trade card). The legacy
  // detail panels (WES-GEX Path, Strike Decision, Decision V2) stay one
  // click away via the "Show detailed breakdown" toggle so power users
  // can still drill into the raw engine outputs without forcing them on
  // every operator.
  const [showRailDetails, setShowRailDetails] = useState(false);
  // Iter117.8 — Anti-flicker: track which symbol the on-screen data
  // belongs to. The cockpit's `fetchAll` only clears existing data
  // when the symbol actually changes; timeframe switches and refresh
  // button clicks keep the previous values painted while the new
  // ones arrive, so the chart, GEX panel, WES card, Trade Verdict
  // and Strategy Verdicts no longer all blank out together.
  const cockpitDataSymbolRef = useRef(null);
  // Iter121.11 — Aggression Sentiment History. Rolling ring buffer of the
  // last N transitions (type or edge change) for the active symbol +
  // timeframe. Persisted to localStorage so the strip survives page
  // reloads. Empty state hides the strip entirely.
  const SIGNAL_HISTORY_MAX = 5;
  const signalHistoryKey = `arxmentis:signal-history:${symbol}:${timeframe}`;
  const [signalHistory, setSignalHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(signalHistoryKey);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.slice(0, SIGNAL_HISTORY_MAX) : [];
    } catch { return []; }
  });
  // Reload from localStorage whenever the symbol/timeframe pair changes
  // so the strip immediately reflects the persisted state for the new
  // pair (no stale entries from the previous symbol).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(signalHistoryKey);
      const arr = raw ? JSON.parse(raw) : [];
      setSignalHistory(Array.isArray(arr) ? arr.slice(0, SIGNAL_HISTORY_MAX) : []);
    } catch { setSignalHistory([]); }
  }, [symbol, timeframe]);
  // Iter34 — Reset gexReport when the active symbol changes so the symbol-
  // mismatch banner self-clears once the new symbol's GEX fetch completes.
  // Without this, a stale gexReport from a previous symbol persists until
  // the GEX panel re-emits onUpdate, which leaves the banner blinking
  // for the duration of one fetch round-trip.
  useEffect(() => {
    setGexReport(null);
    setEvaluatedStrike(null);
  }, [symbol]);

  // Iter121.11 — Track aggression transitions. Push a new entry onto the
  // ring buffer whenever the WES payload's aggression (or lack thereof)
  // differs from the most recent entry for this symbol+timeframe pair.
  // A "no aggression" state records once (as edge=null) so operators see
  // when a signal clears too. The buffer is keyed per symbol+timeframe.
  useEffect(() => {
    if (!wes) return;
    // Guard: only record when the WES payload matches the currently
    // active symbol (avoid recording stale in-flight fetches from a
    // just-switched symbol).
    if ((wes.symbol || "").toUpperCase() !== (symbol || "").toUpperCase()) return;
    const agg = wes.aggression;
    const nextType = agg?.type || null;   // "Bull" | "Bear" | null
    const nextEdge = agg?.edge || null;   // "VAL" | "VAH" | null
    const isTurning = agg && (
      (agg.type === "Bull" && agg.edge === "VAH") ||
      (agg.type === "Bear" && agg.edge === "VAL")
    );
    setSignalHistory(prev => {
      const last = prev[0];
      // De-dupe: only append when the (type, edge) tuple changes.
      if (last && last.type === nextType && last.edge === nextEdge) return prev;
      // Skip recording an initial "clear" state — only start once the
      // first real aggression signal fires. This keeps the strip empty
      // for symbols that never had a signal.
      if (!last && !nextType) return prev;
      // Iter121.13 — Record the bar time the signal was computed
      // against so clicking the pill can pan the chart back to that
      // exact bar. Falls back to the WES `spot_ts` if available.
      const barTime = (bars && bars.length > 0)
        ? bars[bars.length - 1].time
        : (wes.spot_ts || null);
      const entry = {
        type: nextType,
        edge: nextEdge,
        turning: !!isTurning,
        at: Date.now(),
        bar_time: barTime,
        val: wes.val ?? null,
        vah: wes.vah ?? null,
        poc: wes.poc ?? null,
      };
      const next = [entry, ...prev].slice(0, SIGNAL_HISTORY_MAX);
      try { localStorage.setItem(signalHistoryKey, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  }, [wes, symbol, timeframe, bars]);

  const fetchReplay = async () => {
    setReplayLoading(true);
    setReplayError(null);
    setReplayRevealed(false);
    try {
      const r = await axios.get(`${API}/forge/replay`, {
        params: { symbol, timeframe, as_of: replayAsOf, dte: replayDte },
      });
      setReplayData(r.data);
    } catch (e) {
      setReplayError(e?.response?.data?.detail || "Replay failed");
      setReplayData(null);
    }
    setReplayLoading(false);
  };
  // Auto-fetch on toggle on / param change
  useEffect(() => {
    if (replayOn) fetchReplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayOn, symbol, timeframe, replayAsOf, replayDte]);
  // Clear replay data when leaving replay mode
  useEffect(() => {
    if (!replayOn) {
      setReplayData(null);
      setReplayError(null);
      setReplayRevealed(false);
    }
  }, [replayOn]);

  // Phase 4 — Watchlist Scanner Integration
  // Per-symbol ranking by composite ROI + WES-P + IVR. Powered by /api/forge/watchlist-scan.
  const [scanRows, setScanRows] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanMeta, setScanMeta] = useState(null); // {scanned_at, errors, cached, cache_age_s}
  const [rankMode, setRankMode] = useState("ranked"); // 'ranked' | 'alpha'

  const runWatchlistScan = async (refresh = false) => {
    setScanLoading(true);
    try {
      // Iter121.15 — scope the scan to the operator's active watchlist so
      // the rail ticker set + the ranking scan target the same universe.
      const params = { ...(refresh ? { refresh: 1 } : {}) };
      if (activeWatchlistId) params.watchlist_id = activeWatchlistId;
      const r = await axios.get(`${API}/forge/watchlist-scan`, { params });
      setScanRows(r.data?.rows || []);
      setScanMeta({
        scanned_at: r.data?.scanned_at,
        errors: r.data?.errors,
        cached: r.data?.cached,
        cache_age_s: r.data?.cache_age_s,
        count: r.data?.count,
      });
    } catch {
      // soft-fail: keep simple alphabetical mode
      setScanRows([]);
    }
    setScanLoading(false);
  };
  // Iter121.15 — Re-run whenever the operator switches active watchlist so
  // the rail immediately reflects the new list's ranking. First call still
  // fires on mount (activeWatchlistId may be null then; the backend falls
  // back to the caller's active pointer, so the initial paint is correct).
  useEffect(() => { runWatchlistScan(false); /* eslint-disable-next-line */ }, [activeWatchlistId]);

  // Phase 4 — Auto-refresh: lightweight tick polling + periodic full refresh.
  const [tick, setTick] = useState(null);     // {spot, change, change_pct, ts, market_open, stale}
  const [tickError, setTickError] = useState(null);

  // ---- chart refs ----
  const cockpitRef = useRef(null);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const vwapSeriesRef = useRef(null);
  const vwapBandRefs = useRef({ upper1: null, lower1: null, upper2: null, lower2: null });
  const priceLineRefs = useRef([]);
  const [vpOverlay, setVpOverlay] = useState({ bins: [], yByIdx: [], plotH: 0, poc: 0, va: [0, 0] });
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Iter119.9 — Per-symbol VAL/POC/VAH styling. Read on mount; re-load
  // whenever the operator switches symbols. Used by both the HTML
  // overlay (lines + labels) and the Chart Settings popover's new
  // "Levels" section.
  const [levelStyles, setLevelStylesState] = useState(() => readLevelStyles(symbol));
  useEffect(() => { setLevelStylesState(readLevelStyles(symbol)); }, [symbol]);
  const updateLevelStyle = useCallback((key, patch) => {
    setLevelStylesState(prev => {
      const next = { ...prev, [key]: { ...prev[key], ...patch } };
      writeLevelStyles(symbol, next);
      return next;
    });
  }, [symbol]);
  // Iter119.10 — Promote the current symbol's styling to the global
  // default so new symbols inherit it (operator settles on cyan VAH
  // once and every new ticker picks it up).
  const saveLevelsAsDefault = useCallback(() => {
    writeGlobalDefaultLevelStyles(levelStyles);
  }, [levelStyles]);
  // Iter119.10 — Reset this symbol back to whatever the global default
  // (or factory default) is; clears the per-symbol override.
  const resetLevelsForSymbol = useCallback(() => {
    clearSymbolLevelStyles(symbol);
    setLevelStylesState(readLevelStyles(symbol));
  }, [symbol]);

  // Iter119.11 — Chart drawing tools (P3a). Per-symbol drawings array
  // + active tool selector. Tool is local state (not persisted) — only
  // the drawings themselves persist. Reset to "none" on symbol switch.
  const [drawTool, setDrawTool] = useState("none");
  const [drawings, setDrawingsState] = useState(() => readDrawings(symbol));
  useEffect(() => {
    setDrawingsState(readDrawings(symbol));
    setDrawTool("none");
  }, [symbol]);
  const commitDrawing = useCallback((drawing) => {
    setDrawingsState(prev => {
      const next = [...prev, drawing];
      writeDrawings(symbol, next);
      return next;
    });
  }, [symbol]);
  const deleteDrawing = useCallback((id) => {
    setDrawingsState(prev => {
      const next = prev.filter(d => d.id !== id);
      writeDrawings(symbol, next);
      return next;
    });
  }, [symbol]);
  // Iter119.14 — Patch a single drawing's per-instance styling
  // (color / lineStyle / lineWidth). Used by the DrawingsManager
  // "Edit" inline picker.
  const updateDrawing = useCallback((id, patch) => {
    setDrawingsState(prev => {
      const next = prev.map(d => d.id === id ? { ...d, ...patch } : d);
      writeDrawings(symbol, next);
      return next;
    });
  }, [symbol]);
  const toggleDrawingHidden = useCallback((id) => {
    setDrawingsState(prev => {
      const next = prev.map(d => d.id === id ? { ...d, hidden: !d.hidden } : d);
      writeDrawings(symbol, next);
      return next;
    });
  }, [symbol]);
  // Iter119.12 — Jump the chart's visible time range to a specific drawing's
  // anchor time(s). Only meaningful for trendlines/fibs (hlines have no time).
  const jumpToDrawing = useCallback((drawing) => {
    const chart = chartRef.current;
    if (!chart || !drawing?.points || drawing.points.length < 2) return;
    const times = drawing.points.map(p => p.time).filter(Number.isFinite).sort((a, b) => a - b);
    if (times.length < 2) return;
    const isDaily = timeframe === "daily";
    const padding = (times[1] - times[0]) * 0.3;
    const fromTime = unixToChartTime(times[0] - padding, isDaily);
    const toTime   = unixToChartTime(times[1] + padding, isDaily);
    try {
      chart.timeScale().setVisibleRange({ from: fromTime, to: toTime });
    } catch { /* lightweight-charts throws if range is invalid; ignore */ }
  }, [timeframe]);
  const clearAllDrawings = useCallback(() => {
    setDrawingsState([]);
    writeDrawings(symbol, []);
  }, [symbol]);
  // Iter119.12 — Drawings list manager popover toggle. Closed by default;
  // chip in header opens it; clicks outside or X close it.
  const [showDrawingsMgr, setShowDrawingsMgr] = useState(false);
  useEffect(() => { setShowDrawingsMgr(false); }, [symbol]);  // collapse on symbol switch

  // Iter119.14 — Snap-to-OHLC mode. Global operator preference (not
  // per-symbol). When on, drawing clicks snap to the nearest OHLC of
  // the nearest bar so trendlines / hlines anchor exactly on the
  // candle features the operator was aiming at.
  const [snapMode, setSnapModeState] = useState(() => readSnapMode());
  const toggleSnapMode = useCallback(() => {
    setSnapModeState(prev => {
      const next = !prev;
      writeSnapMode(next);
      return next;
    });
  }, []);

  // Iter119.15 — Chart layout presets. Per-symbol named bundles that
  // capture {chartPrefs, levelStyles, timeframe, strikeBand}. Reload
  // them with one click; rename/delete via the Chart Settings popover.
  const [chartPresets, setChartPresetsState] = useState(() => readChartPresets(symbol));
  useEffect(() => { setChartPresetsState(readChartPresets(symbol)); }, [symbol]);
  const saveChartPreset = useCallback((name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return false;
    const presets = readChartPresets(symbol);
    presets[trimmed] = {
      saved_at: new Date().toISOString(),
      prefs: { ...chartPrefs },
      levels: { ...levelStyles },
      timeframe,
      strikeBand,
    };
    writeChartPresets(symbol, presets);
    setChartPresetsState(presets);
    return true;
  }, [symbol, chartPrefs, levelStyles, timeframe, strikeBand]);
  const loadChartPreset = useCallback((name) => {
    const presets = readChartPresets(symbol);
    const p = presets[name];
    if (!p) return false;
    if (p.prefs)      setChartPrefs(prev => ({ ...prev, ...p.prefs }));
    if (p.levels) {
      setLevelStylesState(p.levels);
      writeLevelStyles(symbol, p.levels);
    }
    if (p.timeframe)  setTimeframe(p.timeframe);
    if (p.strikeBand) setStrikeBand(p.strikeBand);
    return true;
  }, [symbol, setStrikeBand]);
  const deleteChartPreset = useCallback((name) => {
    const presets = readChartPresets(symbol);
    delete presets[name];
    writeChartPresets(symbol, presets);
    setChartPresetsState(presets);
  }, [symbol]);

  // Iter119.9 — Crosshair OHLC tooltip (P3c). Floating chip rendered
  // above the chart at the crosshair X — gives the operator a
  // TradingView-style data window without leaving the chart.
  const [crosshair, setCrosshair] = useState(null);

  // Build chart once
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: COLORS.bg },
        textColor: COLORS.text,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      },
      localization: { locale: "en-US" },
      grid: {
        vertLines: { color: chartPrefs.grid ? COLORS.borderSoft : "transparent" },
        horzLines: { color: chartPrefs.grid ? COLORS.borderSoft : "transparent" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: COLORS.border },
      timeScale: {
        borderColor: COLORS.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: Number.isFinite(chartPrefs.rightOffset) ? chartPrefs.rightOffset : 30,
      },
      autoSize: true,
    });
    const themeColors = themeForCandles(chartPrefs.candleTheme);
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: themeColors.up, downColor: themeColors.down,
      borderUpColor: themeColors.up, borderDownColor: themeColors.down,
      wickUpColor: themeColors.up, wickDownColor: themeColors.down,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    chartRef.current = chart;
    candleSeriesRef.current = candle;

    // Session VWAP line — separate series so we can toggle independently of candles.
    // Colored a cool electric blue to stand apart from the value-area greens/reds.
    const vwap = chart.addSeries(LineSeries, {
      color: "#3BB5FF",
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      title: "VWAP",
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    vwapSeriesRef.current = vwap;

    // VWAP bands — ±1σ (inner, solid-ish) and ±2σ (outer, dashed). Toggled separately
    // so the chart stays usable when the user wants a pure volume-profile read.
    // Iter119.16 — Added explicit ±1σ / ±2σ titles so they show up in the
    // lightweight-charts legend.
    const mkBand = (color, style, title) => chart.addSeries(LineSeries, {
      color,
      lineWidth: 1,
      lineStyle: style,
      priceLineVisible: false,
      lastValueVisible: false,
      title,
      priceFormat: { type: "price", precision: 2, minMove: 0.01 },
    });
    vwapBandRefs.current = {
      upper1: mkBand("#3BB5FF80", LineStyle.Dotted, "+1σ"),
      lower1: mkBand("#3BB5FF80", LineStyle.Dotted, "−1σ"),
      upper2: mkBand("#3BB5FF40", LineStyle.Dashed, "+2σ"),
      lower2: mkBand("#3BB5FF40", LineStyle.Dashed, "−2σ"),
    };

    const onResize = () => chart.applyOptions({ width: containerRef.current?.clientWidth, height: containerRef.current?.clientHeight });
    window.addEventListener("resize", onResize);

    // Iter119.9 — Crosshair OHLC subscription (P3c). Fires on every
    // mouse move over the chart; null param.time means the cursor
    // has left the chart area — in which case we clear the tooltip.
    const onCrosshair = (param) => {
      if (!param || !param.time || !param.point) { setCrosshair(null); return; }
      const bar = param.seriesData && param.seriesData.get(candle);
      if (!bar) { setCrosshair(null); return; }
      setCrosshair({
        x: param.point.x,
        y: param.point.y,
        time: param.time,
        open: bar.open, high: bar.high, low: bar.low, close: bar.close,
      });
    };
    chart.subscribeCrosshairMove(onCrosshair);

    return () => {
      window.removeEventListener("resize", onResize);
      chart.unsubscribeCrosshairMove(onCrosshair);
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      vwapSeriesRef.current = null;
      vwapBandRefs.current = { upper1: null, lower1: null, upper2: null, lower2: null };
      priceLineRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPrefs.candleTheme, chartPrefs.grid]);

  // Apply right offset (empty bars to the right of the last candle) whenever the user changes it.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ro = Number.isFinite(chartPrefs.rightOffset) ? chartPrefs.rightOffset : 30;
    try { chart.timeScale().applyOptions({ rightOffset: Math.max(0, Math.min(200, ro)) }); } catch {}
  }, [chartPrefs.rightOffset]);

  // Load watchlist for the left rail
  // Iter121.3 — Fetch items scoped to the currently-active list. The
  // active pointer + lists themselves are managed by `useActiveWatchlist`
  // above; this effect just refetches the ITEMS whenever the active id
  // changes.
  const fetchWatchlistItems = useCallback(async (activeId) => {
    try {
      if (activeId) {
        const items = await axios.get(`${API}/watchlist`, { params: { watchlist_id: activeId } });
        setWatchlist(Array.isArray(items.data) ? items.data : []);
      } else {
        const items = await axios.get(`${API}/watchlist`);
        setWatchlist(Array.isArray(items.data) ? items.data : []);
      }
    } catch {
      try {
        const r = await axios.get(`${API}/watchlist`);
        setWatchlist(Array.isArray(r.data) ? r.data : []);
      } catch { /* noop */ }
    }
  }, []);
  useEffect(() => { fetchWatchlistItems(activeWatchlistId); }, [activeWatchlistId, fetchWatchlistItems]);

  // Iter121.3 — Switch the user's active watchlist via the shared hook,
  // then rerun the ranking scan so the new list is decorated immediately.
  const switchActiveWatchlist = async (newId) => {
    if (!newId || newId === activeWatchlistId) return;
    await switchActiveWatchlistBase(newId);
    runWatchlistScan(true);
  };

  // Inline "Add ticker" input state for the left rail
  const [newWatchSym, setNewWatchSym] = useState("");
  const [addWatchError, setAddWatchError] = useState(null);
  const [addingWatch, setAddingWatch] = useState(false);
  const addToWatchlist = async (e) => {
    e?.preventDefault?.();
    const sym = (newWatchSym || "").trim().toUpperCase();
    if (!sym) return;
    setAddingWatch(true);
    setAddWatchError(null);
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.post(`${API}/watchlist`, { symbol: sym, name: sym }, { params });
      setNewWatchSym("");
      await Promise.all([fetchWatchlistItems(activeWatchlistId), refetchWatchlists(activeWatchlistId)]);
      runWatchlistScan(true);
    } catch (e) {
      setAddWatchError(e?.response?.data?.detail || "Failed");
    }
    setAddingWatch(false);
  };
  const removeFromWatchlist = async (sym) => {
    if (!sym) return;
    try {
      const params = activeWatchlistId ? { watchlist_id: activeWatchlistId } : {};
      await axios.delete(`${API}/watchlist/${encodeURIComponent(sym)}`, { params });
      await Promise.all([fetchWatchlistItems(activeWatchlistId), refetchWatchlists(activeWatchlistId)]);
      runWatchlistScan(true);
    } catch (e) {
      // 404 = wasn't in the user's personal pool (probably shared, viewer can't remove) → silent
    }
  };

  const fetchAll = async (sym, tf) => {
    setError(null);
    setLoadingBars(true);
    setLoadingWes(true);
    setLoadingCandidates(true);
    const upper = (sym || "").toUpperCase();
    // Iter117.8 — Only clear symbol-scoped state when the symbol
    // actually changed. Timeframe switches and refresh-button clicks
    // now keep the previous chart / WES / candidates painted so the
    // operator no longer sees the whole cockpit flicker to empty.
    const prev = cockpitDataSymbolRef.current;
    const symbolChanged = upper !== (prev || "");
    if (symbolChanged) {
      setCandidates(null);
      setWes(null);
      setBars([]);
    }
    try {
      // Iter119.9 — Chart bars use the operator's selected TF; WES + candidates
      // map to "1h" for any intraday and "daily" for daily, since the backend
      // analytics engines are only implemented for those two granularities.
      const wesTf = wesTfFor(tf);
      const [b, w, c] = await Promise.allSettled([
        axios.get(`${API}/forge/bars`,       { params: { symbol: upper, timeframe: tf } }),
        axios.get(`${API}/forge/wes`,        { params: { symbol: upper, timeframe: wesTf, vp_bins: chartPrefs.vpBins, engine: wesEngine } }),
        axios.get(`${API}/forge/candidates`, { params: { symbol: upper, timeframe: wesTf, strike_band: strikeBand } }),
      ]);
      if (b.status === "fulfilled") setBars(b.value.data?.bars || []);
      else if (symbolChanged) { setBars([]); setError(b.reason?.response?.data?.detail || "Could not load bars"); }
      else setError(b.reason?.response?.data?.detail || "Could not load bars");
      if (w.status === "fulfilled") setWes(w.value.data);
      else if (symbolChanged) setWes(null);
      if (c.status === "fulfilled") setCandidates(c.value.data);
      else if (symbolChanged) setCandidates(null);
      // Mark the data as belonging to this symbol so subsequent
      // same-symbol refreshes don't trigger the symbol-change clear.
      cockpitDataSymbolRef.current = upper;
    } finally {
      setLoadingBars(false);
      setLoadingWes(false);
      setLoadingCandidates(false);
    }
  };

  useEffect(() => { fetchAll(symbol, timeframe); /* eslint-disable-next-line */ }, [symbol, timeframe, chartPrefs.vpBins, wesEngine, strikeBand]);

  // ---- Phase 4: Auto-refresh during market hours ----
  // Tick poll every 5s — lightweight, just spot/change/market_open. Updates the live chip without redrawing the chart.
  // Full refresh every 60s — re-fetches bars + WES + candidates.
  // Both pause when the market is closed (server tells us via tick.market_open).
  useEffect(() => {
    let cancelled = false;
    let tickTimer = null;
    let fullTimer = null;
    // Symbol just changed → clear stale tick from the previous symbol so the chart header
    // doesn't show e.g. SPY's price while CCL bars are loading. (issue #2 — frozen spot)
    setTick(null);
    setTickError(null);
    const fetchTick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/forge/tick`, { params: { symbol } });
        if (cancelled) return;
        // Guard against late responses for a previous symbol
        if (r.data?.symbol && r.data.symbol !== symbol) return;
        setTick(r.data);
        setTickError(null);

        // Forming-candle update: paint the live spot onto the most recent candle's close
        // so the chart visually tracks price every 5s — just like TradingView updates the
        // open candle in real time. We don't touch open/high/low/volume; we just nudge
        // the close (and high/low if the spot crossed them) of the latest bar. The next
        // 60s full refresh will replace this with the real bar data from Tradier.
        // DEFENSIVE: use barsRef (always fresh, not a stale closure), skip stale-cache
        // ticks, validate every OHLC field is finite & positive, and sanity-check the
        // spot against the last bar's close to avoid painting a garbage value when
        // the broker flashes a bad quote (this was the "massive wick to $0" bug).
        if (r.data?.spot && r.data.market_open !== false && r.data.source !== "cache" && !replayOn) {
          const candle = candleSeriesRef.current;
          const liveBars = barsRef.current;
          if (candle && liveBars && liveBars.length > 0) {
            const last = liveBars[liveBars.length - 1];
            try {
              const newClose = Number(r.data.spot);
              const lastOpen = Number(last.open);
              const lastHigh = Number(last.high);
              const lastLow = Number(last.low);
              // All values must be finite positive numbers
              if (
                Number.isFinite(newClose) && newClose > 0 &&
                Number.isFinite(lastOpen) && lastOpen > 0 &&
                Number.isFinite(lastHigh) && lastHigh > 0 &&
                Number.isFinite(lastLow) && lastLow > 0
              ) {
                // Anomaly guard: skip if spot deviates >25% from last bar's open
                // (prevents a single bad tick from distorting the chart).
                const dev = Math.abs(newClose - lastOpen) / lastOpen;
                if (dev <= 0.25) {
                  const newHigh = Math.max(lastHigh, newClose);
                  const newLow = Math.min(lastLow, newClose);
                  if (Number.isFinite(newHigh) && Number.isFinite(newLow) && newLow > 0) {
                    candle.update({
                      time: last.time,
                      open: lastOpen,
                      high: newHigh,
                      low: newLow,
                      close: newClose,
                    });
                  }
                }
              }
            } catch {}
          }
        }
      } catch (e) {
        if (cancelled) return;
        setTickError(e?.response?.data?.detail || "tick failed");
      }
    };
    // Kick off immediately
    fetchTick();
    tickTimer = setInterval(() => {
      // Pause polling outside market hours — admin still has the manual Refresh button.
      if (tick && tick.market_open === false) return;
      fetchTick();
    }, 5000);
    fullTimer = setInterval(() => {
      if (tick && tick.market_open === false) return;
      fetchAll(symbol, timeframe);
    }, 60000);
    return () => {
      cancelled = true;
      if (tickTimer) clearInterval(tickTimer);
      if (fullTimer) clearInterval(fullTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe]);

  // Chart toolbar handlers
  const zoomBy = (factor) => {
    const ts = chartRef.current?.timeScale();
    if (!ts) return;
    const r = ts.getVisibleLogicalRange();
    if (!r) return;
    const mid = (r.from + r.to) / 2;
    const half = (r.to - r.from) / 2 / factor;
    ts.setVisibleLogicalRange({ from: mid - half, to: mid + half });
  };
  const resetZoom = () => chartRef.current?.timeScale().fitContent();
  // Iter121.13 — Pan the chart to a specific bar time. Used by the
  // Signal Flow strip so operators can click a historical pill and
  // jump the visible range to the exact bar where that signal fired.
  // Preserves the current zoom width by re-centering around the target.
  const panChartToTime = React.useCallback((targetTime) => {
    const ts = chartRef.current?.timeScale();
    if (!ts || targetTime == null) return;
    const r = ts.getVisibleRange();
    if (!r || r.from == null || r.to == null) {
      ts.scrollToPosition(0, false);
      return;
    }
    const width = Number(r.to) - Number(r.from);
    if (!Number.isFinite(width) || width <= 0) return;
    const half = width / 2;
    try {
      ts.setVisibleRange({
        from: targetTime - half,
        to: targetTime + half,
      });
    } catch { /* invalid range — ignore */ }
  }, []);
  const toggleFullscreen = async () => {
    const el = cockpitRef.current;
    if (!el) return;
    try {
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen?.();
        setIsFullscreen(false);
      }
    } catch {
      // CSS fullscreen fallback for browsers/iframes that block the Fullscreen API
      setIsFullscreen(v => !v);
    }
  };
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Pin a candidate as a new Wheel ITM cycle — opens an editable review modal first
  const openPinModal = () => {
    if (!candidates?.decision?.trade_allowed) return;
    setPinResult(null);
    setPinModalOpen(true);
  };
  const submitPin = async (payload) => {
    try {
      const r = await axios.post(`${API}/cycles`, payload);
      setPinResult({ kind: "ok", msg: `Cycle opened — ${r.data.symbol} K${r.data.kp}`, id: r.data.id });
      setPinModalOpen(false);
    } catch (e) {
      throw e;
    }
  };

  // Phase 3 — Snapshots
  const fetchSnapshots = async () => {
    try {
      const r = await axios.get(`${API}/forge/snapshots`, { params: { limit: 25 } });
      setSnapshots(r.data || []);
    } catch {}
  };
  useEffect(() => { fetchSnapshots(); }, []);
  const saveSnapshot = async () => {
    setSavingSnap(true);
    try {
      const note = window.prompt("Add a note for this snapshot (optional):", "") || "";
      await axios.post(`${API}/forge/snapshots`, { symbol, timeframe, note });
      await fetchSnapshots();
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save snapshot");
    }
    setSavingSnap(false);
  };
  const deleteSnapshot = async (id) => {
    if (!window.confirm("Delete this snapshot?")) return;
    try { await axios.delete(`${API}/forge/snapshots/${id}`); await fetchSnapshots(); }
    catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };
  const replaySnapshot = (snap) => {
    if (snap?.symbol) {
      setDraftSymbol(snap.symbol);
      setSymbol(snap.symbol);
      if (snap.timeframe && snap.timeframe !== timeframe) setTimeframe(snap.timeframe);
      setShowSnapshots(false);
    }
  };

  // Phase 5 — derive what the chart actually renders (live bars vs replay slice)
  const displayBars = useMemo(() => {
    if (replayOn && replayData) {
      const past = replayData.past_bars || [];
      if (replayRevealed) return [...past, ...(replayData.future_bars || [])];
      return past;
    }
    return bars;
  }, [replayOn, replayData, replayRevealed, bars]);
  const displayWes = useMemo(() => {
    if (replayOn && replayData) return { ...(replayData.wes || {}), spot: replayData.spot_at };
    // Iter34 — Symbol-sync guard. If wes payload symbol doesn't match the
    // active chart, refuse to surface it (banner + WesPanel will show empty
    // state instead of stale data). Self-clears once fresh fetch lands.
    if (wes && wes.symbol && symbol && wes.symbol.toUpperCase() !== symbol.toUpperCase()) {
      return null;
    }
    return wes;
  }, [replayOn, replayData, wes, symbol]);

  // Iter119.12 — Today's OHLC + total volume, computed from the bars that
  // fall on the most-recent session date. Drives the new compact day-metrics
  // chip in the chart header (replaces the lonely bar count).
  const dayMetrics = useMemo(() => {
    if (!displayBars || displayBars.length === 0) return null;
    const lastBar = displayBars[displayBars.length - 1];
    const toDay = (t) => typeof t === "string"
      ? t
      : new Date(t * 1000).toISOString().slice(0, 10);
    const lastDay = toDay(lastBar.time);
    const today = displayBars.filter(b => toDay(b.time) === lastDay);
    if (today.length === 0) return null;
    return {
      open:  today[0].open,
      high:  Math.max(...today.map(b => b.high)),
      low:   Math.min(...today.map(b => b.low)),
      close: today[today.length - 1].close,
      vol:   today.reduce((s, b) => s + (Number.isFinite(b.volume) ? b.volume : 0), 0),
    };
  }, [displayBars]);

  // Push bars into the chart on bars change (volume histogram removed — replaced by VP overlay)
  useEffect(() => {
    const candle = candleSeriesRef.current;
    const vwap = vwapSeriesRef.current;
    if (!candle) return;
    const _bars = displayBars;
    if (!_bars || _bars.length === 0) {
      candle.setData([]);
      if (vwap) vwap.setData([]);
      const _bands = vwapBandRefs.current || {};
      _bands.upper1?.setData([]); _bands.lower1?.setData([]);
      _bands.upper2?.setData([]); _bands.lower2?.setData([]);
      return;
    }
    const ohlc = _bars.map(b => ({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close }));
    candle.setData(ohlc);

    // Session VWAP — 1H timeframe only. Resets each trading day.
    // Typical price = (H+L+C)/3, weighted by volume; daily session keyed by the bar's
    // UTC date (matches ET date for our RTH-filtered bars since aggregate_to_1h stores
    // naive-ET datetimes that become UTC timestamps with the same date).
    if (vwap) {
      const showVwap = chartPrefs.showVwap !== false;
      const showBands = chartPrefs.showVwapBands === true;
      // Iter119.9 — VWAP is intraday-only; 15m / 1h / 4h all qualify.
      const isIntraday = timeframe !== "daily";
      if (isIntraday && (showVwap || showBands)) {
        const series = computeSessionVwap(_bars);
        // VWAP main line
        if (showVwap) {
          vwap.setData(series.vwap);
          vwap.applyOptions({ visible: true });
        } else {
          vwap.setData([]);
          vwap.applyOptions({ visible: false });
        }
        // Bands
        const bands = vwapBandRefs.current || {};
        if (showBands) {
          bands.upper1?.setData(series.upper1); bands.upper1?.applyOptions({ visible: true });
          bands.lower1?.setData(series.lower1); bands.lower1?.applyOptions({ visible: true });
          bands.upper2?.setData(series.upper2); bands.upper2?.applyOptions({ visible: true });
          bands.lower2?.setData(series.lower2); bands.lower2?.applyOptions({ visible: true });
        } else {
          bands.upper1?.setData([]); bands.upper1?.applyOptions({ visible: false });
          bands.lower1?.setData([]); bands.lower1?.applyOptions({ visible: false });
          bands.upper2?.setData([]); bands.upper2?.applyOptions({ visible: false });
          bands.lower2?.setData([]); bands.lower2?.applyOptions({ visible: false });
        }
      } else {
        vwap.setData([]);
        vwap.applyOptions({ visible: false });
        const bands = vwapBandRefs.current || {};
        bands.upper1?.setData([]); bands.upper1?.applyOptions({ visible: false });
        bands.lower1?.setData([]); bands.lower1?.applyOptions({ visible: false });
        bands.upper2?.setData([]); bands.upper2?.applyOptions({ visible: false });
        bands.lower2?.setData([]); bands.lower2?.applyOptions({ visible: false });
      }
    }

    const ts = chartRef.current?.timeScale();
    if (!ts) return;
    if (replayOn) { ts.fitContent(); return; }
    // Apply user's time-range preset (cosmetic — bars stay loaded, just visible window changes)
    const last = ohlc[ohlc.length - 1];
    const first = ohlc[0];
    if (!last || !first) { ts.fitContent(); return; }
    const isDaily = timeframe === "daily";
    const pref = isDaily ? chartPrefs.tfRangeDaily : chartPrefs.tfRange1h;
    const dayDelta = (n) => 24 * 60 * 60 * n;
    if (pref === "all" || !pref) { ts.fitContent(); return; }
    try {
      const lastTime = typeof last.time === "string" ? Math.floor(Date.parse(last.time) / 1000) : Number(last.time);
      let span;
      if (pref === "last30d") span = dayDelta(30);
      else if (pref === "last60d") span = dayDelta(60);
      else if (pref === "last90d") span = dayDelta(90);
      else if (pref === "last5d") span = dayDelta(5);
      else if (pref === "last10d") span = dayDelta(10);
      else { ts.fitContent(); return; }
      ts.setVisibleRange({ from: lastTime - span, to: lastTime });
    } catch {
      ts.fitContent();
    }
  }, [displayBars, chartPrefs.tfRange1h, chartPrefs.tfRangeDaily, chartPrefs.showVwap, chartPrefs.showVwapBands, timeframe, replayOn]);

  // Iter119.9 — VAL/POC/VAH lines + labels are now rendered as an
  // HTML overlay (`<LevelLabels />`) above the SVG volume profile.
  // This fixes the legibility bug where the lightweight-charts
  // canvas-drawn price lines were obscured by the SVG VP bars
  // (canvas z=0, SVG z=5). The HTML overlay sits at z=10/20 so
  // both lines and labels render in front of the bars. We still
  // compute the aggression metadata here so the `<LevelLabels />`
  // can thicken the active VAL/VAH stroke.
  const levelOverlayMeta = useMemo(() => {
    const _wes = displayWes;
    if (!_wes || !_wes.val || !_wes.poc || !_wes.vah) return null;
    const agg = _wes.aggression;
    const aggEdge = agg?.edge;
    const aggType = agg?.type || null;  // "Bull" | "Bear" | null
    const isTurning = agg && (
      (agg.type === "Bull" && agg.edge === "VAH") ||
      (agg.type === "Bear" && agg.edge === "VAL")
    );
    const sigLabel = agg ? (isTurning ? "Turning Point" : "Continuation") : null;
    return {
      val: _wes.val, poc: _wes.poc, vah: _wes.vah,
      valBoost: aggEdge === "VAL", vahBoost: aggEdge === "VAH",
      sigLabel,
      // Iter121.10 — propagate aggression direction so LevelLabels can
      // recolor the active edge to match the WES card sentiment
      // (Bear at VAL → red pill, not green VAL default).
      aggType,
    };
  }, [displayWes]);

  // Recompute Volume Profile pixel positions whenever WES, bars, or chart geometry changes.
  // Subscribes to chart's visible-range / size changes so the overlay tracks pan + zoom.
  useEffect(() => {
    const chart = chartRef.current;
    const candle = candleSeriesRef.current;
    if (!chart || !candle) return;
    const compute = () => {
      const vp = displayWes?.volume_profile;
      if (!vp || !vp.bins || vp.bins.length === 0) {
        setVpOverlay({ bins: [], yByIdx: [], plotH: 0, poc: 0, va: [0, 0] });
        return;
      }
      const h = containerRef.current?.clientHeight || 0;
      const yByIdx = vp.bins.map(b => candle.priceToCoordinate(b.price));
      setVpOverlay({
        bins: vp.bins,
        yByIdx,
        plotH: h,
        poc: vp.poc_index,
        va: [vp.va_lo_index, vp.va_hi_index],
        maxVol: vp.max_vol || Math.max(...vp.bins.map(b => b.vol)),
      });
    };
    compute();
    // Re-compute when user pans/zooms or window resizes
    const ts = chart.timeScale();
    const sub = () => compute();
    ts.subscribeVisibleTimeRangeChange(sub);
    chart.subscribeCrosshairMove(() => {}); // keep alive
    const onResize = () => compute();
    window.addEventListener("resize", onResize);
    const interval = setInterval(compute, 1500); // catch price-scale auto-fit transitions
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(sub);
      window.removeEventListener("resize", onResize);
      clearInterval(interval);
    };
  }, [displayWes, displayBars]);

  // ---- Phase 2 — chain + decision are loaded via /api/forge/candidates above ----
  const onSubmitSymbol = (e) => {
    e.preventDefault();
    const v = (draftSymbol || "").trim().toUpperCase();
    if (v) setSymbol(v);
  };

  const onPickWatch = (s) => {
    setDraftSymbol(s);
    setSymbol(s);
  };

  return (
    <div ref={cockpitRef} data-testid="market-forge-page" className={`space-y-4 ${isFullscreen ? "fixed inset-0 z-[100] bg-[#08080A] p-4 overflow-auto" : ""}`}>
      {/* Iter122.7 — Shadow-mode banner. Renders only when the current
          user is BOTH eligible AND has explicitly opted in. v1 remains
          the cockpit's data source unless separately wired to v2. */}
      <ShadowBanner compact />
      {/* Page header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <CandlestickChart size={18} className="text-[#00FF88]" />
            <h1 className="text-2xl font-black tracking-tight text-white">{t("forge.title")}</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20">{t("forge.phase_badge")}</span>
          </div>
          <p className="text-xs text-[#8A8A93] font-mono">{t("forge.subtitle")}</p>
        </div>

        <form onSubmit={onSubmitSymbol} className="flex items-center gap-2 flex-wrap">
          <BrokerHealthStrip
            brokers={brokerHealth?.brokers}
            isAdmin={isAdmin}
            onHealthUpdate={setBrokerHealth}
          />
          <LanguageToggle testid="forge-language-toggle" />
          <div className="flex items-center gap-1.5 bg-[#08080A] border border-[#26262B] focus-within:border-[#00FF88] rounded-sm px-3 py-2">
            <Search size={14} className="text-[#8A8A93]" />
            <input
              data-testid="forge-symbol-input"
              type="text"
              value={draftSymbol}
              onChange={e => setDraftSymbol(e.target.value.toUpperCase())}
              placeholder={t("forge.symbol_placeholder")}
              className="bg-transparent outline-none w-20 text-sm text-white font-mono uppercase tracking-wider"
            />
          </div>
          {TF_OPTIONS.map(tf => (
            <button
              key={tf.id}
              type="button"
              onClick={() => setTimeframe(tf.id)}
              data-testid={`forge-tf-${tf.id}`}
              className={`text-xs font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border ${
                timeframe === tf.id
                  ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
                  : "border-[#26262B] text-[#8A8A93] hover:text-white"
              }`}
            >{tf.label}</button>
          ))}
          <button
            type="button"
            onClick={saveSnapshot}
            data-testid="forge-save-snapshot"
            disabled={savingSnap}
            className="flex items-center gap-1.5 text-xs border border-[#26262B] text-white px-3 py-2 rounded-sm disabled:opacity-50"
            title={t("forge.snapshot_tooltip")}
          >
            {savingSnap ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {t("forge.snapshot")}
          </button>
          <button
            type="button"
            onClick={() => { setShowSnapshots(s => !s); fetchSnapshots(); }}
            data-testid="forge-snapshots-toggle"
            className={`flex items-center gap-1.5 text-xs border px-3 py-2 rounded-sm ${showSnapshots ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-white"}`}
          >
            <BookOpen size={12} /> {t("forge.journal")} {snapshots.length > 0 ? `(${snapshots.length})` : ""}
          </button>
          <button
            type="button"
            onClick={() => fetchAll(symbol, timeframe)}
            data-testid="forge-refresh"
            disabled={loadingBars || loadingWes || loadingCandidates}
            className="flex items-center gap-1.5 text-xs border border-[#26262B] text-white px-3 py-2 rounded-sm disabled:opacity-50"
          >
            <RefreshCw size={12} className={loadingBars || loadingWes || loadingCandidates ? "animate-spin" : ""} /> {t("common.refresh")}
          </button>
        </form>
      </div>

      {showSnapshots && (
        <SnapshotsDrawer
          snapshots={snapshots}
          onClose={() => setShowSnapshots(false)}
          onReplay={replaySnapshot}
          onDelete={deleteSnapshot}
          onRefresh={fetchSnapshots}
          t={t}
        />
      )}

      {error && (
        <div data-testid="forge-error" className="flex items-center gap-2 bg-[#FF3366]/10 border border-[#FF3366]/30 text-[#FF3366] px-3 py-2 rounded-sm text-xs font-mono">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Iter34 — Symbol-sync guard banner. If WES, GEX, or candidates report
          a symbol different from the active chart, refuse to trust the data
          and surface a loud red banner. Self-clears on next aligned tick. */}
      {(() => {
        const expect = (symbol || "").toUpperCase();
        const mismatches = [];
        if (wes?.symbol && wes.symbol.toUpperCase() !== expect) mismatches.push(`WES=${wes.symbol}`);
        if (gexReport?.symbol && gexReport.symbol.toUpperCase() !== expect) mismatches.push(`GEX=${gexReport.symbol}`);
        if (candidates?.symbol && candidates.symbol.toUpperCase() !== expect) mismatches.push(`CAND=${candidates.symbol}`);
        if (mismatches.length === 0) return null;
        return (
          <div data-testid="forge-symbol-mismatch-banner"
               className="flex items-center gap-2 bg-[#FF3366]/15 border border-[#FF3366] text-[#FF3366] px-3 py-2 rounded-sm text-xs font-mono font-bold animate-pulse">
            <AlertTriangle size={14} /> GEX DATA MISMATCH — DO NOT USE · Chart={expect} · {mismatches.join(" · ")}
          </div>
        );
      })()}

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — ranked watchlist scanner */}
        <aside className="col-span-12 lg:col-span-3 xl:col-span-2 bg-[#121216] border border-[#26262B] rounded-sm p-3" data-testid="forge-watchlist-rail">
          {/* Iter121.2 — Watchlist picker. Replaces the static "WATCHLIST"
              label with a dropdown that lets the operator switch between
              named lists. Manage modal opens via the gear icon. */}
          <WatchlistPicker
            watchlists={watchlists}
            activeId={activeWatchlistId}
            onSwitch={switchActiveWatchlist}
            onManage={() => setShowManageWatchlists(true)}
            variant="compact"
            testidPrefix="forge-wl-picker"
          />
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("forge.watchlist")}</div>
            <button
              type="button"
              data-testid="forge-watchlist-scan"
              onClick={() => runWatchlistScan(true)}
              disabled={scanLoading}
              title={t("forge.scan_all")}
              className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#00FF88] hover:border-[#00FF88]/40 disabled:opacity-50"
            >
              {scanLoading ? <Loader2 size={10} className="animate-spin" /> : <Zap size={10} />}
            </button>
          </div>
          {/* Sort toggle */}
          <div className="flex gap-1 mb-2 text-[9px] font-bold uppercase tracking-[0.15em]">
            <button
              type="button"
              data-testid="forge-watchlist-mode-ranked"
              onClick={() => setRankMode("ranked")}
              className={`flex-1 py-1 rounded-sm border ${rankMode === "ranked" ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"}`}
            >{t("forge.ranked")}</button>
            <button
              type="button"
              data-testid="forge-watchlist-mode-alpha"
              onClick={() => setRankMode("alpha")}
              className={`flex-1 py-1 rounded-sm border ${rankMode === "alpha" ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93]"}`}
            >{t("forge.alphabetical")}</button>
          </div>
          {/* Scan meta line */}
          {scanMeta && (
            <div className="text-[8px] font-mono text-[#8A8A93] mb-2 leading-tight" data-testid="forge-watchlist-meta">
              <div>{t("forge.last_scanned")}: {scanMeta.scanned_at ? new Date(scanMeta.scanned_at).toLocaleTimeString() : "—"}{scanMeta.cached && ` · ${t("forge.cached")}`}</div>
              {(scanMeta.errors || 0) > 0 && <div className="text-[#FFB800]">{scanMeta.errors} {t("forge.scan_errors")}</div>}
            </div>
          )}

          {/* Iter121.19 — Bulk-error banner. When >=30% of the ranked
              rows failed AND we have at least 5 rows, surface an
              actionable retry so the operator can force a fresh scan
              without hunting through the rail's individual tooltips.
              Zero-cost visual signal — hides itself when things are
              healthy. */}
          {(() => {
            const totalRows = Array.isArray(scanRows) ? scanRows.length : 0;
            const errRows = Array.isArray(scanRows)
              ? scanRows.filter(r => (r.wes_engine === "err") || (r.wes_p == null || r.wes_c == null)).length
              : 0;
            if (totalRows < 5 || errRows / totalRows < 0.3) return null;
            const pct = Math.round((errRows / totalRows) * 100);
            // Grab the most common short-error message so the banner
            // shows the LIKELY root cause at a glance.
            const shortErr = (() => {
              const counts = {};
              for (const r of (scanRows || [])) {
                if (!r.error) continue;
                const k = String(r.error).replace(/^wes:/, "").replace(/^cand:/, "").slice(0, 70);
                counts[k] = (counts[k] || 0) + 1;
              }
              const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
              return top ? top[0] : null;
            })();
            return (
              <div
                data-testid="forge-watchlist-bulk-err-banner"
                className="mb-2 rounded-sm border border-[#FF3366]/45 bg-[#FF3366]/8 p-1.5"
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[9px] font-mono font-bold uppercase tracking-[0.1em] text-[#FF3366]">
                    {errRows}/{totalRows} ERR ({pct}%)
                  </span>
                  <button
                    data-testid="forge-watchlist-bulk-err-retry"
                    onClick={() => runWatchlistScan(true)}
                    disabled={scanLoading}
                    title="Force a fresh scan bypassing the 5-minute cache. Useful after fixing a broker credential."
                    className="text-[9px] font-mono font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/8 hover:bg-[#00FF88]/15 disabled:opacity-40 transition-colors"
                  >
                    {scanLoading ? "\u23F3" : "\u21BB"} RETRY
                  </button>
                </div>
                {shortErr && (
                  <div
                    data-testid="forge-watchlist-bulk-err-detail"
                    className="mt-1 text-[8px] font-mono text-[#FF3366]/85 leading-snug break-words"
                    title={shortErr}
                  >
                    {shortErr.length > 90 ? shortErr.slice(0, 90) + "\u2026" : shortErr}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Add ticker — every logged-in user can add. Admin adds go to the SHARED pool;
               viewers add to their PERSONAL pool (only they see them). */}
          <form onSubmit={addToWatchlist} className="flex items-center gap-1 mb-2" data-testid="forge-watchlist-add-form">
            <input
              data-testid="forge-watchlist-add-input"
              type="text"
              value={newWatchSym}
              onChange={e => setNewWatchSym(e.target.value.toUpperCase())}
              placeholder="+ TICKER"
              className="flex-1 min-w-0 bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] focus:outline-none text-white rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-wider"
            />
            <button
              type="submit"
              data-testid="forge-watchlist-add-button"
              disabled={addingWatch || !newWatchSym.trim()}
              className="bg-[#00FF88] text-[#08080A] px-2 py-1 rounded-sm text-[10px] font-bold disabled:opacity-40 flex-shrink-0"
            >
              {addingWatch ? <Loader2 size={10} className="animate-spin" /> : "+"}
            </button>
          </form>
          {addWatchError && <div className="text-[9px] text-[#FF3366] mb-1.5 font-mono" data-testid="forge-watchlist-add-error">{addWatchError}</div>}

          <WatchlistRail
            watchlist={watchlist}
            scanRows={scanRows}
            rankMode={rankMode}
            currentSymbol={symbol}
            onPick={onPickWatch}
            onRemove={removeFromWatchlist}
            t={t}
          />
        </aside>

        {/* CENTER — chart (widened) */}
        <section className="col-span-12 lg:col-span-7 xl:col-span-8 bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#26262B]">
            <div className="flex items-center gap-3 flex-wrap">
              <CompanyLogo symbol={symbol} size={28} />
              <div className="flex flex-col leading-tight">
                <span className="text-base font-bold text-white tracking-tight" data-testid="forge-header-symbol">{symbol}</span>
                <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]" data-testid="forge-header-tf-label">{
                  {"15m": "15 MIN", "1h": "1 HOUR", "4h": "4 HOUR", "daily": "DAILY"}[timeframe] || timeframe.toUpperCase()
                }</span>
              </div>
              {/* Iter121.1 — Bar close countdown. Renders once we have at least one bar.
                  Ticks every second; flips green <10s, amber <60s, neutral otherwise. */}
              {displayBars && displayBars.length > 0 && (
                <BarTimerChip
                  lastBarTime={displayBars[displayBars.length - 1].time}
                  timeframe={timeframe}
                />
              )}
              {/* Iter119.12 — Promoted price + change pill. Bigger, more
                  readable, with a tinted background pill for the % change so
                  the operator catches direction at a glance. */}
              {(tick?.spot != null || wes?.spot != null) && (() => {
                const spot = tick?.spot ?? wes?.spot;
                const change = tick?.change;
                const changePct = tick?.change_pct;
                const up = change != null ? change >= 0 : null;
                // Iter121.10 — Inline `MKT CLOSED` pill directly next to
                // the price so the operator gets a second confirmation
                // of session state without scanning to the far-right
                // status badges. Renders only when the tick service
                // explicitly reports market_open=false.
                const marketClosed = tick?.market_open === false;
                return (
                  <div className="flex items-baseline gap-2" data-testid="forge-tick-spot">
                    <span className="text-lg font-bold font-mono text-white tracking-tight">{fmtMoney(spot)}</span>
                    {change != null && (
                      <span
                        className={`text-[10px] font-bold font-mono px-1.5 py-0.5 rounded-sm ${up
                          ? "text-[#00FF88] bg-[#00FF88]/12 border border-[#00FF88]/35"
                          : "text-[#FF3366] bg-[#FF3366]/12 border border-[#FF3366]/35"}`}
                        data-testid="forge-tick-change-pill"
                      >
                        {up ? "▲" : "▼"} {Math.abs(change).toFixed(2)}
                        {changePct != null && ` (${Math.abs(changePct).toFixed(2)}%)`}
                      </span>
                    )}
                    {marketClosed && (
                      <span
                        data-testid="forge-mkt-closed-pill"
                        title="Regular trading hours are closed. Bars are frozen at last session close."
                        className="inline-flex items-center gap-1 text-[9px] font-bold font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]"
                      >
                        <span className="w-1 h-1 rounded-full bg-[#FFB800]" />
                        MKT CLOSED
                      </span>
                    )}
                  </div>
                );
              })()}
              {/* Iter119.12 — Compact day-metrics strip: O / H / L / Vol.
                  Replaces the lonely bar-count chip; gives the operator the
                  TradingView-style "data window" at a glance. */}
              {dayMetrics && (
                <div className="hidden lg:flex items-center gap-2.5 text-[10px] font-mono text-[#8A8A93] pl-2 border-l border-[#26262B]" data-testid="forge-day-metrics">
                  <span><span className="text-[#5F5F66]">O</span> <span className="text-white">{dayMetrics.open.toFixed(2)}</span></span>
                  <span><span className="text-[#5F5F66]">H</span> <span className="text-[#00FF88]">{dayMetrics.high.toFixed(2)}</span></span>
                  <span><span className="text-[#5F5F66]">L</span> <span className="text-[#FF3366]">{dayMetrics.low.toFixed(2)}</span></span>
                  <span><span className="text-[#5F5F66]">V</span> <span className="text-white">{fmtVol(dayMetrics.vol)}</span></span>
                </div>
              )}
              {/* Live / Closed badge + data source freshness chip */}
              <LiveBadge tick={tick} tickError={tickError} bars={displayBars} t={t} />
              {wes?.bar_count != null && <span className="text-[10px] font-mono text-[#8A8A93]">{wes.bar_count} {t("forge.bars_count")}</span>}
              {/* Iter119.12 — Drawings list chip; only renders when ≥1 drawing exists. */}
              {drawings.length > 0 && (
                <button
                  type="button"
                  data-testid="forge-drawings-chip"
                  onClick={() => setShowDrawingsMgr(s => !s)}
                  title="Manage drawings"
                  className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-sm border transition-colors ${
                    showDrawingsMgr
                      ? "border-[#22D3FF] text-[#22D3FF] bg-[#22D3FF]/10"
                      : "border-[#26262B] text-[#8A8A93] hover:text-[#22D3FF] hover:border-[#22D3FF]/40"
                  }`}
                >
                  <GitMerge size={10} /> Drawings: {drawings.length}
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {/* Zoom toolbar */}
              <button onClick={() => zoomBy(1.4)} data-testid="forge-zoom-in" title={t("forge.zoom_in")} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm"><ZoomIn size={13} /></button>
              <button onClick={() => zoomBy(1 / 1.4)} data-testid="forge-zoom-out" title={t("forge.zoom_out")} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm"><ZoomOut size={13} /></button>
              <button onClick={resetZoom} data-testid="forge-zoom-reset" title={t("forge.zoom_reset")} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm"><RotateCcw size={13} /></button>
              {/* Iter119.13 — Chart visibility quick-toggle popover. Highlights when ANY overlay is off so the operator can spot dimmed layers at a glance. */}
              {(() => {
                const allOn = chartPrefs.showVp !== false
                  && chartPrefs.showLevels !== false
                  && chartPrefs.showDrawings !== false
                  && chartPrefs.showWatermark !== false
                  && chartPrefs.showVwap !== false;
                return (
                  <button
                    onClick={() => setShowVisToggle(s => !s)}
                    data-testid="forge-chart-visibility"
                    title="Show / hide overlays"
                    aria-label="Show / hide overlays"
                    className={`p-1.5 border rounded-sm ${
                      !allOn || showVisToggle
                        ? "border-[#22D3FF] text-[#22D3FF] bg-[#22D3FF]/10"
                        : "border-[#26262B] text-[#8A8A93] hover:text-white"
                    }`}
                  >
                    <Eye size={13} />
                  </button>
                );
              })()}
              <button onClick={() => setShowChartMenu(s => !s)} data-testid="forge-chart-settings" title={t("forge.chart_settings")} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm"><SettingsIcon size={13} /></button>
              <button
                onClick={() => setReplayOn(v => !v)}
                data-testid="forge-replay-toggle"
                title={t("replay.toggle_tip")}
                className={`p-1.5 border rounded-sm ${replayOn ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
              >
                <History size={13} />
              </button>
              <button
                onClick={() => setShowBatchModal(true)}
                data-testid="forge-batch-backtest-toggle"
                title={t("batch.toggle_tip")}
                className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm"
              >
                <BarChart3 size={13} />
              </button>
              <button onClick={toggleFullscreen} data-testid="forge-fullscreen" title={isFullscreen ? t("forge.exit_fullscreen") : t("forge.fullscreen")} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm">
                {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
              </button>
              <span className="hidden md:flex items-center gap-3 text-[10px] font-mono text-[#8A8A93] ml-2 pl-3 border-l border-[#26262B]" data-testid="forge-header-level-legend">
                {[
                  { key: "val", label: "VAL" },
                  { key: "poc", label: "POC" },
                  { key: "vah", label: "VAH" },
                ].map(({ key, label }) => {
                  const ls = levelStyles[key];
                  if (!ls.visible) return null;
                  const borderTopStyle = ls.style === "dashed" ? "dashed" : ls.style === "dotted" ? "dotted" : "solid";
                  return (
                    <span key={key} className="flex items-center gap-1" data-testid={`forge-header-legend-${key}`}>
                      <span
                        className="inline-block w-3"
                        style={{ borderTopWidth: `${ls.width}px`, borderTopStyle, borderTopColor: ls.color, height: 0 }}
                      />
                      {label}
                    </span>
                  );
                })}
              </span>
            </div>
          </div>
          {/* Iter121.11 — Aggression sentiment history strip. Shows the last
              N transitions so operators can spot rapid Bear→Bull flips or
              repeated turning-point attempts at the same edge. Hidden when
              no signals have been recorded for this symbol+timeframe. */}
          <SignalHistoryStrip
            history={signalHistory}
            timeframe={timeframe}
            onPan={panChartToTime}
          />
          {replayOn && (
            <ReplayControls
              asOf={replayAsOf} setAsOf={setReplayAsOf}
              dte={replayDte} setDte={setReplayDte}
              loading={replayLoading}
              error={replayError}
              data={replayData}
              revealed={replayRevealed}
              setRevealed={setReplayRevealed}
              onRerun={fetchReplay}
              onClose={() => setReplayOn(false)}
              t={t}
            />
          )}
          <div className="relative">
            {/* Iter119.12 — Symbol watermark. Big faded SYMBOL · TF text
                centered behind the chart at z=1, below the VP overlay (z=5)
                and everything else. Adds TradingView-style "this is which
                chart" branding without cluttering the foreground.
                Iter119.13 — Master toggle: `chartPrefs.showWatermark`. */}
            {chartPrefs.showWatermark !== false && (
              <div
                data-testid="forge-watermark"
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center z-[1] select-none"
              >
                <div className="text-center" style={{ opacity: 0.04 }}>
                  <div className="text-[110px] leading-none font-extrabold tracking-[-0.04em] text-white">{symbol}</div>
                  <div className="text-[20px] tracking-[0.5em] font-bold text-white mt-2">
                    {{"15m": "15 MIN", "1h": "1 HOUR", "4h": "4 HOUR", "daily": "DAILY"}[timeframe] || timeframe.toUpperCase()}
                  </div>
                </div>
              </div>
            )}
            <div data-testid="forge-chart" ref={containerRef} style={{ width: "100%", height: isFullscreen ? "calc(100vh - 360px)" : 540 }} />
            {/* Ticker watermark — large faded acronym inside the chart, like TradingView */}
            <div
              data-testid="forge-chart-watermark"
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              aria-hidden="true"
            >
              <span
                className="font-black tracking-[-0.03em] text-white/[0.04] select-none"
                style={{ fontSize: "clamp(64px, 14vw, 220px)", lineHeight: 1, letterSpacing: "-0.04em" }}
              >
                {symbol}
              </span>
            </div>
            {/* Iter119.13 — Master VP toggle. */}
            {chartPrefs.showVp !== false && (
              <VolumeProfileOverlay overlay={vpOverlay} vaOpacity={chartPrefs.vaOpacity} />
            )}
            {/* Iter119.11 — Drawing tools: layer (z=12) + toolbar (z=15).
                Iter119.13 — Master drawings toggle: when `showDrawings` is
                off we still render the layer (so drawing creation still works)
                but pass empty drawings so nothing displays. */}
            <DrawingLayer
              chartRef={chartRef}
              candleSeriesRef={candleSeriesRef}
              drawings={chartPrefs.showDrawings === false ? [] : drawings}
              tool={drawTool}
              timeframe={timeframe}
              symbol={symbol}
              onCommit={commitDrawing}
              onDelete={deleteDrawing}
              snapMode={snapMode}
              bars={displayBars}
            />
            <DrawingToolbar
              tool={drawTool}
              onToolChange={setDrawTool}
              drawingCount={drawings.length}
              onClearAll={clearAllDrawings}
              snapMode={snapMode}
              onToggleSnap={toggleSnapMode}
              t={t}
            />
            {showDrawingsMgr && (
              <DrawingsManagerPopover
                drawings={drawings}
                timeframe={timeframe}
                onToggleHidden={toggleDrawingHidden}
                onDelete={deleteDrawing}
                onUpdate={updateDrawing}
                onJumpTo={(d) => { jumpToDrawing(d); }}
                onClose={() => setShowDrawingsMgr(false)}
                onClearAll={clearAllDrawings}
              />
            )}
            {/* Iter119.9 — VAL/POC/VAH lines + labels above the VP overlay (z=10/20).
                Iter119.13 — Master toggle: when `showLevels` is off, hide the entire overlay. */}
            {chartPrefs.showLevels !== false && (
              <LevelLabels
                chartRef={chartRef}
                candleSeriesRef={candleSeriesRef}
                meta={levelOverlayMeta}
                styles={levelStyles}
                plotH={vpOverlay.plotH || (containerRef.current?.clientHeight || 0)}
              />
            )}
            {/* Iter119.9 — Crosshair OHLC tooltip (z=25, above everything). */}
            <CrosshairTooltip
              data={crosshair}
              timeframe={timeframe}
              containerWidth={containerRef.current?.clientWidth}
            />
            {showChartMenu && (
              <ChartSettingsPopover
                prefs={chartPrefs}
                onChange={(patch) => setChartPrefs(p => ({ ...p, ...patch }))}
                onClose={() => setShowChartMenu(false)}
                timeframe={timeframe}
                levelStyles={levelStyles}
                onLevelChange={updateLevelStyle}
                onSaveLevelsDefault={saveLevelsAsDefault}
                onResetLevels={resetLevelsForSymbol}
                symbol={symbol}
                presets={chartPresets}
                onSavePreset={saveChartPreset}
                onLoadPreset={loadChartPreset}
                onDeletePreset={deleteChartPreset}
                t={t}
              />
            )}
            {showVisToggle && (
              <ChartVisibilityPopover
                prefs={chartPrefs}
                onChange={(patch) => setChartPrefs(p => ({ ...p, ...patch }))}
                onClose={() => setShowVisToggle(false)}
                drawingCount={drawings.length}
              />
            )}
          </div>

          {/* GEX Panel — below the chart, inside the same column. Collapsible; default open. */}
          {!replayOn && (
            <GEXPanel
              symbol={symbol}
              timeframe={timeframe}
              candidateStrike={candidates?.decision?.primary_strike || candidates?.pure_wheel?.strike || null}
              t={t}
              onUpdate={setGexReport}
            />
          )}

          {/* Iter117 — Per-strategy verdicts (Strategy Scanner doctrine
              extended to Market Forge). Renders right below the GEX
              "Advanced View · By-Strike" curve so the operator can see
              all five evaluator modules' verdicts for the current symbol
              without leaving the chart. Pure read-only — same
              unified-preview endpoint the Strategy Scanner page uses.
              Iter117.1 — `timeframe` is wired through so the section
              refetches whenever the operator switches chart timeframe
              (or clicks a different watchlist symbol). */}
          {!replayOn && (
            <StrategyVerdictsSection symbol={symbol} timeframe={timeframe} t={t} />
          )}
        </section>

        {/* Phase 6 — Batch Backtest modal */}
        {showBatchModal && (
          <BatchBacktestModal
            symbol={symbol}
            onClose={() => setShowBatchModal(false)}
            t={t}
          />
        )}

        {/* Iter121.2 — Manage watchlists modal. CRUD on the named lists
            without leaving Market Forge. */}
        {showManageWatchlists && (
          <ManageWatchlistsModal
            watchlists={watchlists}
            activeId={activeWatchlistId}
            onClose={() => setShowManageWatchlists(false)}
            onRefresh={() => Promise.all([refetchWatchlists(activeWatchlistId), fetchWatchlistItems(activeWatchlistId)])}
            onSwitchActive={switchActiveWatchlist}
            testidPrefix="forge-wl-manage"
          />
        )}

        {/* RIGHT — Consolidated rail (Iter116). Default view = WES Structure
            + Trade Verdict. Detail panels (WES-GEX Path, Strike Decision,
            Decision V2) are tucked behind the "Show detailed breakdown"
            toggle so the operator gets a single, unambiguous verdict by
            default but can still inspect the raw engine outputs on demand. */}
        <aside className="col-span-12 lg:col-span-2 xl:col-span-2 space-y-4">
          <WesPanel wes={displayWes} loading={loadingWes || replayLoading} t={t}
                    wesEngine={wesEngine} setWesEngine={setWesEngine} />
          {replayOn ? (
            <ReplayDecisionPanel data={replayData} loading={replayLoading} revealed={replayRevealed} t={t} />
          ) : (
            <>
              {/* Iter116 — Single unified Trade Verdict card. Consumes
                  candidates.decision + gexReport from the cockpit and
                  fetches /api/forge/strike-decision internally so the
                  status pill, ROI gate, GEX risk readout, suggested
                  strikes and DO/DON'T playbook all live in one place. */}
              <TradeVerdictCard
                symbol={symbol}
                wesEngine={wesEngine}
                candidates={candidates}
                candidateStrike={evaluatedStrike ?? (gexReport?.candidate_strike || null)}
                strategy={(gexReport?.position?.shares || 0) > 0 ? "WHEEL_ITM_CC" : "WHEEL_ITM_CSP"}
                freshnessState="FRESH"
                gexReport={gexReport}
                onPin={openPinModal}
                pinResult={pinResult}
                onEvaluate={(strike) => setEvaluatedStrike(strike)}
                detailsOpen={showRailDetails}
                onToggleDetails={() => setShowRailDetails(s => !s)}
                t={t}
              />
              {showRailDetails && (
                <div data-testid="forge-rail-details" className="space-y-4">
                  <WesGexPanel symbol={symbol} timeframe={timeframe}
                                wesEngine={wesEngine} t={t} candidates={candidates}
                                candidateStrike={evaluatedStrike ?? (gexReport?.candidate_strike || null)}
                                strategy={(gexReport?.position?.shares || 0) > 0 ? "WHEEL_ITM_CC" : "WHEEL_ITM_CSP"} />
                  <StrikeDecisionCard
                    symbol={symbol}
                    wesEngine={wesEngine}
                    candidateStrike={evaluatedStrike ?? (gexReport?.candidate_strike || null)}
                    strategy={(gexReport?.position?.shares || 0) > 0 ? "WHEEL_ITM_CC" : "WHEEL_ITM_CSP"}
                    freshnessState="FRESH"
                    onEvaluate={(strike) => setEvaluatedStrike(strike)}
                  />
                  <DecisionPanelV2
                    candidates={candidates}
                    loading={loadingCandidates}
                    onPin={openPinModal}
                    pinResult={pinResult}
                    gexReport={gexReport}
                    t={t}
                  />
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      {/* BOTTOM — Options Chain table (live mode only — replay hides the live chain) */}
      {!replayOn && <ChainTable candidates={candidates} loading={loadingCandidates} strikeBand={strikeBand} onStrikeBandChange={setStrikeBand} t={t} />}

      {pinModalOpen && candidates && (
        <PinCycleModal
          candidates={candidates}
          onSubmit={submitPin}
          onClose={() => setPinModalOpen(false)}
          t={t}
        />
      )}
    </div>
  );
}

function VolumeProfileOverlay({ overlay, vaOpacity = "default" }) {
  const { bins, yByIdx, plotH, poc: pocIdx, va, maxVol } = overlay;
  if (!bins || bins.length === 0 || !plotH) return null;
  const overlayW = 180;
  // Iter119.9 — Pulled back from 64 to 100 so the VP bars stop further
  // to the left, leaving clearance for the price-scale labels AND the
  // new HTML level-label badges (VAL/POC/VAH).
  const overlayRight = 100;
  const valid = yByIdx.map((y, i) => ({ y, i, b: bins[i] })).filter(o => o.y != null);
  if (valid.length === 0) return null;
  valid.sort((a, b) => a.y - b.y);
  const rowHs = valid.map((o, i) => i < valid.length - 1 ? Math.max(1, valid[i + 1].y - o.y) : (plotH / bins.length));
  const safeMax = maxVol > 0 ? maxVol : 1;
  // Visual emphasis preference
  const opacityFor = (inVA, isPOC) => {
    if (isPOC) return 1;
    if (vaOpacity === "subtle")  return inVA ? 0.65 : 0.25;
    if (vaOpacity === "bold")    return inVA ? 1.0  : 0.55;
    return inVA ? 0.85 : 0.45; // 'default'
  };
  return (
    <svg
      data-testid="forge-vp-overlay"
      style={{ position: "absolute", top: 0, right: overlayRight, width: overlayW, height: plotH, pointerEvents: "none", zIndex: 5 }}
      width={overlayW}
      height={plotH}
    >
      {valid.map((o, i) => {
        const buy = o.b.buy || 0;
        const sell = o.b.sell || 0;
        const totalLen = ((buy + sell) / safeMax) * (overlayW - 8);
        if (totalLen <= 0) return null;
        const buyLen = totalLen * (buy / Math.max(1e-9, buy + sell));
        const sellLen = totalLen - buyLen;
        const inVA = o.i >= va[0] && o.i <= va[1];
        const isPOC = o.i === pocIdx;
        const rowH = Math.max(1, rowHs[i] - 0.5);
        const yTop = o.y - rowH / 2;
        const opacity = opacityFor(inVA, isPOC);
        // Bars grow LEFT from the right edge: sell first (red, outermost = closest to price scale), then buy (green)
        return (
          <g key={o.i} opacity={opacity}>
            <rect x={overlayW - sellLen} y={yTop} width={sellLen} height={rowH} fill={COLORS.red} />
            <rect x={overlayW - sellLen - buyLen} y={yTop} width={buyLen} height={rowH} fill={COLORS.green} />
            {isPOC && <rect x={0} y={yTop} width={overlayW} height={rowH} fill="rgba(255,184,0,0.10)" />}
          </g>
        );
      })}
      {/* POC dashed marker line across the VP band */}
      {pocIdx != null && yByIdx[pocIdx] != null && (
        <line
          x1={0} x2={overlayW}
          y1={yByIdx[pocIdx]} y2={yByIdx[pocIdx]}
          stroke={COLORS.amber} strokeWidth={1} strokeDasharray="3 3" opacity={0.7}
        />
      )}
    </svg>
  );
}


// Iter119.9 — VAL / POC / VAH lines + label badges as an HTML overlay.
// Solves the legibility bug where the SVG VP overlay (z=5) was painting
// over the lightweight-charts canvas-drawn price lines (z=0). Now the
// lines AND badges live in a sibling div at z=10/20, ON TOP of the VP.
//
// Why HTML instead of staying on the lightweight-charts canvas?
//   • Internal z-order in lightweight-charts is fixed; we cannot push
//     the canvas above our own SVG overlay.
//   • Rendering ourselves unlocks per-symbol customization (color,
//     dashed/dotted/solid, line width, per-line visibility) without
//     ripping into the chart library.
//
// Tracks pan/zoom + price-scale changes via the same subscribeVisibleTimeRange
// + 1.5s interval pattern the VP overlay already uses (`priceToCoordinate`).
function LevelLabels({ chartRef, candleSeriesRef, meta, styles, plotH }) {
  const [pos, setPos] = useState({ val: null, poc: null, vah: null });
  useEffect(() => {
    const chart = chartRef.current;
    const candle = candleSeriesRef.current;
    if (!chart || !candle || !meta) { setPos({ val: null, poc: null, vah: null }); return; }
    const recompute = () => {
      const get = (p) => {
        if (p == null) return null;
        const y = candle.priceToCoordinate(p);
        return Number.isFinite(y) ? y : null;
      };
      setPos({ val: get(meta.val), poc: get(meta.poc), vah: get(meta.vah) });
    };
    recompute();
    const ts = chart.timeScale();
    const sub = () => recompute();
    ts.subscribeVisibleTimeRangeChange(sub);
    const onResize = () => recompute();
    window.addEventListener("resize", onResize);
    const interval = setInterval(recompute, 1500);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(sub);
      window.removeEventListener("resize", onResize);
      clearInterval(interval);
    };
  }, [chartRef, candleSeriesRef, meta]);

  if (!meta || !plotH) return null;

  // Iter121.10 — When an aggression signal fires on VAL or VAH, override
  // the pill's color to match the SENTIMENT (Bull=green, Bear=red) so
  // the chart doesn't contradict the WES Structure card. Otherwise a
  // "Bear at VAL — Turning Point" chip (red) would sit next to a green
  // "VAL · Turning Point" label because VAL defaults to green.
  const AGG_GREEN = "#00FF88";
  const AGG_RED = "#FF3366";
  const aggColor = meta.aggType === "Bull" ? AGG_GREEN : meta.aggType === "Bear" ? AGG_RED : null;

  const renderLevel = (key, price, labelText, boost, overrideColor) => {
    const s = styles[key];
    if (!s || !s.visible) return null;
    const y = pos[key];
    if (y == null) return null;
    const widthPx = Math.max(1, Math.min(4, (s.width || 1) + (boost ? 1 : 0)));
    const borderStyle = s.style === "dashed" ? "dashed" : s.style === "dotted" ? "dotted" : "solid";
    // Boosted level with an aggression sentiment overrides both line + pill
    // color to communicate the DIRECTION of the signal, not the level type.
    const lineColor = boost && overrideColor ? overrideColor : s.color;
    const pillColor = boost && overrideColor ? overrideColor : s.color;
    return (
      <React.Fragment key={key}>
        <div
          data-testid={`forge-level-line-${key}`}
          data-testid-agg={boost && overrideColor ? (meta.aggType || "").toLowerCase() : ""}
          style={{
            position: "absolute",
            left: 0,
            // Stop the line BEFORE the price scale so the y-axis ticks
            // remain readable. 64px matches the price scale width.
            right: 64,
            top: Math.round(y - widthPx / 2),
            borderTopWidth: `${widthPx}px`,
            borderTopStyle: borderStyle,
            borderTopColor: lineColor,
            pointerEvents: "none",
            zIndex: 10,
          }}
        />
        <div
          data-testid={`forge-level-label-${key}`}
          data-testid-agg={boost && overrideColor ? (meta.aggType || "").toLowerCase() : ""}
          style={{
            position: "absolute",
            // Sit on the right edge ON TOP of the price scale area so
            // the operator sees the badge anchored to the y-axis.
            right: 4,
            top: Math.round(y - 9),
            fontSize: "10px",
            fontFamily: "ui-monospace, monospace",
            fontWeight: 800,
            letterSpacing: "0.04em",
            color: "#08080A",
            background: pillColor,
            padding: "1px 5px",
            borderRadius: "2px",
            pointerEvents: "none",
            zIndex: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.6)",
            whiteSpace: "nowrap",
          }}
        >
          {labelText} {Number(price).toFixed(2)}
        </div>
      </React.Fragment>
    );
  };

  // Iter121.10 — Append the aggression sentiment + signal to VAL/VAH when
  // active so the pill's text explicitly names the direction that the
  // color communicates. Example: `VAL · BEAR TURNING PT`.
  const dirWord = meta.aggType === "Bull" ? "BULL" : meta.aggType === "Bear" ? "BEAR" : null;
  const sigCompact = meta.sigLabel === "Turning Point" ? "TURNING PT" : "CONTINUATION";
  const boostedLabel = (base) =>
    dirWord && meta.sigLabel ? `${base} · ${dirWord} ${sigCompact}` : base;
  const valLabel = meta.valBoost ? boostedLabel("VAL") : "VAL";
  const vahLabel = meta.vahBoost ? boostedLabel("VAH") : "VAH";

  return (
    <div
      data-testid="forge-level-labels-overlay"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10 }}
      aria-hidden
    >
      {renderLevel("val", meta.val, valLabel, meta.valBoost, aggColor)}
      {renderLevel("poc", meta.poc, "POC", false, null)}
      {renderLevel("vah", meta.vah, vahLabel, meta.vahBoost, aggColor)}
    </div>
  );
}


// Iter119.9 — Crosshair OHLC tooltip (P3c). Renders a small floating
// data-window pinned just below the chart's top edge, horizontally
// tracking the crosshair. Skips render when cursor is outside the
// chart (param.time === null).
function CrosshairTooltip({ data, timeframe, containerWidth }) {
  if (!data) return null;
  // Format the time label based on the chart timeframe.
  let timeStr = "";
  if (typeof data.time === "string") {
    timeStr = data.time; // YYYY-MM-DD daily
  } else if (Number.isFinite(data.time)) {
    const d = new Date(data.time * 1000);
    if (timeframe === "daily") {
      timeStr = d.toISOString().slice(0, 10);
    } else {
      timeStr = d.toLocaleString("en-US", {
        month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
    }
  }
  const change = data.close - data.open;
  const changePct = data.open ? (change / data.open) * 100 : 0;
  const up = change >= 0;
  const cell = (label, val, color) => (
    <span className="inline-flex items-baseline gap-0.5">
      <span className="text-[8px] uppercase tracking-[0.15em] text-[#8A8A93]">{label}</span>
      <span className="font-bold" style={color ? { color } : null}>{val}</span>
    </span>
  );
  // Anchor near the crosshair X but clamp so it never clips out of the
  // container (rightward overshoot would hide the panel under the price scale).
  const TOOLTIP_W = 380;
  const x = Math.min(Math.max(8, data.x - TOOLTIP_W / 2), (containerWidth || 800) - TOOLTIP_W - 8);
  return (
    <div
      data-testid="forge-crosshair-tooltip"
      style={{
        position: "absolute",
        top: 6,
        left: x,
        width: TOOLTIP_W,
        background: "rgba(8,8,10,0.92)",
        border: "1px solid #26262B",
        borderRadius: "3px",
        padding: "4px 10px",
        pointerEvents: "none",
        zIndex: 25,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      className="flex items-center gap-3 text-[11px] font-mono text-white"
    >
      <span className="text-[#8A8A93] text-[10px]">{timeStr}</span>
      {cell("O", data.open.toFixed(2))}
      {cell("H", data.high.toFixed(2), "#00FF88")}
      {cell("L", data.low.toFixed(2),  "#FF3366")}
      {cell("C", data.close.toFixed(2))}
      <span className={`ml-auto text-[10px] font-bold ${up ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
        {up ? "+" : ""}{change.toFixed(2)} ({up ? "+" : ""}{changePct.toFixed(2)}%)
      </span>
    </div>
  );
}


// Iter119.11 — Vertical drawing toolbar pinned to the chart's left edge.
// Compact (32px wide), TradingView-style. Each button is a tool selector;
// the active tool gets a cyan border + cyan icon. "Clear all" is a
// destructive action so it lives at the bottom with a confirmation guard.
function DrawingToolbar({ tool, onToolChange, drawingCount, onClearAll, snapMode, onToggleSnap, t }) {
  const TOOL_DEFS = [
    { id: "none",      icon: MousePointer2, label: t("draw.cursor")    || "Cursor"           },
    { id: "hline",     icon: Minus,         label: t("draw.hline")     || "Horizontal line"  },
    { id: "trendline", icon: TrendingUp,    label: t("draw.trendline") || "Trendline"        },
    { id: "fib",       icon: GitMerge,      label: t("draw.fib")       || "Fib retracement"  },
    { id: "eraser",    icon: Eraser,        label: t("draw.eraser")    || "Eraser (click a drawing to delete)" },
  ];
  // Iter119.14 — Snap-to-OHLC modifier button. Lives between the
  // drawing tools and Clear-all. ON state lights up amber so the
  // operator notices snap is engaged.
  return (
    <div
      data-testid="forge-drawing-toolbar"
      className="absolute top-2 left-2 z-[15] flex flex-col gap-1 bg-[#08080A]/85 border border-[#26262B] rounded-sm p-1 backdrop-blur-sm"
    >
      {TOOL_DEFS.map(({ id, icon: Icon, label }) => {
        const active = tool === id;
        return (
          <button
            key={id}
            type="button"
            data-testid={`forge-draw-tool-${id}`}
            onClick={() => onToolChange(id)}
            title={label}
            aria-label={label}
            className={`w-7 h-7 inline-flex items-center justify-center rounded-sm border transition-colors ${
              active
                ? "border-[#22D3FF] text-[#22D3FF] bg-[#22D3FF]/10"
                : "border-transparent text-[#8A8A93] hover:text-white hover:bg-[#26262B]/60"
            }`}
          >
            <Icon size={14} />
          </button>
        );
      })}
      {/* Iter119.14 — Snap-to-OHLC modifier toggle */}
      {onToggleSnap && (
        <>
          <div className="w-full h-px bg-[#26262B] my-0.5" />
          <button
            type="button"
            data-testid="forge-draw-snap-toggle"
            onClick={onToggleSnap}
            title={snapMode ? "Snap to OHLC: ON — click to disable" : "Snap to OHLC: OFF — click to enable"}
            aria-label="Snap to OHLC"
            aria-pressed={!!snapMode}
            className={`w-7 h-7 inline-flex items-center justify-center rounded-sm border transition-colors ${
              snapMode
                ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/15"
                : "border-transparent text-[#8A8A93] hover:text-white hover:bg-[#26262B]/60"
            }`}
          >
            <Magnet size={14} />
          </button>
        </>
      )}
      {drawingCount > 0 && (
        <>
          <div className="w-full h-px bg-[#26262B] my-0.5" />
          <button
            type="button"
            data-testid="forge-draw-clear-all"
            onClick={() => {
              if (window.confirm(`Remove all ${drawingCount} drawing${drawingCount === 1 ? "" : "s"} for this symbol?`)) {
                onClearAll();
              }
            }}
            title={t("draw.clear_all") || "Clear all drawings"}
            aria-label={t("draw.clear_all") || "Clear all drawings"}
            className="w-7 h-7 inline-flex items-center justify-center rounded-sm border border-transparent text-[#8A8A93] hover:text-[#FF3366] hover:bg-[#FF3366]/10"
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  );
}


// Iter119.11 — Drawing layer SVG sitting above the chart (z=12, below
// crosshair tooltip z=25 but above LevelLabels z=10). Captures clicks
// for new drawing creation when a tool is active; in cursor mode the
// layer has `pointerEvents: none` so chart pan/zoom still works.
// In eraser mode, individual drawing elements opt-in to pointer events
// so the operator can click to delete.
//
// Re-render strategy: drawings store (time, price); we recompute (x, y)
// every 500ms (interval) + on `subscribeVisibleTimeRangeChange` + on
// window resize. Same pattern as LevelLabels.
function DrawingLayer({ chartRef, candleSeriesRef, drawings, tool, timeframe, symbol, onCommit, onDelete, snapMode, bars }) {
  const svgRef = useRef(null);
  const [tick, setTick] = useState(0);  // forces recompute of x/y
  const [pendingStart, setPendingStart] = useState(null);  // {time, price}
  const [cursor, setCursor] = useState(null);  // {x, y, price, time} during pending draw
  const [snapHint, setSnapHint] = useState(null);  // {x, y, anchor} hover preview when snap on

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const sub = () => setTick(t => t + 1);
    ts.subscribeVisibleTimeRangeChange(sub);
    const onResize = () => setTick(t => t + 1);
    window.addEventListener("resize", onResize);
    const interval = setInterval(() => setTick(t => t + 1), 500);
    return () => {
      ts.unsubscribeVisibleTimeRangeChange(sub);
      window.removeEventListener("resize", onResize);
      clearInterval(interval);
    };
  }, [chartRef]);

  // Reset pending when tool changes or symbol changes
  useEffect(() => {
    setPendingStart(null);
    setCursor(null);
    setSnapHint(null);
  }, [tool, symbol]);

  const isDaily = timeframe === "daily";
  const chart  = chartRef.current;
  const candle = candleSeriesRef.current;

  const eventToPoint = useCallback((e) => {
    if (!chart || !candle || !svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let rawTime = chart.timeScale().coordinateToTime(x);
    // Iter119.11 — Clicks in the chart's empty-bars zone (the
    // rightOffset breathing room past the last data bar) return
    // null from coordinateToTime. Clamp to the rightmost visible
    // data time so the operator can still anchor drawings near the
    // chart's leading edge. If even that fails, give up.
    if (rawTime == null) {
      const range = chart.timeScale().getVisibleRange();
      if (range && range.to != null) rawTime = range.to;
      else return null;
    }
    const price = candle.coordinateToPrice(y);
    if (price == null) return null;
    let time = normalizeChartTime(rawTime);
    if (time == null) return null;
    let snappedAnchor = null;
    let snappedPrice = price;
    // Iter119.14 — Snap-to-OHLC: replace the (time, price) pair with
    // the nearest bar's nearest OHLC value if snap mode is on. Affects
    // both the click commit + the cursor preview projection.
    if (snapMode && Array.isArray(bars) && bars.length > 0) {
      const snapped = snapToOHLC(time, price, bars);
      time = snapped.time;
      snappedPrice = snapped.price;
      snappedAnchor = snapped.anchor;
    }
    return { x, y, time, price: snappedPrice, anchor: snappedAnchor };
  }, [chart, candle, snapMode, bars]);

  // Project a stored {time, price} → {x, y}; returns null if off-chart.
  const project = useCallback((pt) => {
    if (!chart || !candle || !pt) return null;
    const chartTime = unixToChartTime(pt.time, isDaily);
    let x;
    try {
      x = chart.timeScale().timeToCoordinate(chartTime);
    } catch { x = null; }
    const y = candle.priceToCoordinate(pt.price);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  }, [chart, candle, isDaily]);

  // For horizontal lines, we ignore the X anchor and stretch the line
  // across the full plot width (excluding the price scale on the right).
  const plotW = svgRef.current?.clientWidth || 0;
  const PRICE_SCALE_W = 64;
  const drawableW = Math.max(0, plotW - PRICE_SCALE_W);

  const handleClick = useCallback((e) => {
    if (tool === "none") return;
    if (tool === "eraser") return;  // eraser clicks land on drawing elements directly
    const pt = eventToPoint(e);
    if (!pt) return;
    if (tool === "hline") {
      onCommit({
        id: `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type: "hline",
        tf: timeframe,
        points: [{ time: pt.time, price: pt.price }],
      });
      return;
    }
    // Two-click tools: trendline + fib
    if (!pendingStart) {
      setPendingStart({ time: pt.time, price: pt.price });
      setCursor({ x: pt.x, y: pt.y, time: pt.time, price: pt.price });
      return;
    }
    onCommit({
      id: `d_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: tool,
      tf: timeframe,
      points: [
        { time: pendingStart.time, price: pendingStart.price },
        { time: pt.time,           price: pt.price           },
      ],
    });
    setPendingStart(null);
    setCursor(null);
  }, [tool, pendingStart, eventToPoint, onCommit, timeframe]);

  const handleMove = useCallback((e) => {
    if (tool === "none" || tool === "eraser") return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xLocal = e.clientX - rect.left;
    const yLocal = e.clientY - rect.top;
    // Iter119.14 — Live snap-to-OHLC preview dot. While the user is
    // dragging their mouse with a drawing tool active, project the
    // cursor onto the nearest bar's nearest OHLC so they can see
    // exactly where the click will anchor.
    if (snapMode) {
      const pt = eventToPoint(e);
      if (pt) {
        const proj = project({ time: pt.time, price: pt.price });
        if (proj) setSnapHint({ x: proj.x, y: proj.y, anchor: pt.anchor });
        else setSnapHint(null);
      } else {
        setSnapHint(null);
      }
    } else if (snapHint) {
      setSnapHint(null);
    }
    if (!pendingStart) return;
    setCursor({ x: xLocal, y: yLocal, time: null, price: null });
  }, [tool, pendingStart, snapMode, eventToPoint, project, snapHint]);

  // ESC cancels the pending drawing without committing.
  useEffect(() => {
    if (!pendingStart) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setPendingStart(null); setCursor(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingStart]);

  // Pointer-events policy: when a drawing tool is active OR there are
  // drawings to hit-test (eraser mode), capture events; else pass through.
  const captureEvents = tool !== "none";

  if (!chart || !candle) return null;

  // Iter119.14 — Per-drawing styling helpers. Each drawing may carry
  // optional `color`, `lineStyle`, `lineWidth` overrides; we fall back
  // to the type default + width=1.5 + solid line.
  const STYLE_DASH = { solid: "", dashed: "6 4", dotted: "2 3" };
  const styleFor = (d) => ({
    color: d.color || DRAW_DEFAULT_COLOR[d.type] || "#22D3FF",
    width: Number.isFinite(d.lineWidth) ? d.lineWidth : 1.5,
    dash: STYLE_DASH[d.lineStyle] ?? "",
  });

  // Render each drawing into SVG elements. `tick` is a phantom dep
  // forcing recomputation on pan/zoom/resize.
  void tick;
  const renderedDrawings = drawings.map((d) => {
    // Iter119.12 — Operator-toggled visibility (set via DrawingsManager).
    if (d.hidden) return null;
    // Hlines render on ANY TF; trendlines and fibs only on their original TF.
    // Iter119.17 — `d.allTfs === true` promotes a trendline/fib to render
    // on every chart timeframe (operator opt-in from the Drawings Manager).
    if (d.type !== "hline" && !d.allTfs && d.tf !== timeframe) return null;
    const points = (d.points || []).map(project);
    const st = styleFor(d);
    if (d.type === "hline") {
      // Only the price matters — use the projected Y, ignore X.
      const yProj = candle.priceToCoordinate(d.points[0]?.price);
      if (!Number.isFinite(yProj)) return null;
      const y = yProj;
      return (
        <g key={d.id} data-testid={`forge-drawing-${d.id}`} data-type="hline">
          <line x1={0} x2={drawableW} y1={y} y2={y} stroke={st.color} strokeWidth={st.width} strokeDasharray={st.dash} />
          <rect x={drawableW + 2} y={y - 8} width={50} height={16} fill={st.color} rx={2} />
          <text x={drawableW + 6} y={y + 4} fontSize="10" fontWeight="700" fontFamily="ui-monospace,monospace" fill="#08080A">
            {Number(d.points[0]?.price).toFixed(2)}
          </text>
          {/* Iter119.11 — Wider invisible hit-area for the eraser tool.
              Renders LAST (above the visible line) so it captures clicks
              first; `pointer-events: stroke` forces hit-testing on the
              stroke even when transparent (default `visiblePainted`
              would skip transparent strokes). */}
          {tool === "eraser" && (
            <line x1={0} x2={drawableW} y1={y} y2={y} stroke="transparent" strokeWidth={14}
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onDelete(d.id); }} />
          )}
        </g>
      );
    }
    if (d.type === "trendline" && points.length === 2 && points[0] && points[1]) {
      const { x: x1, y: y1 } = points[0];
      const { x: x2, y: y2 } = points[1];
      return (
        <g key={d.id} data-testid={`forge-drawing-${d.id}`} data-type="trendline">
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={st.color} strokeWidth={st.width} strokeDasharray={st.dash} />
          <circle cx={x1} cy={y1} r={3} fill={st.color} />
          <circle cx={x2} cy={y2} r={3} fill={st.color} />
          {tool === "eraser" && (
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={16}
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onDelete(d.id); }} />
          )}
        </g>
      );
    }
    if (d.type === "fib" && points.length === 2 && points[0] && points[1]) {
      const p1 = d.points[0];  // high anchor
      const p2 = d.points[1];  // low anchor
      const hi = Math.max(p1.price, p2.price);
      const lo = Math.min(p1.price, p2.price);
      const xMin = Math.min(points[0].x, points[1].x);
      const xMax = Math.max(points[0].x, points[1].x);
      return (
        <g key={d.id} data-testid={`forge-drawing-${d.id}`} data-type="fib">
          {/* Faint band between hi and lo */}
          <rect
            x={xMin} y={candle.priceToCoordinate(hi)}
            width={xMax - xMin}
            height={candle.priceToCoordinate(lo) - candle.priceToCoordinate(hi)}
            fill={st.color} fillOpacity={0.06}
          />
          {FIB_LEVELS.map(({ v, label }) => {
            const levelPrice = hi - (hi - lo) * v;
            const yL = candle.priceToCoordinate(levelPrice);
            if (!Number.isFinite(yL)) return null;
            // Per-drawing lineStyle wins; fall back to the original 0/1
            // "anchor solid, mid dashed" treatment if none specified.
            const dashOverride = d.lineStyle
              ? st.dash
              : (v === 0 || v === 1 ? "" : "3 3");
            const widthOverride = d.lineStyle
              ? st.width
              : (v === 0 || v === 1 ? 1.5 : 1);
            return (
              <g key={v}>
                <line x1={xMin} x2={drawableW} y1={yL} y2={yL} stroke={st.color}
                      strokeWidth={widthOverride}
                      strokeDasharray={dashOverride} />
                <text x={xMin + 4} y={yL - 2} fontSize="9" fontFamily="ui-monospace,monospace"
                      fill={st.color} opacity={0.9}>
                  {label} · {levelPrice.toFixed(2)}
                </text>
                {tool === "eraser" && (
                  <line x1={xMin} x2={drawableW} y1={yL} y2={yL} stroke="transparent" strokeWidth={12}
                        style={{ pointerEvents: "stroke", cursor: "pointer" }}
                        onClick={(e) => { e.stopPropagation(); onDelete(d.id); }} />
                )}
              </g>
            );
          })}
          <circle cx={points[0].x} cy={points[0].y} r={3} fill={st.color} />
          <circle cx={points[1].x} cy={points[1].y} r={3} fill={st.color} />
        </g>
      );
    }
    return null;
  });

  // Pending preview while in two-click mode (between first and second click).
  let pendingPreview = null;
  if (pendingStart && cursor) {
    const proj = project(pendingStart);
    if (proj) {
      const color = DRAW_DEFAULT_COLOR[tool] || "#22D3FF";
      pendingPreview = (
        <g data-testid="forge-drawing-pending" pointerEvents="none">
          <line x1={proj.x} y1={proj.y} x2={cursor.x} y2={cursor.y}
                stroke={color} strokeWidth={1} strokeDasharray="4 4" opacity={0.7} />
          <circle cx={proj.x} cy={proj.y} r={3} fill={color} />
        </g>
      );
    }
  }

  // Iter119.14 — Live snap preview dot. Shown only when snap is on AND
  // a drawing tool is active. Position projected from the snapped (time,
  // price). Includes a tiny anchor label (O/H/L/C) so the operator
  // knows which OHLC value the next click will lock onto.
  let snapPreview = null;
  if (snapMode && snapHint && tool !== "none" && tool !== "eraser") {
    const anchorLabel = snapHint.anchor ? snapHint.anchor[0].toUpperCase() : "";
    snapPreview = (
      <g data-testid="forge-snap-hint" pointerEvents="none">
        <circle cx={snapHint.x} cy={snapHint.y} r={6} fill="none" stroke="#FFB800" strokeWidth={1.5} opacity={0.9} />
        <circle cx={snapHint.x} cy={snapHint.y} r={2} fill="#FFB800" />
        {anchorLabel && (
          <text x={snapHint.x + 8} y={snapHint.y - 6} fontSize="9" fontFamily="ui-monospace,monospace" fontWeight="700" fill="#FFB800">
            {anchorLabel}
          </text>
        )}
      </g>
    );
  }

  return (
    <svg
      ref={svgRef}
      data-testid="forge-drawing-layer"
      width="100%"
      height="100%"
      style={{
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        width: "100%",
        height: "100%",
        pointerEvents: captureEvents ? "auto" : "none",
        cursor: tool === "none" ? "default" : tool === "eraser" ? "not-allowed" : "crosshair",
        zIndex: 12,
      }}
      onClick={handleClick}
      onMouseMove={handleMove}
      onMouseLeave={() => setSnapHint(null)}
    >
      {renderedDrawings}
      {pendingPreview}
      {snapPreview}
    </svg>
  );
}


// Iter119.12 — Drawings list manager. Opens from a "Drawings: N" chip in
// the chart header. Each row carries:
//   • Type icon + summary (e.g. "HLine · $745.93", "Trendline · 1h · $730 → $750")
//   • Visibility toggle (eye / eye-off) — sets `hidden: true|false` on the drawing
//   • Delete button — removes the drawing from localStorage
//   • Jump-to button — only for trendlines/fibs; zooms the chart to the
//     drawing's time anchor range (meaningless for hlines, hidden there)
function DrawingsManagerPopover({ drawings, timeframe, onToggleHidden, onDelete, onUpdate, onJumpTo, onClose, onClearAll }) {
  const SUMMARY_BY_TYPE = {
    hline:     (d) => `$${Number(d.points?.[0]?.price ?? 0).toFixed(2)}`,
    trendline: (d) => {
      const ps = d.points || [];
      if (ps.length < 2) return "—";
      return `$${Number(ps[0].price).toFixed(2)} → $${Number(ps[1].price).toFixed(2)}`;
    },
    fib: (d) => {
      const ps = d.points || [];
      if (ps.length < 2) return "—";
      const hi = Math.max(ps[0].price, ps[1].price);
      const lo = Math.min(ps[0].price, ps[1].price);
      return `$${lo.toFixed(2)} — $${hi.toFixed(2)}`;
    },
  };
  const ICON_BY_TYPE = { hline: Minus, trendline: TrendingUp, fib: GitMerge };
  const COLOR_BY_TYPE = DRAW_DEFAULT_COLOR;
  // Iter119.14 — Per-drawing color/style edit. Tracks which row's
  // editor is open; clicking the Palette icon toggles it. Only one
  // editor open at a time so the popover stays compact.
  const [editingId, setEditingId] = useState(null);
  const PRESET_COLORS = ["#22D3FF", "#00FF88", "#FFB800", "#FF3366", "#A78BFA", "#FFFFFF"];
  const STYLE_OPTS = [["solid", "—"], ["dashed", "––"], ["dotted", "··"]];
  const WIDTH_OPTS = [1, 2, 3];

  // Sort: hlines first, then trendlines, then fibs.
  const sorted = [...drawings].sort((a, b) => {
    const order = { hline: 0, trendline: 1, fib: 2 };
    return (order[a.type] ?? 9) - (order[b.type] ?? 9);
  });

  return (
    <div
      data-testid="forge-drawings-manager-popover"
      className="absolute top-12 left-12 z-[30] w-[320px] max-h-[80vh] overflow-y-auto bg-[#121216] border border-[#26262B] rounded-sm shadow-2xl p-3 space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#22D3FF]">
          Drawings <span className="text-[#8A8A93] normal-case tracking-normal">· {drawings.length}</span>
        </div>
        <button onClick={onClose} className="text-[#8A8A93] hover:text-white" aria-label="Close drawings manager">
          <X size={14} />
        </button>
      </div>

      {drawings.length === 0 ? (
        <div className="text-[11px] font-mono text-[#5F5F66] text-center py-6">No drawings on this symbol yet.</div>
      ) : (
        <>
          <div className="space-y-1">
            {sorted.map((d) => {
              const Icon = ICON_BY_TYPE[d.type] || Minus;
              const color = d.color || COLOR_BY_TYPE[d.type] || "#22D3FF";
              // Iter119.17 — `allTfs` promoted drawings behave like
              // hlines: they render on every TF and the TF badge
              // collapses to "ALL".
              const isCrossTf = d.type !== "hline" && d.allTfs === true;
              const offTf = d.type !== "hline" && !isCrossTf && d.tf !== timeframe;
              const summary = (SUMMARY_BY_TYPE[d.type] || (() => "—"))(d);
              const tfBadge = d.type === "hline"
                ? "ANY"
                : isCrossTf
                ? "ALL"
                : (d.tf || "—").toUpperCase();
              const isEditing = editingId === d.id;
              return (
                <div
                  key={d.id}
                  data-testid={`forge-drawings-manager-row-${d.id}`}
                  className={`rounded-sm border border-[#26262B] bg-[#0A0A0E] ${d.hidden ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-2 px-2 py-1.5">
                    <Icon size={12} style={{ color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-white" style={{ color: offTf ? "#5F5F66" : undefined }}>
                        {d.type === "hline" ? "HLine" : d.type === "trendline" ? "Trendline" : "Fib"}
                        <span className={`ml-1.5 text-[9px] font-mono tracking-normal normal-case ${isCrossTf ? "text-[#22D3FF]" : "text-[#5F5F66]"}`}>{tfBadge}</span>
                      </div>
                      <div className="text-[10px] font-mono text-[#8A8A93] truncate">{summary}</div>
                    </div>
                    {/* Jump-to (only for trendline/fib) */}
                    {d.type !== "hline" && (
                      <button
                        type="button"
                        data-testid={`forge-drawings-jump-${d.id}`}
                        onClick={() => onJumpTo(d)}
                        title={offTf ? `Switch chart to ${(d.tf || "?").toUpperCase()} first` : "Zoom to this drawing"}
                        disabled={offTf}
                        className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#22D3FF] hover:border-[#22D3FF]/40 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Search size={11} />
                      </button>
                    )}
                    {/* Iter119.17 — Cross-TF sync toggle for trendlines/fibs.
                        Promotes a single-TF drawing to render on every TF
                        (or demotes back to its original TF). Layers icon
                        lights up cyan when promoted. */}
                    {d.type !== "hline" && onUpdate && (
                      <button
                        type="button"
                        data-testid={`forge-drawings-alltfs-${d.id}`}
                        onClick={() => onUpdate(d.id, { allTfs: !d.allTfs })}
                        title={isCrossTf
                          ? `Demote: render only on ${(d.tf || "?").toUpperCase()}`
                          : "Promote: render on every timeframe"}
                        aria-pressed={isCrossTf}
                        className={`p-1 rounded-sm border ${
                          isCrossTf
                            ? "border-[#22D3FF] text-[#22D3FF] bg-[#22D3FF]/10"
                            : "border-transparent text-[#8A8A93] hover:text-[#22D3FF] hover:border-[#22D3FF]/40"
                        }`}
                      >
                        <Layers size={11} />
                      </button>
                    )}
                    {/* Iter119.14 — Edit color/style toggle */}
                    {onUpdate && (
                      <button
                        type="button"
                        data-testid={`forge-drawings-edit-${d.id}`}
                        onClick={() => setEditingId(prev => prev === d.id ? null : d.id)}
                        title={isEditing ? "Close style editor" : "Edit color / line style"}
                        aria-pressed={isEditing}
                        className={`p-1 rounded-sm border ${
                          isEditing
                            ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                            : "border-transparent text-[#8A8A93] hover:text-[#FFB800] hover:border-[#FFB800]/40"
                        }`}
                      >
                        <Palette size={11} />
                      </button>
                    )}
                    {/* Visibility toggle */}
                    <button
                      type="button"
                      data-testid={`forge-drawings-toggle-${d.id}`}
                      onClick={() => onToggleHidden(d.id)}
                      title={d.hidden ? "Show this drawing" : "Hide this drawing"}
                      className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-white hover:border-[#5F5F66]"
                    >
                      {d.hidden ? <EyeOff size={11} /> : <Eye size={11} />}
                    </button>
                    {/* Delete */}
                    <button
                      type="button"
                      data-testid={`forge-drawings-delete-${d.id}`}
                      onClick={() => onDelete(d.id)}
                      title="Delete this drawing"
                      className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                  {/* Iter119.14 — Inline color/style editor. Renders only
                      when the user clicks the Palette icon for this row. */}
                  {isEditing && onUpdate && (
                    <div
                      data-testid={`forge-drawings-editor-${d.id}`}
                      className="border-t border-[#26262B] px-2 py-1.5 space-y-1.5 bg-[#08080A]"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] w-10">Color</span>
                        {PRESET_COLORS.map(c => (
                          <button
                            key={c}
                            type="button"
                            data-testid={`forge-drawings-color-${d.id}-${c.replace("#", "")}`}
                            onClick={() => onUpdate(d.id, { color: c })}
                            title={c}
                            aria-label={`Color ${c}`}
                            className={`w-4 h-4 rounded-sm border ${color.toLowerCase() === c.toLowerCase() ? "border-white" : "border-[#26262B] hover:border-[#5F5F66]"}`}
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] w-10">Style</span>
                        {STYLE_OPTS.map(([v, sym]) => (
                          <button
                            key={v}
                            type="button"
                            data-testid={`forge-drawings-style-${d.id}-${v}`}
                            onClick={() => onUpdate(d.id, { lineStyle: v })}
                            className={`flex-1 text-[10px] font-bold py-0.5 rounded-sm border ${
                              (d.lineStyle || "solid") === v
                                ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                                : "border-[#26262B] text-[#8A8A93] hover:text-white"
                            }`}
                          >{sym}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] w-10">Width</span>
                        {WIDTH_OPTS.map(w => (
                          <button
                            key={w}
                            type="button"
                            data-testid={`forge-drawings-width-${d.id}-${w}`}
                            onClick={() => onUpdate(d.id, { lineWidth: w })}
                            className={`flex-1 text-[10px] font-bold py-0.5 rounded-sm border ${
                              (Number.isFinite(d.lineWidth) ? d.lineWidth : 1.5) === w
                                ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                                : "border-[#26262B] text-[#8A8A93] hover:text-white"
                            }`}
                          >{w}</button>
                        ))}
                        <button
                          type="button"
                          data-testid={`forge-drawings-reset-${d.id}`}
                          onClick={() => onUpdate(d.id, { color: null, lineStyle: null, lineWidth: null })}
                          title="Reset to type default"
                          className="ml-1 text-[9px] font-bold uppercase tracking-[0.12em] py-0.5 px-1.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            data-testid="forge-drawings-manager-clear-all"
            onClick={() => {
              if (window.confirm(`Remove all ${drawings.length} drawing${drawings.length === 1 ? "" : "s"}?`)) {
                onClearAll();
                onClose();
              }
            }}
            className="w-full text-[10px] font-bold uppercase tracking-[0.12em] py-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40"
          >
            Clear all
          </button>
        </>
      )}
    </div>
  );
}


// Iter31b — Side-by-side ARX vs TradingView diff card. Renders inside
// WesPanel after a successful CSV upload. Fields with green-text mean ARX
// matched within 0.011 (mintick); amber means within ±0.20 (the no-volume
// approximation regime); red means a real divergence worth investigating.
function ParityCsvDiff({ result, t }) {
  if (!result || !result.arx) return null;
  const tv = result.tradingview_reference || {};
  const arx = result.arx || {};
  const alt = result.arx_alt_hlc3;
  const _row = (label, arxVal, tvVal, kind = "price") => {
    const ok = arxVal != null && tvVal != null;
    const diff = ok ? (arxVal - tvVal) : null;
    let cls = "text-[#8A8A93]";
    if (ok) {
      const tol = kind === "price" ? 0.011 : 5.0;
      const tolWide = kind === "price" ? 0.20 : 10.0;
      cls = Math.abs(diff) <= tol ? "text-[#00FF88]"
            : Math.abs(diff) <= tolWide ? "text-[#FFB800]" : "text-[#FF3366]";
    }
    const fmt = (v) => v == null ? "—" : (kind === "price" ? `$${v.toFixed(2)}` : `${v.toFixed(2)}%`);
    return (
      <div className="grid grid-cols-12 gap-1 text-[10px] font-mono">
        <div className="col-span-3 text-[#8A8A93]">{label}</div>
        <div className="col-span-3 text-right text-white">{fmt(arxVal)}</div>
        <div className="col-span-3 text-right text-[#8A8A93]">{fmt(tvVal)}</div>
        <div className={`col-span-3 text-right ${cls}`}>{ok ? `Δ ${(diff >= 0 ? "+" : "")}${diff.toFixed(kind === "price" ? 2 : 2)}` : "—"}</div>
      </div>
    );
  };
  const note = (arx.data_quality_notes || []).find(n => n.includes("MISSING VOLUME"));
  return (
    <div data-testid="forge-parity-csv-diff"
         className="mb-3 bg-[#0A0A0E] border border-[#3BB5FF]/30 rounded-sm p-2.5">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#3BB5FF]">
          CSV Parity Test · {result.filename}
        </div>
        <div className="text-[8px] font-mono text-[#5F5F66]">
          {tv.bar_count} bars · vol col: {tv.has_volume_column ? "yes" : "no"}
        </div>
      </div>
      {note && (
        <div className="mb-1.5 text-[9px] font-mono text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-sm px-1.5 py-1 leading-snug">
          ⚠ {note}
        </div>
      )}
      <div className="grid grid-cols-12 gap-1 text-[8px] font-bold uppercase tracking-[0.15em] text-[#5F5F66] mb-0.5">
        <div className="col-span-3"></div>
        <div className="col-span-3 text-right">ARX</div>
        <div className="col-span-3 text-right">TV</div>
        <div className="col-span-3 text-right">Δ</div>
      </div>
      {_row("VAL", arx.val, tv.val, "price")}
      {_row("POC", arx.poc, tv.poc, "price")}
      {_row("VAH", arx.vah, tv.vah, "price")}
      {_row("Δ% VAL", arx.delta_val_pct, tv.d_val, "pct")}
      {_row("Δ% VAH", arx.delta_vah_pct, tv.d_vah, "pct")}
      <div className="border-t border-[#26262B] my-1.5" />
      <div className="grid grid-cols-12 gap-1 text-[10px] font-mono">
        <div className="col-span-3 text-[#8A8A93]">WES-P</div>
        <div className="col-span-3 text-right text-[#00FF88] font-bold">{arx.wes_p?.toFixed(1)}</div>
        <div className="col-span-3 text-right text-[#8A8A93]">7.0</div>
        <div className="col-span-3 text-right text-[#00FF88]">{arx.advice_p}</div>
      </div>
      <div className="grid grid-cols-12 gap-1 text-[10px] font-mono">
        <div className="col-span-3 text-[#8A8A93]">WES-C</div>
        <div className="col-span-3 text-right text-[#00FF88] font-bold">{arx.wes_c?.toFixed(1)}</div>
        <div className="col-span-3 text-right text-[#8A8A93]">4.0</div>
        <div className="col-span-3 text-right text-[#00FF88]">{arx.advice_c}</div>
      </div>
      {alt && (
        <div className="mt-2 pt-1.5 border-t border-[#26262B] text-[9px] font-mono text-[#8A8A93]">
          <div className="text-[#5F5F66] uppercase tracking-[0.15em] text-[8px] mb-0.5">
            HLC3 alloc (volume-less approximation)
          </div>
          <div>VAL ${alt.val?.toFixed(2)} · POC ${alt.poc?.toFixed(2)} · VAH ${alt.vah?.toFixed(2)} · WES-P {alt.wes_p?.toFixed(1)} · WES-C {alt.wes_c?.toFixed(1)}</div>
        </div>
      )}
    </div>
  );
}

function WesPanel({ wes, loading, t, wesEngine, setWesEngine }) {
  // ARX vs TV side-by-side diff. State is local; result clears when the user
  // flips engine modes or uploads a new file.
  const [csvResult, setCsvResult] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvErr, setCsvErr] = useState(null);
  const csvInputRef = useRef(null);
  useEffect(() => { setCsvResult(null); setCsvErr(null); }, [wesEngine]);
  const onPickCsv = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setCsvBusy(true); setCsvErr(null); setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const resp = await axios.post(`${API}/forge/wes/parity-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(resp.data);
    } catch (err) {
      setCsvErr(err?.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setCsvBusy(false);
      if (csvInputRef.current) csvInputRef.current.value = "";
    }
  };
  return (
    <div data-testid="forge-wes-panel" className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#00FF88]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("forge.wes_structure")}</h3>
          {loading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
        </div>
        {/* Iter31 — WES engine A/B toggle. Lets the operator flip between the
            legacy in-house engine and the Pine-parity port to verify scores
            against TradingView. Persists in localStorage. */}
        {setWesEngine && (
          <div className="flex items-center gap-1">
            {wesEngine === "parity" && (
              <>
                <input ref={csvInputRef} type="file" accept=".csv,text/csv"
                       onChange={onPickCsv}
                       data-testid="forge-wes-csv-input"
                       className="hidden" />
                <button
                  data-testid="forge-wes-csv-upload"
                  onClick={() => csvInputRef.current?.click()}
                  disabled={csvBusy}
                  className="text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 border border-[#3BB5FF]/40 text-[#3BB5FF] hover:bg-[#3BB5FF]/10 rounded-sm flex items-center gap-1 disabled:opacity-50"
                  title="Upload a TradingView OHLCV CSV export to validate parity bar-for-bar"
                >
                  {csvBusy ? <Loader2 size={9} className="animate-spin" /> : null}
                  CSV
                </button>
              </>
            )}
            <div className="flex items-center gap-px bg-[#08080A] border border-[#26262B] rounded-sm overflow-hidden">
              <button
                data-testid="forge-wes-engine-parity"
                onClick={() => setWesEngine("parity")}
                className={`text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 ${
                  wesEngine === "parity" ? "bg-[#00FF88]/15 text-[#00FF88]" : "text-[#5F5F66] hover:text-[#8A8A93]"}`}
                title="FROZEN Pine-parity engine v1 — official operational source (powers Decision Engine, Scanner, ROI gate, alerts)"
              >TV</button>
              <button
                data-testid="forge-wes-engine-legacy"
                onClick={() => setWesEngine("legacy")}
                className={`text-[8px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 ${
                  wesEngine === "legacy" ? "bg-[#FFB800]/15 text-[#FFB800]" : "text-[#5F5F66] hover:text-[#8A8A93]"}`}
                title="Legacy engine — DIAGNOSTIC ONLY (not consumed by any production path)"
              >LGY</button>
            </div>
          </div>
        )}
      </div>
      {/* Parity-CSV result viewer (renders only in parity mode after upload) */}
      {wesEngine === "parity" && csvErr && (
        <InlineError
          testId="forge-wes-csv-error"
          className="mb-3"
          message={`CSV error: ${csvErr}`}
        />
      )}
      {wesEngine === "parity" && csvResult && (
        <ParityCsvDiff result={csvResult} t={t} />
      )}
      {!wes && <p className="text-xs text-[#8A8A93]">{t("common.awaiting")}</p>}
      {wes && (
        <>
          {/* Score row */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <ScoreCell label="WES-P" value={wes.wes_p} advice={wes.wes_p_advice} accent />
            <ScoreCell label="WES-C" value={wes.wes_c} advice={wes.wes_c_advice} />
          </div>
          {/* Levels */}
          <div className="space-y-1.5 mb-3">
            <KV label="VAL" value={fmtMoney(wes.val)} valueClass="text-[#00FF88]" />
            <KV label="POC" value={fmtMoney(wes.poc)} valueClass="text-[#FFB800]" />
            <KV label="VAH" value={fmtMoney(wes.vah)} valueClass="text-[#FF3366]" />
          </div>
          {/* Deltas */}
          <div className="space-y-1.5 mb-3">
            <KV label="VAL Δ" value={fmtPct(wes.delta_val_pct)} valueClass={(wes.delta_val_pct ?? 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"} />
            <KV label="VAH Δ" value={fmtPct(wes.delta_vah_pct)} valueClass={(wes.delta_vah_pct ?? 0) >= 0 ? "text-[#00FF88]" : "text-[#FF3366]"} />
            <KV label={t("forge.combo")} value={wes.delta_combo || "—"} valueClass="text-white" />
          </div>
          {/* Aggression — Bull/Bear @ VAL/VAH · Turning Point / Continuation
              (Pine-parity signal already computed by wes_engine.detect_aggression).
              Renders only when aggression is detected near an edge. */}
          {wes.aggression && (
            <div data-testid="forge-wes-aggression-row"
                 className="flex items-center justify-between text-[11px] font-mono mb-3">
              <span className="text-[#8A8A93]">{t("forge.signal")}</span>
              <AggressionChip agg={wes.aggression} t={t} />
            </div>
          )}
          <div className="border-t border-[#26262B] pt-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-1.5">{t("forge.bias")}</div>
            <p data-testid="forge-bias" className="text-[11px] leading-relaxed text-[#E4E4E7]">{wes.bias || "—"}</p>
          </div>
        </>
      )}
    </div>
  );
}

function ScoreCell({ label, value, advice, accent }) {
  const v = Number(value);
  const color = !Number.isFinite(v) ? "text-[#8A8A93]" : v >= 8 ? "text-[#00FF88]" : v >= 6 ? "text-[#00FF88]/80" : v >= 4 ? "text-[#FFB800]" : "text-[#FF3366]";
  return (
    <div className={`bg-[#08080A] border ${accent ? "border-[#00FF88]/30" : "border-[#26262B]"} rounded-sm p-2.5`}>
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{label}</div>
      <div className={`text-2xl font-black tracking-tight ${color}`} data-testid={`forge-${label.toLowerCase()}`}>{fmtNum(value, 1)}</div>
      <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] truncate">{advice || ""}</div>
    </div>
  );
}

function KV({ label, value, valueClass = "text-white" }) {
  return (
    <div className="flex items-center justify-between text-[11px] font-mono">
      <span className="text-[#8A8A93]">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

function DecisionPanelV2({ candidates, loading, onPin, pinResult, gexReport, t }) {
  if (loading && !candidates) {
    return (
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <Layers size={14} className="text-[#00FF88]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("forge.decision")}</h3>
          <Loader2 size={12} className="animate-spin text-[#8A8A93]" />
        </div>
        <p className="text-xs text-[#8A8A93]">{t("forge.evaluating")}</p>
      </div>
    );
  }
  if (!candidates || !candidates.decision) {
    return (
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-2">{t("forge.decision")}</div>
        <p className="text-xs text-[#8A8A93]">{t("common.no_data")}</p>
      </div>
    );
  }
  const dec = candidates.decision;
  const c = dec.trade_allowed ? COLORS.green : COLORS.red;
  const label = dec.strategy === "PURE_WHEEL_ITM" ? t("forge.label_pure")
    : dec.strategy === "WHEEL_LADDER_ITM" ? t("forge.label_ladder")
    : t("forge.label_no_trade");
  return (
    <div data-testid="forge-decision-panel" className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <Layers size={14} className="text-[#00FF88]" />
        <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("forge.decision")}</h3>
        {loading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
      </div>
      <div className="rounded-sm border p-3 mb-3" style={{ borderColor: c, background: `${c}10` }}>
        <div className="flex items-center gap-2 flex-wrap">
          <div data-testid="forge-decision-label" className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: c }}>{label}</div>
          {/* GEX classification chip — informational only, never overrides ROI/WES decision */}
          {gexReport?.classification && gexReport.classification !== "NO_DATA" && (
            <span
              data-testid="forge-decision-gex-chip"
              title={t("gex.classification") + ": " + t(`gex.class.${gexReport.classification}`) + " · " + t("gex.score") + " " + gexReport.score}
              className="text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm"
              style={{ color: GEX_CLASS_COLORS[gexReport.classification], border: `1px solid ${GEX_CLASS_COLORS[gexReport.classification]}55`, background: `${GEX_CLASS_COLORS[gexReport.classification]}10` }}
            >
              GEX · {t(`gex.class.${gexReport.classification}`)}
            </span>
          )}
        </div>
        <p className="text-[11px] mt-1 text-[#E4E4E7] leading-relaxed">{dec.reason}</p>
      </div>
      <div className="space-y-1.5 text-[11px] font-mono mb-3">
        <KV label={t("forge.expiration")} value={`${candidates.expiration} (${candidates.dte}d)`} />
        {dec.primary_strike != null && <KV label={t("forge.primary_k")} value={fmtMoney(dec.primary_strike)} />}
        {dec.secondary_strike != null && <KV label={t("forge.secondary_k")} value={fmtMoney(dec.secondary_strike)} />}
        {dec.premium_bid != null && <KV label={t("forge.premium_bid")} value={fmtMoney(dec.premium_bid)} valueClass="text-[#00FF88]" />}
        {dec.roi_pct != null && <KV label={t("forge.roi_bid")} value={`${dec.roi_pct.toFixed(2)}%`} valueClass={dec.trade_allowed ? "text-[#00FF88]" : "text-[#FF3366]"} />}
        <KV label={t("forge.gate")} value={`≥ ${candidates.min_income_pct}%`} />
      </div>
      {dec.do_rules?.length > 0 && (
        <div className="mb-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#00FF88] mb-1">{t("forge.do")}</div>
          <ul className="text-[10px] text-[#E4E4E7] space-y-0.5 list-disc pl-4">
            {dec.do_rules.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {dec.dont_rules?.length > 0 && (
        <div className="mb-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#FF3366] mb-1">{t("forge.dont")}</div>
          <ul className="text-[10px] text-[#E4E4E7] space-y-0.5 list-disc pl-4">
            {dec.dont_rules.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {dec.execution_plan && (
        <div className="text-[10px] font-mono text-[#8A8A93] italic leading-relaxed mb-3 pt-2 border-t border-[#26262B]">{dec.execution_plan}</div>
      )}
      {dec.trade_allowed && (
        <button
          data-testid="forge-pin-cycle"
          onClick={onPin}
          className="w-full flex items-center justify-center gap-1.5 bg-[#00FF88] text-[#08080A] font-bold px-3 py-2 rounded-sm text-xs"
        >
          <Pin size={12} /> {t("forge.pin_button")}
        </button>
      )}
      {pinResult && (
        <div data-testid="forge-pin-result" className={`mt-2 text-[10px] font-mono ${pinResult.kind === "ok" ? "text-[#00FF88]" : "text-[#FF3366]"}`}>
          {pinResult.kind === "ok" ? `✓ ${pinResult.msg}` : `✗ ${pinResult.msg}`}
        </div>
      )}
    </div>
  );
}

function ChainTable({ candidates, loading, strikeBand, onStrikeBandChange, t }) {
  if (!candidates || !candidates.chain) return null;
  const chain = candidates.chain || [];
  const pure = candidates.pure_wheel;
  const ladder = candidates.ladder;
  const dec = candidates.decision;
  // Iter119 — strike_band_meta is the backend's authoritative readout of
  // the σ trim that was actually applied. Falls back to empty {} if the
  // operator's backend predates Iter119.
  const meta = candidates.strike_band_meta || {};
  const sigmaDollars = meta.sigma_dollars;
  const rowsBefore = meta.rows_before_trim;
  const trimApplied = !!meta.trim_applied;

  // Map strike → classification badge
  const cls = {};
  if (pure && pure.status === "VALID" && dec?.strategy === "PURE_WHEEL_ITM") cls[pure.strike] = { label: "PURE WHEEL ITM", color: COLORS.green };
  else if (pure) cls[pure.strike] = pure.status === "VALID" ? { label: "PURE — Eligible", color: COLORS.green } : { label: `Rejected — ${(pure.rejected_reasons[0] || "").split(" ")[0]}`, color: COLORS.red };
  if (ladder) {
    const isPicked = dec?.strategy === "WHEEL_LADDER_ITM";
    const lc = ladder.status === "VALID" ? (isPicked ? COLORS.green : "#00C36C") : COLORS.amber;
    cls[ladder.leg1.strike] = { label: isPicked ? "LADDER LEG 1" : "Ladder Leg 1", color: lc };
    cls[ladder.leg2.strike] = { label: isPicked ? "LADDER LEG 2" : "Ladder Leg 2", color: lc };
  }

  // Segmented strike-band toggle (Iter119) — kept compact + monospace
  // to match the rest of the cockpit's header chips. Reflects the
  // currently-applied σ trim and lets the operator widen / collapse
  // the view without a page reload.
  const BAND_OPTIONS = [
    { id: "2sigma", label: "±2σ", title: "Trim chain to ±2σ — ~95% touch band (default, perf-optimal)" },
    { id: "3sigma", label: "±3σ", title: "Trim chain to ±3σ — ~99% touch band" },
    { id: "full",   label: "FULL", title: "Show the full 0.85–1.05 × spot ITM band (no σ trim)" },
  ];
  const handleBand = (id) => {
    if (typeof onStrikeBandChange === "function") onStrikeBandChange(id);
  };

  return (
    <div data-testid="forge-chain-table" className="bg-[#121216] border border-[#26262B] rounded-sm">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#26262B] gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-[#00FF88]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("forge.chain_title")}</h3>
          {loading && <Loader2 size={12} className="animate-spin text-[#8A8A93]" />}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[10px] font-mono text-[#8A8A93]">
            {t("forge.exp")} <span className="text-white">{candidates.expiration}</span> · {t("forge.dte")} {candidates.dte} · {t("forge.roi_gate")} <span className="text-[#00FF88]">{candidates.min_income_pct}%</span> · <span data-testid="forge-chain-row-count">{chain.length}</span>{rowsBefore && trimApplied ? <span className="text-[#5F5F66]">/{rowsBefore}</span> : null} {t("forge.strikes")}
            {sigmaDollars && trimApplied && (
              <span
                data-testid="forge-chain-sigma-meta"
                className="ml-2 text-[#22D3FF]"
                title={`ATM IV ${meta.atm_iv ? (meta.atm_iv * 100).toFixed(1) + "%" : "—"} · Expected move ${meta.expected_move_pct ? meta.expected_move_pct.toFixed(2) + "%" : "—"} · Band $${meta.lower_strike}–$${meta.upper_strike}`}
              >
                σ ±${Number(sigmaDollars).toFixed(2)}
              </span>
            )}
          </div>
          {/* Iter119 — segmented σ-band toggle */}
          <div
            data-testid="forge-chain-band-toggle"
            role="group"
            aria-label="Chain strike band"
            className="inline-flex rounded-sm border border-[#26262B] overflow-hidden"
          >
            {BAND_OPTIONS.map((opt, i) => {
              const active = strikeBand === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`forge-chain-band-${opt.id}`}
                  onClick={() => handleBand(opt.id)}
                  title={opt.title}
                  aria-pressed={active}
                  className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] transition-colors ${
                    active
                      ? "bg-[#00FF88]/15 text-[#00FF88]"
                      : "bg-transparent text-[#8A8A93] hover:text-white hover:bg-[#26262B]/30"
                  } ${i > 0 ? "border-l border-[#26262B]" : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] border-b border-[#26262B]">
              <th className="text-left px-3 py-2">{t("forge.col_strike")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_bid")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_ask")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_mid")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_delta")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_iv")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_oi")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_vol")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_roi_bid")}</th>
              <th className="text-right px-3 py-2">{t("forge.col_roi_mid")}</th>
              <th className="text-left px-3 py-2">{t("forge.col_class")}</th>
            </tr>
          </thead>
          <tbody>
            {chain.map((row) => {
              const roiBid = row.bid && row.strike > 0 ? (row.bid / row.strike) * 100 : 0;
              const roiMid = row.mid && row.strike > 0 ? (row.mid / row.strike) * 100 : 0;
              const tag = cls[row.strike];
              const inGate = roiBid >= candidates.min_income_pct;
              return (
                <tr key={row.strike} data-testid={`forge-chain-row-${row.strike}`} className="border-b border-[#1A1A20] hover:bg-[#26262B]/30">
                  <td className="px-3 py-1.5 text-white font-bold">{row.strike}</td>
                  <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{row.bid?.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{row.ask?.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{row.mid?.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{row.delta != null ? Math.abs(row.delta).toFixed(2) : "—"}</td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{row.iv != null ? (row.iv * 100).toFixed(1) + "%" : "—"}</td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{row.open_interest?.toLocaleString() || 0}</td>
                  <td className="px-3 py-1.5 text-right text-[#8A8A93]">{row.volume?.toLocaleString() || 0}</td>
                  <td className={`px-3 py-1.5 text-right font-bold ${inGate ? "text-[#00FF88]" : "text-[#FF3366]"}`}>{roiBid.toFixed(2)}%</td>
                  <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{roiMid.toFixed(2)}%</td>
                  <td className="px-3 py-1.5">
                    {tag ? <span className="text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm" style={{ color: tag.color, background: `${tag.color}15`, border: `1px solid ${tag.color}40` }}>{tag.label}</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PinCycleModal({ candidates, onSubmit, onClose, t }) {
  const dec = candidates?.decision;
  const isLadder = dec?.strategy === "WHEEL_LADDER_ITM";
  const primary = isLadder ? candidates.ladder?.leg2 : candidates.pure_wheel;
  const secondary = isLadder ? candidates.ladder?.leg1 : null;

  const initialComment = isLadder
    ? `Wheel Ladder ITM — Leg 1 K${candidates.ladder.leg1.strike} (P=$${candidates.ladder.leg1.premium_bid}); Leg 2 K${candidates.ladder.leg2.strike} (P=$${candidates.ladder.leg2.premium_bid}). Combined ROI ${candidates.ladder.combined_roi_bid_pct}% vs Pure ${candidates.pure_wheel?.roi_bid_pct ?? "—"}%. Bias: ${(candidates.bias || "").slice(0, 240)}`
    : `Pure Wheel ITM — K${primary?.strike} (P=$${primary?.premium_bid}). ROI ${primary?.roi_bid_pct}%. Bias: ${(candidates.bias || "").slice(0, 240)}`;

  const [kp, setKp] = useState(primary?.strike ?? "");
  const [pp, setPp] = useState(primary?.premium_bid ?? "");
  const [dte, setDte] = useState(primary?.dte ?? "");
  const [spotEntry, setSpotEntry] = useState(candidates.spot ?? "");
  const [comment, setComment] = useState(initialComment);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  if (!primary) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!kp || !pp || !dte) { setErr(t("pin.required")); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        symbol: candidates.symbol,
        kp: Number(kp),
        pp: Number(pp),
        dte_put: Number(dte),
        spot_entry: Number(spotEntry) || candidates.spot,
        wes_p: candidates.wes_p,
        wes_c: candidates.wes_c,
        regime: dec.strategy,
        comment: (comment || "").slice(0, 2000),
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || t("pin.error_default"));
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[110] p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={submit} data-testid="forge-pin-modal" className="bg-[#121216] border border-[#26262B] rounded-sm p-6 w-[560px] max-w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00FF88]">{dec.strategy.replace(/_/g, " ")}</div>
            <h3 className="text-lg font-black tracking-tight text-white mt-0.5">{t("pin.title")}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pin.symbol")} value={candidates.symbol} readOnly />
            <Field label={t("pin.spot_entry")} type="number" step="0.01" value={spotEntry} onChange={setSpotEntry} testid="pin-spot" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={isLadder ? t("pin.strike_leg2") : t("pin.strike")} type="number" step="0.5" value={kp} onChange={setKp} testid="pin-kp" />
            <Field label={t("pin.premium_fill")} type="number" step="0.01" value={pp} onChange={setPp} testid="pin-pp" hint={`${t("pin.suggested")} $${primary.premium_bid} · ${t("pin.mid")} $${primary.premium_mid}`} />
          </div>

          {isLadder && secondary && (
            <div className="rounded-sm border border-[#26262B] bg-[#08080A] p-3 text-[11px] font-mono text-[#8A8A93]">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB800] mb-1.5">{t("pin.ladder_context")}</div>
              <div>Leg 1 (ATM/shallow ITM): K${secondary.strike} · P=${secondary.premium_bid} · Δ {secondary.delta?.toFixed(2)}</div>
              <div>Combined ROI (bid): {candidates.ladder.combined_roi_bid_pct}% &nbsp;·&nbsp; Combined Premium: ${candidates.ladder.combined_premium_bid}</div>
              <div className="mt-1 text-[10px] italic">Pin records the deeper-ITM leg as the primary cycle. Open Leg 1 manually as a separate cycle if needed.</div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label={t("pin.dte")} type="number" step="1" value={dte} onChange={setDte} testid="pin-dte" />
            <Field label={t("pin.wes_p")} value={candidates.wes_p?.toFixed?.(1) ?? "—"} readOnly />
            <Field label={t("pin.wes_c")} value={candidates.wes_c?.toFixed?.(1) ?? "—"} readOnly />
          </div>

          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{t("pin.comment_label")}</label>
            <textarea
              data-testid="pin-comment"
              rows={4}
              value={comment}
              onChange={e => setComment(e.target.value)}
              className="w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-[11px] font-mono leading-relaxed"
            />
          </div>

          <div className="rounded-sm border border-[#26262B] bg-[#08080A] p-3 text-[11px] font-mono text-[#8A8A93]">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00FF88] mb-1.5">{t("pin.computed")}</div>
            <div>{t("pin.capital")} ${(Number(kp) * 100 || 0).toLocaleString()}</div>
            <div>{t("pin.breakeven")} ${(Number(kp) - Number(pp) || 0).toFixed(2)}</div>
            <div>{t("pin.roi_fill")} {Number(pp) > 0 && Number(kp) > 0 ? ((Number(pp) / Number(kp)) * 100).toFixed(2) : "0.00"}%</div>
          </div>

          {err && <div data-testid="pin-error" className="text-xs font-mono text-[#FF3366]">✗ {err}</div>}
        </div>

        <div className="flex gap-2 mt-6">
          <button type="button" onClick={onClose} className="flex-1 border border-[#3F3F47] text-white py-2.5 rounded-sm text-sm">{t("common.cancel")}</button>
          <button data-testid="pin-confirm" type="submit" disabled={submitting} className="flex-1 bg-[#00FF88] text-[#08080A] font-bold py-2.5 rounded-sm text-sm disabled:opacity-40 flex items-center justify-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? t("pin.pinning") : t("pin.confirm")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, readOnly = false, hint, testid }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value ?? ""}
        readOnly={readOnly}
        onChange={onChange ? e => onChange(e.target.value) : undefined}
        className={`w-full bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-sm font-mono ${readOnly ? "opacity-60" : ""}`}
      />
      {hint && <div className="text-[9px] font-mono text-[#8A8A93] mt-1">{hint}</div>}
    </div>
  );
}

function ChartSettingsPopover({ prefs, onChange, onClose, timeframe, t, levelStyles, onLevelChange, onSaveLevelsDefault, onResetLevels, symbol, presets, onSavePreset, onLoadPreset, onDeletePreset }) {
  // Iter119.9 — Compact per-line controls used by the new "Levels" section.
  // Three rows (VAL/POC/VAH), each row = visibility toggle + color picker +
  // style selector + width selector. Renders inline so the popover stays
  // compact (no nested popovers).
  // Iter119.10 — Adds "Save as default" + "Reset" so the operator can
  // promote a styling to the global default (inherited by new symbols)
  // or wipe the current symbol's overrides.
  const [savedFlash, setSavedFlash] = useState(false);
  // Iter119.15 — Chart layout presets (per-symbol).
  const [newPresetName, setNewPresetName] = useState("");
  const [presetSavedFlash, setPresetSavedFlash] = useState(null);  // name or null
  const presetNames = useMemo(() => Object.keys(presets || {}).sort((a, b) => a.localeCompare(b)), [presets]);
  const handleSavePreset = () => {
    const name = newPresetName.trim();
    if (!name || !onSavePreset) return;
    const ok = onSavePreset(name);
    if (ok) {
      setPresetSavedFlash(name);
      setNewPresetName("");
      setTimeout(() => setPresetSavedFlash(null), 1500);
    }
  };
  const LEVEL_DEFS = [
    { key: "val", label: "VAL", swatch: "#00FF88" },
    { key: "poc", label: "POC", swatch: "#FFB800" },
    { key: "vah", label: "VAH", swatch: "#FF3366" },
  ];
  const PRESET_COLORS = ["#00FF88", "#FFB800", "#FF3366", "#22D3FF", "#A78BFA", "#FFFFFF"];
  const STYLE_OPTS = [["solid", "—"], ["dashed", "––"], ["dotted", "··"]];
  const WIDTH_OPTS = [1, 2, 3];

  return (
    <div data-testid="forge-chart-settings-popover" className="absolute top-12 right-2 z-[30] w-[300px] max-h-[80vh] overflow-y-auto bg-[#121216] border border-[#26262B] rounded-sm shadow-2xl p-4 space-y-4" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#00FF88]">{t("chart.settings")}</div>
        <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={14} /></button>
      </div>

      {/* Iter119.15 — Chart Layout Presets. Per-symbol named bundles
          that capture prefs + levels + timeframe + strikeBand. Renders
          only when the presets infra is wired in (`presets` !== undefined). */}
      {presets !== undefined && onSavePreset && (
        <div data-testid="forge-presets-section">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
              <Sparkles size={10} className="inline-block mr-1 -mt-0.5 text-[#A78BFA]" />
              Layout Presets <span className="text-[#5F5F66] normal-case tracking-normal">· {symbol}</span>
            </div>
          </div>
          {presetNames.length > 0 ? (
            <div className="space-y-1 mb-2">
              {presetNames.map(name => {
                const meta = presets[name] || {};
                return (
                  <div
                    key={name}
                    data-testid={`forge-preset-row-${name}`}
                    className="flex items-center gap-1 rounded-sm border border-[#26262B] bg-[#0A0A0E] px-2 py-1"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-bold text-white truncate">{name}</div>
                      <div className="text-[9px] font-mono text-[#5F5F66] truncate">
                        {meta.timeframe ? meta.timeframe.toUpperCase() : "—"}
                        {meta.prefs?.candleTheme ? ` · ${meta.prefs.candleTheme}` : ""}
                        {meta.prefs?.vpBins ? ` · vp${meta.prefs.vpBins}` : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`forge-preset-load-${name}`}
                      onClick={() => onLoadPreset && onLoadPreset(name)}
                      title="Load this preset"
                      className="text-[9px] font-bold uppercase tracking-[0.12em] py-0.5 px-1.5 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] hover:bg-[#22D3FF]/15"
                    >
                      Load
                    </button>
                    <button
                      type="button"
                      data-testid={`forge-preset-delete-${name}`}
                      onClick={() => {
                        if (window.confirm(`Delete preset "${name}"?`)) {
                          onDeletePreset && onDeletePreset(name);
                        }
                      }}
                      title="Delete this preset"
                      className="p-1 rounded-sm border border-transparent text-[#8A8A93] hover:text-[#FF3366] hover:border-[#FF3366]/40"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[10px] font-mono text-[#5F5F66] italic mb-2 px-2">No saved presets yet.</div>
          )}
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={newPresetName}
              onChange={e => setNewPresetName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSavePreset(); } }}
              placeholder="Preset name"
              maxLength={32}
              data-testid="forge-preset-name-input"
              className="flex-1 min-w-0 bg-[#08080A] border border-[#26262B] focus:border-[#A78BFA] focus:outline-none text-white rounded-sm px-2 py-1 text-[10px] font-mono"
            />
            <button
              type="button"
              data-testid="forge-preset-save"
              onClick={handleSavePreset}
              disabled={!newPresetName.trim()}
              title="Save current chart layout as a preset"
              className={`text-[10px] font-bold uppercase tracking-[0.12em] py-1 px-2 rounded-sm border transition-colors flex-shrink-0 ${
                presetSavedFlash
                  ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/15"
                  : !newPresetName.trim()
                  ? "border-[#26262B] text-[#5F5F66] opacity-50"
                  : "border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/15"
              }`}
            >
              <Save size={11} className="inline-block -mt-0.5 mr-0.5" />
              {presetSavedFlash ? "Saved ✓" : "Save"}
            </button>
          </div>
          <div className="text-[9px] text-[#5F5F66] italic mt-1">Saves prefs + levels + TF + strike band per symbol.</div>
        </div>
      )}

      {/* Iter119.9 — Per-symbol Levels section (P1b). Each line carries
          visibility / color / style / width — saved per-symbol so SPY
          can use cyan dashed while QQQ stays green solid.
          Iter119.10 — Adds "Save as default" + "Reset" footer actions. */}
      {levelStyles && onLevelChange && (
        <div data-testid="forge-levels-section">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
              {t("chart.levels_section") || "Levels"} <span className="text-[#5F5F66] normal-case tracking-normal">· {symbol}</span>
            </div>
          </div>
          <div className="space-y-2">
            {LEVEL_DEFS.map(({ key, label }) => {
              const ls = levelStyles[key] || {};
              return (
                <div key={key} data-testid={`forge-level-row-${key}`} className="rounded-sm border border-[#26262B] p-2 space-y-1.5 bg-[#0A0A0E]">
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                      <input
                        type="checkbox"
                        data-testid={`forge-level-visible-${key}`}
                        checked={!!ls.visible}
                        onChange={e => onLevelChange(key, { visible: e.target.checked })}
                      />
                      <span style={{ color: ls.color || "#fff" }}>● {label}</span>
                    </label>
                    <div className="flex items-center gap-1">
                      {PRESET_COLORS.map(c => (
                        <button
                          key={c}
                          type="button"
                          data-testid={`forge-level-color-${key}-${c.replace("#", "")}`}
                          onClick={() => onLevelChange(key, { color: c })}
                          title={c}
                          aria-label={`Color ${c}`}
                          className={`w-3.5 h-3.5 rounded-sm border ${ls.color === c ? "border-white" : "border-[#26262B] hover:border-[#5F5F66]"}`}
                          style={{ background: c }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] w-10">Style</span>
                    {STYLE_OPTS.map(([v, sym]) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`forge-level-style-${key}-${v}`}
                        onClick={() => onLevelChange(key, { style: v })}
                        className={`flex-1 text-[10px] font-bold py-0.5 rounded-sm border ${ls.style === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
                      >{sym}</button>
                    ))}
                    <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66] w-8 ml-1">Width</span>
                    {WIDTH_OPTS.map(w => (
                      <button
                        key={w}
                        type="button"
                        data-testid={`forge-level-width-${key}-${w}`}
                        onClick={() => onLevelChange(key, { width: w })}
                        className={`w-7 text-[10px] font-bold py-0.5 rounded-sm border ${ls.width === w ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
                      >{w}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Iter119.10 — Footer actions: promote-to-global / reset. */}
          <div className="flex items-center gap-1.5 mt-2">
            <button
              type="button"
              data-testid="forge-levels-save-default"
              onClick={() => {
                onSaveLevelsDefault && onSaveLevelsDefault();
                setSavedFlash(true);
                setTimeout(() => setSavedFlash(false), 1500);
              }}
              title="Make this the default for new symbols"
              className={`flex-1 text-[10px] font-bold uppercase tracking-[0.12em] py-1 rounded-sm border transition-colors ${
                savedFlash
                  ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/15"
                  : "border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#5F5F66]"
              }`}
            >
              {savedFlash ? "Saved ✓" : "Save as default"}
            </button>
            <button
              type="button"
              data-testid="forge-levels-reset-symbol"
              onClick={() => onResetLevels && onResetLevels()}
              title={`Reset ${symbol} to default`}
              className="flex-1 text-[10px] font-bold uppercase tracking-[0.12em] py-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#5F5F66]"
            >
              Reset {symbol}
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">{t("chart.vp_bins")}</div>
        <div className="flex gap-1.5">
          {[40, 60, 80, 120, 160].map(n => (
            <button
              key={n}
              data-testid={`forge-vp-bins-${n}`}
              onClick={() => onChange({ vpBins: n })}
              className={`flex-1 text-[10px] font-bold py-1.5 rounded-sm border ${prefs.vpBins === n ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{n}</button>
          ))}
        </div>
        <div className="text-[9px] text-[#8A8A93] mt-1.5 italic">{t("chart.vp_warning")}</div>
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">{t("chart.va_highlight")}</div>
        <div className="flex gap-1.5">
          {[["subtle", t("chart.va_subtle")], ["default", t("chart.va_default")], ["bold", t("chart.va_bold")]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`forge-va-${v}`}
              onClick={() => onChange({ vaOpacity: v })}
              className={`flex-1 text-[10px] font-bold py-1.5 rounded-sm border ${prefs.vaOpacity === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{l}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">{t("chart.candle_theme")}</div>
        <div className="flex gap-1.5">
          {[["green-red", t("chart.theme_gr")], ["blue-orange", t("chart.theme_bo")], ["mono", t("chart.theme_mono")]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`forge-theme-${v}`}
              onClick={() => onChange({ candleTheme: v })}
              className={`flex-1 text-[10px] font-bold py-1.5 rounded-sm border ${prefs.candleTheme === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{l}</button>
          ))}
        </div>
      </div>

      <div>
        <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
          <input data-testid="forge-grid-toggle" type="checkbox" checked={!!prefs.grid} onChange={e => onChange({ grid: e.target.checked })} />
          {t("chart.show_grid")}
        </label>
      </div>

      {timeframe !== "daily" && (
        <div>
          <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
            <input
              data-testid="forge-vwap-toggle"
              type="checkbox"
              checked={prefs.showVwap !== false}
              onChange={e => onChange({ showVwap: e.target.checked })}
            />
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-[#3BB5FF] inline-block" /> {t("chart.show_vwap")}
            </span>
          </label>
          <label className="flex items-center gap-2 mt-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
            <input
              data-testid="forge-vwap-bands-toggle"
              type="checkbox"
              checked={prefs.showVwapBands === true}
              onChange={e => onChange({ showVwapBands: e.target.checked })}
            />
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 border-t border-dashed border-[#3BB5FF]/60 inline-block" /> {t("chart.show_vwap_bands")}
            </span>
          </label>
        </div>
      )}

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">{t("chart.visible_range")}</div>
        <div className="flex flex-wrap gap-1.5">
          {timeframe === "1h"
            ? [["last5d", "5d"], ["last10d", "10d"], ["all", "All"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`forge-range-${v}`}
                  onClick={() => onChange({ tfRange1h: v })}
                  className={`text-[10px] font-bold py-1.5 px-3 rounded-sm border ${prefs.tfRange1h === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
                >{l}</button>
              ))
            : [["last30d", "30d"], ["last60d", "60d"], ["last90d", "90d"], ["all", "All"]].map(([v, l]) => (
                <button
                  key={v}
                  data-testid={`forge-range-${v}`}
                  onClick={() => onChange({ tfRangeDaily: v })}
                  className={`text-[10px] font-bold py-1.5 px-3 rounded-sm border ${prefs.tfRangeDaily === v ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
                >{l}</button>
              ))
          }
        </div>
      </div>

      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-1.5">
          {t("chart.right_offset")} <span className="text-[#00FF88] normal-case">({prefs.rightOffset ?? 30})</span>
        </div>
        <input
          data-testid="forge-right-offset"
          type="range"
          min={0}
          max={120}
          step={1}
          value={Number.isFinite(prefs.rightOffset) ? prefs.rightOffset : 30}
          onChange={(e) => onChange({ rightOffset: Number(e.target.value) })}
          className="w-full accent-[#00FF88]"
        />
        <div className="flex justify-between text-[9px] text-[#8A8A93] mt-0.5">
          <span>0</span><span>30</span><span>60</span><span>120</span>
        </div>
      </div>

      <div className="text-[9px] text-[#8A8A93] italic pt-2 border-t border-[#26262B]">
        {t("chart.saved_local")}
      </div>
    </div>
  );
}


// Iter119.13 — Chart-visibility quick-toggle popover. Lives next to the
// Settings button; gives the operator one-click control over each overlay
// layer (VWAP, VWAP Bands, VP, Levels, Drawings, Watermark) PLUS two
// global presets: "Candles only" (hide everything) and "Show all"
// (restore defaults). Designed for the "screenshot ready" moment when
// the operator wants a clean read without losing their settings.
function ChartVisibilityPopover({ prefs, onChange, onClose, drawingCount }) {
  const TOGGLES = [
    { key: "showVwap",      label: "VWAP",             hint: "Session VWAP line"                  },
    { key: "showVwapBands", label: "VWAP Bands",       hint: "±1σ / ±2σ bands around VWAP"        },
    { key: "showVp",        label: "Volume Profile",   hint: "Right-side SVG VP overlay"          },
    { key: "showLevels",    label: "VAL / POC / VAH",  hint: "Auction levels overlay"             },
    { key: "showDrawings",  label: `Drawings${drawingCount ? ` (${drawingCount})` : ""}`, hint: "Your hlines / trendlines / fibs" },
    { key: "showWatermark", label: "Watermark",        hint: "Faded symbol behind the chart"      },
  ];
  // Default to "on" if the pref hasn't been set (back-compat).
  const isOn = (k) => prefs[k] !== false;
  const hideAll = () => onChange({
    showVwap: false, showVwapBands: false, showVp: false,
    showLevels: false, showDrawings: false, showWatermark: false,
  });
  const showAll = () => onChange({
    showVwap: true, showVwapBands: false, showVp: true,
    showLevels: true, showDrawings: true, showWatermark: true,
  });

  return (
    <div
      data-testid="forge-chart-visibility-popover"
      className="absolute top-12 right-2 z-[30] w-[280px] bg-[#121216] border border-[#26262B] rounded-sm shadow-2xl p-3 space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#22D3FF]">Chart Visibility</div>
        <button onClick={onClose} className="text-[#8A8A93] hover:text-white" aria-label="Close"><X size={14} /></button>
      </div>
      <div className="space-y-1">
        {TOGGLES.map(({ key, label, hint }) => (
          <label
            key={key}
            data-testid={`forge-vis-row-${key}`}
            className="flex items-center gap-2 rounded-sm border border-[#26262B] bg-[#0A0A0E] px-2 py-1.5 hover:border-[#5F5F66] cursor-pointer"
          >
            <input
              type="checkbox"
              data-testid={`forge-vis-toggle-${key}`}
              checked={isOn(key)}
              onChange={(e) => onChange({ [key]: e.target.checked })}
              className="accent-[#22D3FF]"
            />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold text-white">{label}</div>
              <div className="text-[9px] font-mono text-[#5F5F66] truncate">{hint}</div>
            </div>
            {isOn(key) ? <Eye size={11} className="text-[#22D3FF]" /> : <EyeOff size={11} className="text-[#5F5F66]" />}
          </label>
        ))}
      </div>
      <div className="flex items-center gap-1.5 pt-1">
        <button
          type="button"
          data-testid="forge-vis-candles-only"
          onClick={hideAll}
          title="Hide every overlay (candles only)"
          className="flex-1 text-[10px] font-bold uppercase tracking-[0.12em] py-1 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white hover:border-[#5F5F66]"
        >
          Candles only
        </button>
        <button
          type="button"
          data-testid="forge-vis-show-all"
          onClick={showAll}
          title="Restore all overlays to defaults"
          className="flex-1 text-[10px] font-bold uppercase tracking-[0.12em] py-1 rounded-sm border border-[#22D3FF]/40 text-[#22D3FF] hover:bg-[#22D3FF]/15"
        >
          Show all
        </button>
      </div>
    </div>
  );
}


function SnapshotsDrawer({ snapshots, onClose, onReplay, onDelete, onRefresh, t }) {
  // Decision Score Card aggregate stats — computed locally from the snapshots list
  const stats = useMemo(() => {
    let wins = 0, losses = 0, breakeven = 0, pending = 0, expired = 0, autos = 0;
    let tradeAllowed = 0;
    snapshots.forEach(s => {
      const dec = s.decision_strategy;
      if (dec === "PURE_WHEEL_ITM" || dec === "WHEEL_LADDER_ITM") tradeAllowed += 1;
      const o = (s.outcome || "PENDING").toUpperCase();
      if (o === "WIN") wins += 1;
      else if (o === "LOSS") losses += 1;
      else if (o === "BREAKEVEN") breakeven += 1;
      else if (o === "EXPIRED") expired += 1;
      else pending += 1;
      if (s.outcome_auto) autos += 1;
    });
    const graded = wins + losses + breakeven;
    const winRate = graded > 0 ? Math.round((wins / graded) * 100) : null;
    return { wins, losses, breakeven, pending, expired, tradeAllowed, autos, total: snapshots.length, winRate };
  }, [snapshots]);

  return (
    <div data-testid="forge-snapshots-drawer" className="bg-[#121216] border border-[#26262B] rounded-sm">
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#26262B]">
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-[#00FF88]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93]">{t("snap.title")}</h3>
          <span className="text-[10px] font-mono text-[#8A8A93]">({snapshots.length})</span>
          <span className="ml-3 text-[9px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20">
            <Trophy size={9} className="inline-block mr-1 -mt-0.5" /> {t("snap.score_card")}
          </span>
        </div>
        <button onClick={onClose} className="text-[#8A8A93] hover:text-white"><X size={14} /></button>
      </div>

      {/* Score Card summary strip */}
      {snapshots.length > 0 && (
        <div data-testid="forge-score-summary" className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 border-b border-[#26262B] bg-[#08080A]">
          <ScoreSummary label={t("snap.summary_total")} value={stats.total} tone="white" />
          <ScoreSummary label={t("snap.summary_wins")} value={stats.wins} tone="green" />
          <ScoreSummary label={t("snap.summary_losses")} value={stats.losses} tone="red" />
          <ScoreSummary label={t("snap.summary_be")} value={stats.breakeven} tone="amber" />
          <ScoreSummary label={t("snap.summary_pending")} value={stats.pending + stats.expired} tone="dim" />
          <ScoreSummary label={t("snap.summary_winrate")} value={stats.winRate != null ? `${stats.winRate}%` : "—"} tone={stats.winRate == null ? "dim" : stats.winRate >= 60 ? "green" : stats.winRate >= 40 ? "amber" : "red"} />
        </div>
      )}

      {snapshots.length === 0 ? (
        <p className="text-xs text-[#8A8A93] p-4">
          {t("snap.empty")} <strong className="text-white">{t("snap.empty_button_word")}</strong> {t("snap.empty_tail")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] border-b border-[#26262B]">
                <th className="text-left px-3 py-2">{t("snap.col_when")}</th>
                <th className="text-left px-3 py-2">{t("snap.col_symbol")}</th>
                <th className="text-left px-3 py-2">{t("snap.col_tf")}</th>
                <th className="text-right px-3 py-2">{t("snap.col_spot")}</th>
                <th className="text-right px-3 py-2">{t("snap.col_wes")}</th>
                <th className="text-left px-3 py-2">{t("snap.col_decision")}</th>
                <th className="text-right px-3 py-2">{t("snap.col_roi")}</th>
                <th className="text-left px-3 py-2">{t("snap.col_outcome")}</th>
                <th className="text-left px-3 py-2">{t("snap.col_note")}</th>
                <th className="text-right px-3 py-2">{t("snap.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map(s => (
                <SnapshotRow
                  key={s.id}
                  s={s}
                  t={t}
                  onReplay={onReplay}
                  onDelete={onDelete}
                  onRefresh={onRefresh}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScoreSummary({ label, value, tone = "white" }) {
  const toneCls = {
    white: "text-white border-[#26262B]",
    green: "text-[#00FF88] border-[#00FF88]/30 bg-[#00FF88]/5",
    red: "text-[#FF3366] border-[#FF3366]/30 bg-[#FF3366]/5",
    amber: "text-[#FFB800] border-[#FFB800]/30 bg-[#FFB800]/5",
    dim: "text-[#8A8A93] border-[#26262B]",
  }[tone];
  return (
    <div className={`rounded-sm border px-2 py-1.5 ${toneCls}`}>
      <div className="text-[9px] font-bold uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="text-base font-black tracking-tight">{value}</div>
    </div>
  );
}

function SnapshotRow({ s, t, onReplay, onDelete, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [outcome, setOutcome] = useState((s.outcome || "PENDING").toUpperCase());
  const [notes, setNotes] = useState(s.outcome_notes || "");
  const [busy, setBusy] = useState(false);
  const [autoBusy, setAutoBusy] = useState(false);

  useEffect(() => {
    setOutcome((s.outcome || "PENDING").toUpperCase());
    setNotes(s.outcome_notes || "");
  }, [s.outcome, s.outcome_notes]);

  const dec = s.decision_strategy;
  const decColor = dec === "PURE_WHEEL_ITM" || dec === "WHEEL_LADDER_ITM"
    ? "text-[#00FF88]"
    : dec === "NO_TRADE"
    ? "text-[#FF3366]"
    : "text-[#8A8A93]";

  const outcomeBadge = (() => {
    const o = (s.outcome || "PENDING").toUpperCase();
    if (o === "WIN") return { color: "text-[#00FF88]", bg: "bg-[#00FF88]/10 border-[#00FF88]/30", label: t("snap.outcome.win") };
    if (o === "LOSS") return { color: "text-[#FF3366]", bg: "bg-[#FF3366]/10 border-[#FF3366]/30", label: t("snap.outcome.loss") };
    if (o === "BREAKEVEN") return { color: "text-[#FFB800]", bg: "bg-[#FFB800]/10 border-[#FFB800]/30", label: t("snap.outcome.breakeven") };
    if (o === "EXPIRED") return { color: "text-[#8A8A93]", bg: "bg-[#26262B]/40 border-[#26262B]", label: t("snap.outcome.expired") };
    return { color: "text-[#8A8A93]", bg: "bg-[#26262B]/40 border-[#26262B]", label: t("snap.outcome.pending") };
  })();

  const tradeAllowed = dec === "PURE_WHEEL_ITM" || dec === "WHEEL_LADDER_ITM";

  const saveOutcome = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/forge/snapshots/${s.id}/outcome`, { outcome, outcome_notes: notes });
      setEditing(false);
      await onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  const autoOutcome = async () => {
    setAutoBusy(true);
    try {
      await axios.post(`${API}/forge/snapshots/${s.id}/auto-outcome`);
      await onRefresh?.();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
    setAutoBusy(false);
  };

  return (
    <>
      <tr data-testid={`snap-row-${s.id}`} className="border-b border-[#1A1A20] hover:bg-[#26262B]/30">
        <td className="px-3 py-1.5 text-[#8A8A93]">{new Date(s.created_at).toLocaleString()}</td>
        <td className="px-3 py-1.5 text-white font-bold">{s.symbol}</td>
        <td className="px-3 py-1.5 text-[#8A8A93]">{s.timeframe}</td>
        <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{s.spot != null ? `$${Number(s.spot).toFixed(2)}` : "—"}</td>
        <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{s.wes_p?.toFixed?.(1) ?? "—"} / {s.wes_c?.toFixed?.(1) ?? "—"}</td>
        <td className={`px-3 py-1.5 ${decColor}`}>
          {dec || "—"}
          {dec === "NO_TRADE" && s.primary_reason && (
            <div
              className="text-[9px] uppercase tracking-[0.1em] text-[#FFB800] mt-0.5 font-mono"
              title={s.decision_reason || s.primary_reason}
              data-testid={`snap-no-trade-reason-${s.id}`}
            >
              {s.primary_reason.split(" (")[0]}
            </div>
          )}
        </td>
        <td className="px-3 py-1.5 text-right text-[#E4E4E7]">{s.decision_roi != null ? `${s.decision_roi.toFixed(2)}%` : "—"}</td>
        <td className="px-3 py-1.5">
          <button
            type="button"
            data-testid={`snap-outcome-toggle-${s.id}`}
            onClick={() => setEditing(e => !e)}
            className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border inline-flex items-center gap-1 ${outcomeBadge.color} ${outcomeBadge.bg}`}
            title={s.outcome_notes || ""}
          >
            {outcomeBadge.label}
            {s.outcome_auto && <Zap size={9} className="opacity-70" />}
          </button>
        </td>
        <td className="px-3 py-1.5 text-[#8A8A93] truncate max-w-[280px]" title={s.note || s.outcome_notes || ""}>
          {s.note || (s.outcome_notes ? <span className="italic">{s.outcome_notes}</span> : "")}
        </td>
        <td className="px-3 py-1.5 text-right whitespace-nowrap">
          <button data-testid={`snap-replay-${s.id}`} onClick={() => onReplay(s)} className="text-[10px] border border-[#00FF88]/30 text-[#00FF88] px-2 py-1 rounded-sm mr-1 hover:bg-[#00FF88]/5">{t("snap.replay")}</button>
          <button
            data-testid={`snap-export-${s.id}`}
            onClick={() => window.open(`${API}/forge/snapshots/${s.id}/export`, "_blank", "noopener")}
            title={t("snap.export_title")}
            className="text-[10px] border border-[#3BB5FF]/30 text-[#3BB5FF] px-2 py-1 rounded-sm mr-1 hover:bg-[#3BB5FF]/5"
          >
            {t("snap.export")}
          </button>
          <button data-testid={`snap-delete-${s.id}`} onClick={() => onDelete(s.id)} className="text-[10px] border border-[#FF3366]/30 text-[#FF3366] px-2 py-1 rounded-sm hover:bg-[#FF3366]/5"><Trash2 size={10} className="inline" /></button>
        </td>
      </tr>
      {editing && (
        <tr data-testid={`snap-outcome-editor-${s.id}`} className="bg-[#08080A] border-b border-[#1A1A20]">
          <td colSpan={10} className="px-4 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-1">{t("snap.col_outcome")}</div>
                <div className="flex gap-1.5">
                  {["PENDING", "WIN", "LOSS", "BREAKEVEN", "EXPIRED"].map(o => (
                    <button
                      key={o}
                      type="button"
                      data-testid={`snap-outcome-${o.toLowerCase()}-${s.id}`}
                      onClick={() => setOutcome(o)}
                      className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-sm border ${
                        outcome === o
                          ? o === "WIN" ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
                            : o === "LOSS" ? "border-[#FF3366] text-[#FF3366] bg-[#FF3366]/10"
                            : o === "BREAKEVEN" ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10"
                            : "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10"
                          : "border-[#26262B] text-[#8A8A93] hover:text-white"
                      }`}
                    >
                      {o === "WIN" ? t("snap.outcome.win")
                        : o === "LOSS" ? t("snap.outcome.loss")
                        : o === "BREAKEVEN" ? t("snap.outcome.breakeven")
                        : o === "EXPIRED" ? t("snap.outcome.expired")
                        : t("snap.outcome.pending")}
                    </button>
                  ))}
                </div>
              </div>
              <input
                data-testid={`snap-outcome-notes-${s.id}`}
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={t("snap.outcome_notes_placeholder")}
                className="flex-1 min-w-[200px] bg-[#08080A] border border-[#26262B] focus:border-[#00FF88] text-white rounded-sm px-3 py-2 text-[11px] font-mono"
              />
              <button
                data-testid={`snap-outcome-save-${s.id}`}
                onClick={saveOutcome}
                disabled={busy}
                className="text-[10px] font-bold tracking-[0.15em] px-3 py-2 rounded-sm bg-[#00FF88] text-[#08080A] disabled:opacity-50"
              >
                {busy ? <Loader2 size={11} className="animate-spin inline" /> : t("snap.outcome_save")}
              </button>
              {tradeAllowed && (
                <button
                  data-testid={`snap-auto-detect-${s.id}`}
                  onClick={autoOutcome}
                  disabled={autoBusy}
                  className="text-[10px] font-bold tracking-[0.15em] px-3 py-2 rounded-sm border border-[#FFB800] text-[#FFB800] hover:bg-[#FFB800]/10 disabled:opacity-50 flex items-center gap-1"
                  title={t("snap.auto_detect_tip")}
                >
                  {autoBusy ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} {t("snap.auto_detect")}
                </button>
              )}
            </div>
            {s.outcome_auto && s.outcome_notes && (
              <div className="mt-2 text-[10px] text-[#FFB800] italic">{s.outcome_notes}</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// Iter36 — WES-GEX Strike Decision Card.
// Final pre-trade checkpoint card. Renders below WES-GEX Path panel. The
// backend (`/api/forge/strike-decision`) composes Parity v1 WES + WES-GEX +
// candidate trade + freshness into a single status (ACCEPT / CAUTION /
// REJECT / BLOCKED / NO_TRADE_ROI_FAIL / CAUTION_DATA_STALE) and renders
// gates, alignment, do/dont, plain English.
// Iter35 — Per-row data-freshness badge. States:
//   FRESH    ≤ 120 s   green
//   STALE    ≤ 300 s   amber
//   OLD      > 300 s   orange/red
//   ERROR    fetch failed or invalid payload
//   MISMATCH server returned a different symbol than the active chart row
// Tooltip exposes: last scan timestamp, age in seconds, engine, source.
function WatchlistFreshnessBadge({ row, chartSymbol, now }) {
  // MISMATCH wins over everything — never let a wrong-symbol row sneak into
  // the rail unflagged. The backend echoes `row.symbol`; if the row is keyed
  // to a different ticker than the rendering row, that's a regression.
  // (This is a belt-and-suspenders check; in normal flow row.symbol == key.)
  const sym = (row.symbol || "").toUpperCase();
  const cs  = (chartSymbol || "").toUpperCase();
  let label, cls, title;
  const tsIso = row.scanned_at;
  const ageSec = tsIso ? Math.max(0, Math.floor((now - new Date(tsIso).getTime()) / 1000)) : null;
  const engine = (row.wes_engine || "unknown").toLowerCase();
  const src = (row.source || (row.error ? "error" : "unknown"));
  const tooltipBase = `Last scan: ${tsIso || "—"}` +
    (ageSec != null ? ` · age ${ageSec}s` : "") +
    ` · engine: ${engine}` +
    ` · source: ${src}`;
  if (row.error) {
    label = "ERROR"; cls = "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40";
    title = `${tooltipBase}\n${row.error}`;
  } else if (row.symbol && cs && sym !== cs && row.symbol === sym /* always true; placeholder for cross-row mismatch */ && false) {
    // Server-side mismatch would surface here; left unreachable but documented.
    label = "MISMATCH"; cls = "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/60 animate-pulse";
    title = `${tooltipBase}\nSymbol mismatch — row keyed to ${sym} but chart is ${cs}`;
  } else if (ageSec == null) {
    label = "?"; cls = "bg-[#26262B] text-[#5F5F66] border-[#26262B]";
    title = tooltipBase;
  } else if (ageSec > 300) {
    label = "OLD"; cls = "bg-[#FF7700]/15 text-[#FF7700] border-[#FF7700]/50";
    title = tooltipBase;
  } else if (ageSec > 120) {
    label = "STALE"; cls = "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/50";
    title = tooltipBase;
  } else {
    label = "FRESH"; cls = "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30";
    title = tooltipBase;
  }
  return (
    <span
      data-testid={`forge-watch-fresh-${row.symbol}`}
      title={title}
      className={`text-[7px] font-bold tracking-[0.1em] px-1 py-px rounded-sm border flex-shrink-0 ${cls}`}
    >
      {label}
    </span>
  );
}

function WatchlistRail({ watchlist, scanRows, rankMode, currentSymbol, onPick, onRemove, t }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Iter35 — Tick state so per-row FRESH/STALE/OLD badges re-evaluate on the
  // wall clock even when scanRows haven't changed. 15s cadence is sharp enough
  // for a 120/300s threshold model without burning CPU.
  const [scanNowMs, setScanNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setScanNowMs(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  // Alias to keep below-rail JSX self-explanatory.
  const symbol = currentSymbol;
  // Build a map of scan results keyed by symbol so we can decorate watchlist items
  const scanBySym = useMemo(() => {
    const m = {};
    (scanRows || []).forEach(r => { if (r?.symbol) m[r.symbol] = r; });
    return m;
  }, [scanRows]);

  // Choose ordering: scanRows are already ranked server-side. Fallback to watchlist order.
  // Iter41 — also merge per-row scope (shared/personal) from the watchlist
  // so the rail can render an ownership dot + only show delete on rows
  // the calling user actually owns.
  const scopeBySym = useMemo(() => {
    const m = {};
    (watchlist || []).forEach(w => { m[w.symbol] = w.scope || "shared"; });
    return m;
  }, [watchlist]);

  const ordered = useMemo(() => {
    const decorate = (row) => ({ ...row, _scope: scopeBySym[row.symbol] || "shared" });
    if (rankMode === "ranked" && scanRows && scanRows.length > 0) {
      const inScan = new Set(scanRows.map(r => r.symbol));
      const missing = (watchlist || []).filter(w => !inScan.has(w.symbol))
        .map(w => ({ symbol: w.symbol, _missing: true, _scope: w.scope || "shared" }));
      return [...scanRows.map(decorate), ...missing];
    }
    return (watchlist || []).map(w => {
      const base = scanBySym[w.symbol] || { symbol: w.symbol, _missing: true };
      return { ...base, _scope: w.scope || "shared" };
    });
  }, [rankMode, scanRows, watchlist, scanBySym, scopeBySym]);

  if (!ordered || ordered.length === 0) {
    return <p className="text-[11px] text-[#8A8A93]">{t("forge.watchlist_empty")}</p>;
  }

  return (
    <div className="flex flex-col gap-1 max-h-[640px] overflow-y-auto pr-1">
      {ordered.map((row) => {
        const isPicked = currentSymbol === row.symbol;
        const dec = row.decision_strategy;
        const tradeable = !!row.trade_allowed;
        const wesP = row.wes_p;
        const roi = row.roi_pct;
        // Color cue based on tradeability + WES strength
        const dotColor = row._missing
          ? "bg-[#3F3F47]"
          : tradeable
            ? "bg-[#00FF88]"
            : (wesP != null && wesP >= 7)
              ? "bg-[#FFB800]"
              : dec === "NO_TRADE"
                ? "bg-[#FF3366]"
                : "bg-[#3F3F47]";
        const decBadge = tradeable
          ? (dec === "WHEEL_LADDER_ITM" ? "L" : "P")
          : null;
        return (
          <div
            key={row.symbol}
            data-testid={`forge-watch-row-${row.symbol}`}
            className={`relative group rounded-sm ${
              isPicked ? "bg-[#00FF88]/10 border border-[#00FF88]/30" : "border border-transparent hover:bg-[#26262B]/40"
            }`}
          >
            <button
              onClick={() => onPick(row.symbol)}
              data-testid={`forge-watch-${row.symbol}`}
              className={`w-full text-left px-2 py-1.5 text-xs font-mono flex items-center gap-2 ${
                isPicked ? "text-[#00FF88]" : "text-[#E4E4E7]"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor}`} />
              {/* Iter41 — ownership dot (amber=personal, blue=shared). */}
              <span
                data-testid={`forge-watch-scope-${row.symbol}`}
                data-scope={row._scope || "shared"}
                title={row._scope === "personal" ? "Personal — only you see this ticker" : "Shared — admin watchlist"}
                className={`w-1 h-1 rounded-full flex-shrink-0 ${row._scope === "personal" ? "bg-[#FFB800]" : "bg-[#3BB5FF]/70"}`}
              />
              <span className="font-bold flex-shrink-0">{row.symbol}</span>
              {/* Aggression marker — Bull/Bear @ VAL/VAH. Turning Point edges
                  (Bull@VAH or Bear@VAL) get a thicker ring + the "TP" letters
                  so the user can spot regime shifts across the rail at a glance. */}
              {row.aggression && (() => {
                const a = row.aggression;
                const bull = a.type === "Bull";
                const turning = (bull && a.edge === "VAH") || (!bull && a.edge === "VAL");
                const arrow = bull ? "\u25B2" : "\u25BC";
                const cls = bull ? "text-[#00FF88]" : "text-[#FF3366]";
                const ring = turning
                  ? (bull ? "ring-1 ring-[#FFB800]/70 bg-[#FFB800]/15" : "ring-1 ring-[#FFB800]/70 bg-[#FFB800]/15")
                  : "bg-transparent";
                return (
                  <span
                    data-testid={`forge-watch-agg-${row.symbol}`}
                    title={`${a.type} @ ${a.edge} · ${turning ? t("forge.signal_turning_point") : t("forge.signal_continuation")} · ΔVAL ${a.d_val}% · ΔVAH ${a.d_vah}%`}
                    className={`text-[8px] font-bold flex-shrink-0 px-1 rounded-sm ${cls} ${ring}`}
                  >
                    {arrow}{turning ? " TP" : ""}
                  </span>
                );
              })()}
              {/* Iter32 — Engine transparency badge. Lets the user verify at a
                  glance which engine produced this row's WES math. Should be
                  PARITY (green) for every normal production row; LGY (amber)
                  marks any diagnostic fallback; ERR (red) flags missing/failed
                  WES payload (no scores, insufficient bars). */}
              {(() => {
                const eng = row.wes_engine;
                const hasScores = wesP != null && row.wes_c != null;
                let label, cls, title;
                if (!hasScores || eng === "err") {
                  label = "ERR";
                  cls = "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/40";
                  // Iter121.19 — Surface the SPECIFIC error reason in the
                  // tooltip so operators can distinguish "insufficient
                  // Tradier bars" from "Tastytrade OAuth failure" etc.
                  // Backend now populates `row.error` on WES failure.
                  const rowErr = (row && row.error) ? String(row.error).replace(/^wes:/, "").replace(/^cand:/, "").slice(0, 200) : null;
                  title = rowErr
                    ? `WES row failed: ${rowErr}`
                    : "WES calculation failed or insufficient bars — row is informational only";
                } else if (eng === "csv") {
                  label = "CSV";
                  cls = "bg-[#3BB5FF]/15 text-[#3BB5FF] border-[#3BB5FF]/40";
                  title = "TradingView CSV validation mode";
                } else if (eng === "legacy") {
                  label = "LGY";
                  cls = "bg-[#FFB800]/15 text-[#FFB800] border-[#FFB800]/40";
                  title = "Legacy engine — DIAGNOSTIC only, NOT production";
                } else {
                  label = "PARITY";
                  cls = "bg-[#00FF88]/15 text-[#00FF88] border-[#00FF88]/30";
                  title = "Pine Parity Engine v1 — official production source";
                }
                return (
                  <span
                    data-testid={`forge-watch-engine-${row.symbol}`}
                    title={title}
                    className={`text-[7px] font-bold tracking-[0.1em] px-1 py-px rounded-sm border flex-shrink-0 ${cls}`}
                  >
                    {label}
                  </span>
                );
              })()}
              {/* Iter35 — Per-row data-freshness badge. FRESH ≤120s, STALE ≤300s,
                  OLD >300s, ERROR for failed fetch, MISMATCH for cross-symbol
                  payload. The age check uses `scanned_at`; the rail re-renders
                  every ~30s via the parent scanTick effect so this updates live. */}
              <WatchlistFreshnessBadge row={row} chartSymbol={symbol} now={scanNowMs} />
              {decBadge && (
                <span className="text-[8px] font-bold tracking-[0.1em] px-1 py-px rounded-sm bg-[#00FF88]/15 text-[#00FF88] border border-[#00FF88]/30 flex-shrink-0">
                  {decBadge}
                </span>
              )}
              <span className="flex-1 text-right text-[#8A8A93] text-[10px] truncate pr-4">
                {tradeable && roi != null ? (
                  <span className="text-[#00FF88]">{roi.toFixed(1)}%</span>
                ) : wesP != null ? (
                  <span className={wesP >= 7 ? "text-[#FFB800]" : "text-[#8A8A93]"}>w{wesP.toFixed(0)}</span>
                ) : (
                  <span className="text-[#3F3F47]">{t("forge.no_trade_short")}</span>
                )}
              </span>
            </button>
            {/* Iter41 — viewers can only remove their personal entries.
                Admins can remove anything (shared + their own personal). */}
            {onRemove && (isAdmin || row._scope === "personal") && (
              <button
                data-testid={`forge-watch-remove-${row.symbol}`}
                onClick={(e) => { e.stopPropagation(); onRemove(row.symbol); }}
                title={row._scope === "personal" ? "Remove from your personal watchlist" : "Remove from shared watchlist"}
                className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-[#8A8A93] hover:text-[#FF3366]"
              >
                <X size={11} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReplayControls({ asOf, setAsOf, dte, setDte, loading, error, data, revealed, setRevealed, onRerun, onClose, t }) {
  // Quick step controls — bumps as_of by one trading-day chunk (5 = a week of trading)
  const stepDays = (delta) => {
    try {
      const d = new Date(asOf + "T00:00:00Z");
      d.setUTCDate(d.getUTCDate() + delta);
      setAsOf(d.toISOString().slice(0, 10));
    } catch {}
  };
  return (
    <div data-testid="forge-replay-controls" className="border-b border-[#26262B] bg-[#08080A] px-4 py-2 flex items-center gap-2 flex-wrap">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB800] flex items-center gap-1.5">
        <History size={12} /> {t("replay.title")}
      </span>
      <div className="flex items-center gap-1">
        <button data-testid="replay-step-back-week" onClick={() => stepDays(-7)} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm" title="-7d"><ChevronLeft size={12} /></button>
        <label className="flex items-center gap-1 text-[10px] font-mono text-[#8A8A93] uppercase tracking-[0.15em]">
          {t("replay.as_of")}
          <input
            data-testid="replay-as-of"
            type="date"
            value={asOf}
            max={new Date().toISOString().slice(0, 10)}
            onChange={e => setAsOf(e.target.value)}
            className="bg-[#121216] border border-[#26262B] focus:border-[#FFB800] text-white px-2 py-1 rounded-sm text-[11px] font-mono"
          />
        </label>
        <button data-testid="replay-step-forward-week" onClick={() => stepDays(7)} className="p-1.5 border border-[#26262B] text-[#8A8A93] hover:text-white rounded-sm" title="+7d"><ChevronRight size={12} /></button>
      </div>
      <label className="flex items-center gap-1 text-[10px] font-mono text-[#8A8A93] uppercase tracking-[0.15em]">
        {t("replay.dte")}
        <input
          data-testid="replay-dte"
          type="number"
          min={1}
          max={60}
          value={dte}
          onChange={e => setDte(Math.max(1, Math.min(60, Number(e.target.value) || 7)))}
          className="bg-[#121216] border border-[#26262B] focus:border-[#FFB800] text-white px-2 py-1 rounded-sm w-14 text-[11px] font-mono"
        />
      </label>
      <button
        data-testid="replay-run"
        onClick={onRerun}
        disabled={loading}
        className="text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm bg-[#FFB800] text-[#08080A] disabled:opacity-50 flex items-center gap-1"
      >
        {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={11} />}
        {loading ? t("replay.running") : t("replay.run")}
      </button>
      {data && data.future_count > 0 && (
        <button
          data-testid="replay-reveal"
          onClick={() => setRevealed(v => !v)}
          className={`text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm flex items-center gap-1 border ${revealed ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
        >
          {revealed ? <EyeOff size={11} /> : <Eye size={11} />}
          {revealed ? t("replay.hide") : t("replay.reveal")}
        </button>
      )}
      {data && (
        <span className="text-[10px] font-mono text-[#8A8A93] ml-auto">
          {data.past_count}p · {data.future_count}f · spot ${data.spot_at?.toFixed(2)}
        </span>
      )}
      {error && <span className="text-[10px] font-mono text-[#FF3366]">{error}</span>}
      <button data-testid="replay-close" onClick={onClose} className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1.5 rounded-sm border border-[#26262B] text-[#8A8A93] hover:text-white flex items-center gap-1">
        <X size={11} /> {t("replay.exit")}
      </button>
    </div>
  );
}

function ReplayDecisionPanel({ data, loading, revealed, t }) {
  if (loading && !data) {
    return (
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <History size={14} className="text-[#FFB800]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB800]">{t("replay.title")}</h3>
          <Loader2 size={12} className="animate-spin text-[#8A8A93]" />
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4">
        <div className="flex items-center gap-2 mb-2">
          <History size={14} className="text-[#FFB800]" />
          <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB800]">{t("replay.title")}</h3>
        </div>
        <p className="text-xs text-[#8A8A93]">{t("common.awaiting")}</p>
      </div>
    );
  }
  const h = data.hypothetical || {};
  const o = data.outcome || {};
  const tradeColor = h.trade_allowed ? COLORS.green : COLORS.red;
  const outcomeMap = {
    WIN: { color: COLORS.green, label: t("replay.outcome_win") },
    LOSS: { color: COLORS.red, label: t("replay.outcome_loss") },
    BREAKEVEN: { color: COLORS.amber, label: t("replay.outcome_be") },
    PENDING: { color: "#8A8A93", label: t("snap.outcome.pending") },
  };
  const verdict = outcomeMap[o.verdict] || outcomeMap.PENDING;
  return (
    <div data-testid="forge-replay-panel" className="bg-[#121216] border border-[#26262B] rounded-sm p-4 space-y-3">
      <div className="flex items-center gap-2">
        <History size={14} className="text-[#FFB800]" />
        <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#FFB800]">{t("replay.hypothetical")}</h3>
      </div>
      <div className="rounded-sm border p-2.5" style={{ borderColor: tradeColor, background: `${tradeColor}10` }}>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: tradeColor }}>
          {h.trade_allowed ? t("forge.label_pure") : t("forge.label_no_trade")}
        </div>
        <div className="text-[10px] font-mono text-[#8A8A93] mt-1">{h.premium_method}</div>
      </div>
      <div className="space-y-1.5 text-[11px] font-mono">
        <KV label={t("replay.spot_at")} value={`$${data.spot_at?.toFixed(2)}`} />
        <KV label={t("replay.atr_label")} value={`$${data.atr14?.toFixed(2)}`} />
        <KV label={t("replay.strike")} value={`$${h.strike?.toFixed(2)}`} valueClass="text-white" />
        <KV label={t("replay.premium")} value={`$${h.premium_est?.toFixed(2)}`} valueClass="text-[#00FF88]" />
        <KV label={t("replay.breakeven")} value={`$${h.breakeven?.toFixed(2)}`} />
        <KV label={t("replay.roi")} value={`${h.roi_pct?.toFixed(2)}%`} valueClass={h.trade_allowed ? "text-[#00FF88]" : "text-[#FF3366]"} />
        <KV label={t("replay.gate")} value={`≥ ${h.min_income_pct}%`} />
      </div>
      <div className="border-t border-[#26262B] pt-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-2">
          {t("replay.outcome")} · DTE {data.dte}
        </div>
        {revealed || o.verdict !== "PENDING" ? (
          <>
            <div data-testid="replay-outcome-badge" className="rounded-sm border p-2.5 mb-2" style={{ borderColor: verdict.color, background: `${verdict.color}10` }}>
              <div className="text-sm font-black uppercase tracking-[0.1em]" style={{ color: verdict.color }}>{verdict.label}</div>
              {o.spot_at_exp != null && <div className="text-[10px] font-mono text-white mt-0.5">{t("replay.spot_at_exp")}: ${o.spot_at_exp.toFixed(2)}</div>}
            </div>
            <p className="text-[10px] text-[#E4E4E7] leading-relaxed">{o.reason}</p>
          </>
        ) : (
          <p className="text-[10px] text-[#8A8A93] italic flex items-center gap-1.5">
            <Eye size={11} /> {t("replay.reveal")} →
          </p>
        )}
      </div>
      <p className="text-[9px] text-[#8A8A93] italic pt-2 border-t border-[#26262B]">{t("replay.note_proxy")}</p>
    </div>
  );
}

function CompanyLogo({ symbol, size = 28 }) {
  // Use Financial Modeling Prep public logos (no auth, broad coverage of US tickers).
  // Fall back to a letter bubble if the asset is missing.
  const [error, setError] = useState(false);
  if (!symbol) return null;
  if (error) {
    return (
      <div
        data-testid={`forge-logo-${symbol}`}
        className="flex items-center justify-center rounded-sm bg-[#1F1F25] text-[#00FF88] font-bold border border-[#26262B]"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      >
        {symbol.slice(0, 1)}
      </div>
    );
  }
  return (
    <img
      data-testid={`forge-logo-${symbol}`}
      src={`https://financialmodelingprep.com/image-stock/${symbol}.png`}
      alt={symbol}
      onError={() => setError(true)}
      style={{ width: size, height: size, objectFit: "contain" }}
      className="rounded-sm bg-white/95 p-0.5"
    />
  );
}

function LiveBadge({ tick, tickError, bars, t }) {
  // Three states: market open (green pulse), market closed (dim), error (amber).
  // Plus a small data-source / last-bar freshness chip so the user always knows where
  // the price is coming from (Tradier / Tastytrade / cache) and how recent the last
  // CLOSED candle is — important context when comparing to TradingView.
  const sourceLabel = (() => {
    const s = (tick?.source || "").toLowerCase();
    if (s === "tradier") return t("forge.source_tradier");
    if (s === "tastytrade") return t("forge.source_tastytrade");
    if (s === "cache") return t("forge.source_cache");
    return null;
  })();
  const lastBar = bars && bars.length > 0 ? bars[bars.length - 1] : null;
  const lastBarLabel = (() => {
    if (!lastBar?.time) return null;
    try {
      let d;
      if (typeof lastBar.time === "number") d = new Date(lastBar.time * 1000);
      else if (typeof lastBar.time === "string") {
        d = lastBar.time.length === 10 ? new Date(lastBar.time + "T00:00:00") : new Date(lastBar.time);
      } else return null;
      return String(lastBar.time).length === 10
        ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
        : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
  })();

  let mainBadge;
  if (tickError && !tick) {
    mainBadge = (
      <span data-testid="forge-live-badge" className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#FF3366]/30 bg-[#FF3366]/10 text-[#FF3366]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF3366]" /> ERR
      </span>
    );
  } else if (!tick) {
    mainBadge = (
      <span data-testid="forge-live-badge" className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#8A8A93]" /> …
      </span>
    );
  } else if (tick.market_open === false) {
    mainBadge = (
      <span data-testid="forge-live-badge" title={tick.ts ? `${t("forge.last_tick")}: ${new Date(tick.ts).toLocaleString()}` : ""} className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#3F3F47] text-[#8A8A93]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#3F3F47]" /> {t("forge.market_closed")}
      </span>
    );
  } else if (tick.stale) {
    mainBadge = (
      <span data-testid="forge-live-badge" className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[#FFB800]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" /> {t("forge.stale")}
      </span>
    );
  } else {
    mainBadge = (
      <span data-testid="forge-live-badge" title={tick.ts ? `${t("forge.last_tick")}: ${new Date(tick.ts).toLocaleString()}` : ""} className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/10 text-[#00FF88]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" /> {t("forge.live")} · {t("forge.live_tick")}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      {mainBadge}
      {sourceLabel && (
        <span data-testid="forge-source-chip" className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#8A8A93] border-l border-[#26262B] pl-1.5">
          {sourceLabel}
          {lastBarLabel && <span className="ml-1 text-[#5F5F66]">· {t("forge.last_bar")} {lastBarLabel}</span>}
        </span>
      )}
    </span>
  );
}


// Iter121.1 — Bar timer chip. Lives inside the chart header right of
// the timeframe label and ticks once per second. Pure derivation from
// the latest bar's open time + the active timeframe; no extra network
// traffic. Color thresholds: <10s → green pulse, <60s → amber, ≥60s →
// neutral. When the last bar is older than the boundary (market
// closed, stale data) shows "OFFLINE" rather than counting negatives.
// Iter121.16 — Broker Health Strip.
// Two compact status dots (`TRADIER` + `TASTY`) shown in the Market
// Forge header. Colour reflects the derived server-side status:
//   healthy   → green (last call OK, no recent errors)
//   degraded  → amber (recent OKs AND recent errors — partial)
//   unhealthy → red (last call failed, no recent OK)
//   unknown   → dim  (no calls yet since server restart)
// Hover any dot to see the exact age of the last OK / last error and
// the last error message (truncated to 250 chars server-side).
function BrokerHealthStrip({ brokers, isAdmin, onHealthUpdate }) {
  const list = Array.isArray(brokers) ? brokers : [];
  const [reconnecting, setReconnecting] = React.useState(false);
  const labelFor = (b) => (b === "tradier" ? "TRADIER" : b === "tastytrade" ? "TASTY" : b.toUpperCase());
  const clsFor = (status) => {
    if (status === "healthy") return { dot: "bg-[#00FF88]", chip: "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/8" };
    if (status === "degraded") return { dot: "bg-[#FFB800]", chip: "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/8" };
    if (status === "unhealthy") return { dot: "bg-[#FF3366]", chip: "border-[#FF3366]/45 text-[#FF3366] bg-[#FF3366]/8" };
    return { dot: "bg-[#3F3F47]", chip: "border-[#26262B] text-[#5F5F66] bg-[#08080A]" };
  };
  const fmtAge = (s) => {
    if (s == null) return "\u2014";
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };
  // Iter121.17 — Inline "Reconnect" affordance for the Tastytrade
  // pill. Only surfaces for admin users when TASTY is unhealthy.
  // Kicks off the SAME OAuth flow as Settings → "Re-authorize" so we
  // don't duplicate contract knowledge.
  // Iter121.18 — Popup flow. Instead of full-page navigation the
  // OAuth flow opens in a small window (600×720). The backend callback
  // detects the popup mode (via `?mode=popup` propagated through the
  // state doc) and serves a self-closing HTML page that postMessages
  // the outcome back to us. We refresh the health strip immediately
  // on receipt so the operator sees the TASTY chip flip to green
  // without waiting for the next 60 s poll.
  const [reconnectFeedback, setReconnectFeedback] = React.useState(null); // {status, ts}
  React.useEffect(() => {
    if (!reconnectFeedback) return;
    const id = setTimeout(() => setReconnectFeedback(null), 4500);
    return () => clearTimeout(id);
  }, [reconnectFeedback]);
  const reconnectTastytrade = async () => {
    if (!isAdmin || reconnecting) return;
    setReconnecting(true);
    let popup = null;
    let cleanup = () => {};
    try {
      const redirectUri = `${window.location.origin}/api/oauth/tastytrade/callback`;
      const r = await axios.get(`${API}/oauth/tastytrade/authorize`, {
        params: { redirect_uri: redirectUri, mode: "popup" },
      });
      // Open the OAuth login in a compact popup so the operator keeps
      // their chart visible in the background.
      const w = 600, h = 720;
      const left = Math.max(0, (window.screen.width - w) / 2);
      const top = Math.max(0, (window.screen.height - h) / 2);
      popup = window.open(
        r.data.url,
        "arxmentis-tt-oauth",
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,noopener=no`
      );
      if (!popup) {
        // Popup blocked — fall back to full-page navigation so the
        // operator can still complete the flow.
        window.location.href = r.data.url;
        return;
      }
      // Listen for the callback page's postMessage. On success we bump
      // the broker-health poll straight away.
      const onMessage = (ev) => {
        const d = ev?.data;
        if (!d || d.source !== "arxmentis-tt-oauth") return;
        setReconnectFeedback({ status: d.status, ts: Date.now() });
        setReconnecting(false);
        // Force an immediate re-poll of /broker/health so the strip
        // updates without waiting for the 60 s tick.
        (async () => {
          try {
            const hr = await axios.get(`${API}/broker/health`);
            if (typeof onHealthUpdate === "function") onHealthUpdate(hr.data || { brokers: [] });
          } catch { /* silent */ }
        })();
        cleanup();
      };
      window.addEventListener("message", onMessage);
      // Also watch for manual close (user dismissed the popup without
      // completing OAuth). Poll every 500 ms.
      const closedWatcher = setInterval(() => {
        if (popup && popup.closed) {
          setReconnecting(false);
          cleanup();
        }
      }, 500);
      cleanup = () => {
        window.removeEventListener("message", onMessage);
        clearInterval(closedWatcher);
      };
      // Safety timeout — if the popup never posts back within 5 min,
      // release the button. OAuth state itself expires server-side.
      setTimeout(() => { cleanup(); setReconnecting(false); }, 5 * 60 * 1000);
    } catch (e) {
      setReconnecting(false);
      cleanup();
      alert(e.response?.data?.detail || "Could not start Tastytrade authorization. Open Settings to configure the client_id / client_secret first.");
    }
  };
  const tasty = list.find((b) => b.broker === "tastytrade");
  const showReconnect = isAdmin && tasty && tasty.status === "unhealthy";
  return (
    <div
      data-testid="forge-broker-health-strip"
      className="hidden md:inline-flex items-center gap-1"
      role="status"
      aria-label="Broker health"
    >
      {list.map((b) => {
        const s = clsFor(b.status);
        const okLine = b.last_ok_at
          ? `Last OK: ${fmtAge(b.last_ok_age_s)} ago`
          : "No successful calls this session";
        const errLine = b.last_err
          ? `Last error (${fmtAge(b.last_err_age_s)} ago): ${b.last_err}`
          : "No errors";
        const tip = `${labelFor(b.broker)} \u00B7 ${b.status}\n${okLine}\n${errLine}\nOK ${b.ok_count} \u00B7 ERR ${b.err_count}`;
        return (
          <span
            key={b.broker}
            data-testid={`forge-broker-health-${b.broker}`}
            data-testid-status={b.status}
            title={tip}
            className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.15em] px-1.5 py-1 rounded-sm border ${s.chip}`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${s.dot} ${b.status === "unhealthy" ? "animate-pulse" : ""}`}
            />
            {labelFor(b.broker)}
          </span>
        );
      })}
      {showReconnect && (
        <button
          type="button"
          data-testid="forge-broker-reconnect-tastytrade"
          onClick={reconnectTastytrade}
          disabled={reconnecting}
          title={"Start Tastytrade OAuth re-authorization in a popup window. Complete 2FA in the popup — the strip refreshes automatically when the popup closes."}
          className="inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.12em] px-1.5 py-1 rounded-sm border border-[#FFB800]/45 text-[#FFB800] bg-[#FFB800]/8 hover:bg-[#FFB800]/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {reconnecting ? "\u23F3" : "\u21BB"} RECONNECT
        </button>
      )}
      {reconnectFeedback && (
        <span
          data-testid="forge-broker-reconnect-feedback"
          data-testid-status={reconnectFeedback.status === "ok" ? "ok" : "error"}
          className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.15em] px-1.5 py-1 rounded-sm border ${
            reconnectFeedback.status === "ok"
              ? "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/10"
              : "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/10"
          }`}
        >
          {reconnectFeedback.status === "ok" ? "\u2713 RECONNECTED" : "\u2715 OAUTH FAILED"}
        </span>
      )}
    </div>
  );
}


function BarTimerChip({ lastBarTime, timeframe }) {
  // Normalise the bar time to UNIX seconds — `displayBars[last].time`
  // can be a number (intraday) or a "YYYY-MM-DD" string (daily).
  const lastUnix = React.useMemo(() => normalizeChartTime(lastBarTime), [lastBarTime]);
  // Iter121.9 — Use ET-wall-clock-as-UTC seconds so the comparison
  // aligns with the backend's bar-time convention (see doc above).
  const [now, setNow] = React.useState(() => nowAsEtSeconds());
  React.useEffect(() => {
    const id = setInterval(() => setNow(nowAsEtSeconds()), 1000);
    return () => clearInterval(id);
  }, []);
  // Iter121.8 — Roll-forward instead of freezing on OFFLINE. When the
  // current bar's close is already past (market closed, weekends,
  // holidays), we compute the NEXT expected boundary and keep ticking
  // in a dim "· CLOSED" style. The operator always sees a live timer,
  // never a dead status label.
  const expected = React.useMemo(
    () => nextExpectedBarBoundary(lastUnix, timeframe, now),
    [lastUnix, timeframe, now],
  );
  if (!expected) return null;
  const { boundary: closeAt, rolled } = expected;
  const remain = closeAt - now;
  let toneClass;
  let dotClass;
  if (rolled) {
    // Between-session state — dim indigo, no pulse.
    toneClass = "border-[#3F3F47] text-[#8A8A93] bg-[#0A0A0D]";
    dotClass = "bg-[#5F5F66]";
  } else if (remain <= 10) {
    toneClass = "border-[#00FF88]/45 text-[#00FF88] bg-[#00FF88]/10";
    dotClass = "bg-[#00FF88] animate-pulse";
  } else if (remain <= 60) {
    toneClass = "border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10";
    dotClass = "bg-[#FFB800]";
  } else {
    toneClass = "border-[#26262B] text-white";
    dotClass = "bg-[#22D3FF]";
  }
  const label = formatBarCountdown(remain);
  // Iter121.9 — Tooltip renders the bar's OPEN time in America/New_York
  // (matches how the chart itself labels bars). Uses the raw
  // ET-as-UTC UNIX seconds against `toLocaleString` with `timeZone:
  // "UTC"` so the wall-clock we display equals the ET wall-clock the
  // backend encoded.
  const fmtEt = (unixSec) =>
    new Date(unixSec * 1000).toLocaleString("en-US", {
      timeZone: "UTC",
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    }) + " ET";
  const tooltip = rolled
    ? `Market closed. Next ${timeframe.toUpperCase()} bar boundary in ${label} (${fmtEt(closeAt)}).`
    : `Bar close countdown · ${timeframe.toUpperCase()} · bar opens ${fmtEt(lastUnix)}`;
  return (
    <span
      data-testid="forge-bar-timer"
      data-testid-state={rolled ? "closed" : "live"}
      title={tooltip}
      className={`inline-flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.12em] px-2 py-0.5 rounded-sm border ${toneClass}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span data-testid="forge-bar-timer-value">{label}</span>
      {rolled && (
        <span
          data-testid="forge-bar-timer-closed-suffix"
          className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#5F5F66]"
        >· CLOSED</span>
      )}
    </span>
  );
}


// Iter121.11 — Aggression Sentiment History Strip.
// Sits between the chart header and the chart canvas. Renders the last
// 5 signal transitions for the active symbol+timeframe pair as compact
// coloured pills so operators can spot rapid Bear→Bull flips or
// repeated turning-point attempts at the same edge — a common
// pre-cursor pattern for major moves. Empty state hides the strip
// entirely so it never adds vertical noise to symbols that have never
// fired a signal in this session.
function formatRelativeAge(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ""}`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

function SignalHistoryStrip({ history, timeframe, onPan }) {
  // Re-render every 30s so the relative timestamps stay fresh.
  const [, setNowTick] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setNowTick(x => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!Array.isArray(history) || history.length === 0) return null;
  const now = Date.now();
  const canPan = typeof onPan === "function";
  return (
    <div
      data-testid="forge-signal-history"
      className="flex items-center gap-2 px-4 py-1.5 border-b border-[#26262B] bg-[#0A0A0D] overflow-x-auto"
    >
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#5F5F66] flex-shrink-0">
        Signal Flow
      </span>
      <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#3F3F47] flex-shrink-0">
        {timeframe.toUpperCase()}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        {history.map((entry, idx) => {
          // Iter121.13 — click a pill to pan the chart to the bar where
          // the signal fired. Skipped for entries without a bar_time
          // (older records from before Iter121.13) — those render as
          // static badges.
          const hasBar = entry.bar_time != null && canPan;
          const clickHandler = hasBar ? () => onPan(entry.bar_time) : undefined;
          const clickProps = hasBar
            ? {
                role: "button",
                tabIndex: 0,
                onClick: clickHandler,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    clickHandler();
                  }
                },
              }
            : {};
          const hoverCls = hasBar ? "cursor-pointer hover:brightness-125 hover:ring-1 hover:ring-white/20" : "";
          // "Clear" entry — signal cleared. Neutral pill.
          if (!entry.type) {
            return (
              <span
                key={`${entry.at}-${idx}`}
                data-testid={`forge-signal-history-entry-${idx}`}
                data-testid-sentiment="none"
                data-testid-clickable={hasBar ? "1" : "0"}
                title={
                  (hasBar ? "Click to pan chart · " : "") +
                  `Signal cleared · ${new Date(entry.at).toLocaleString()}`
                }
                className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border border-[#3F3F47] text-[#5F5F66] bg-[#08080A] flex-shrink-0 transition-all ${hoverCls}`}
                {...clickProps}
              >
                ○ CLEAR · {formatRelativeAge(now - entry.at)}
              </span>
            );
          }
          const isBull = entry.type === "Bull";
          const arrow = isBull ? "\u25B2" : "\u25BC";
          const sig = entry.turning ? "TP" : "CONT";
          const cls = isBull
            ? "border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/10"
            : "border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/10";
          const dir = isBull ? "Bull" : "Bear";
          const fullSig = entry.turning ? "Turning Point" : "Continuation";
          const tip =
            (hasBar ? "Click to pan chart to this bar\n" : "") +
            `${dir} @ ${entry.edge} · ${fullSig}\n` +
            `${new Date(entry.at).toLocaleString()}\n` +
            `VAL ${entry.val ?? "—"} · POC ${entry.poc ?? "—"} · VAH ${entry.vah ?? "—"}`;
          return (
            <span
              key={`${entry.at}-${idx}`}
              data-testid={`forge-signal-history-entry-${idx}`}
              data-testid-sentiment={isBull ? "bull" : "bear"}
              data-testid-edge={entry.edge}
              data-testid-clickable={hasBar ? "1" : "0"}
              title={tip}
              className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${cls} flex-shrink-0 transition-all ${hoverCls}`}
              {...clickProps}
            >
              {arrow} {entry.edge}·{sig} · {formatRelativeAge(now - entry.at)}
            </span>
          );
        })}
      </div>
    </div>
  );
}


export default MarketForge;
